import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import NotificationsTab from '../components/NotificationsTab'
import ChurchesTab from '../components/ChurchesTab'
import HelpVideosTab from '../components/HelpVideosTab'
import { logAction } from '../utils/auditLog'
import { generateReceiptReport } from '../utils/generateReceiptReport'
import { generateFinancialReport } from '../utils/generateFinancialReport'
import { selectRefundedWithReceipts } from '../utils/refundedReceipts'
import { buildRoomsReportData, roomsToCsv } from '../utils/roomsReportData'
import { generateRoomsReport } from '../utils/generateRoomsReport'
import { sumDonations, computeNetResult, donorDisplayName } from '../utils/donations'
import { uploadReceipt } from '../utils/receipts'
import { PAYMENT_METHOD_CODES, paymentMethodLabel } from '../utils/paymentMethods'
import { toWhatsAppNumber } from '../utils/phone'
import { attendeesToCsv } from '../utils/attendeesCsv'
import { deriveInstallmentStatus, deriveAgreementStatus, installmentPaidAmount, installmentRemaining, pendingAgreementIncome } from '../utils/agreements'

/* ── Pure helpers ─────────────────────────────────────────────────────────── */

function getAge(dob) {
  if (!dob) return null
  const today = new Date()
  const birth = new Date(dob + 'T00:00:00')
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

function getAgeGroup(age, groups) {
  if (age === null || !groups.length) return null
  return groups.find(g => age >= g.min_age && age <= g.max_age) ?? null
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function formatCurrency(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0)
}

function daysSince(dateStr) {
  if (!dateStr) return null
  const diff = Date.now() - new Date(dateStr).getTime()
  return Math.floor(diff / (1000 * 60 * 60 * 24))
}

/* ── Constants ───────────────────────────────────────────────────────────── */

const INPUT_CLS =
  'w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-3 py-2.5 text-sm text-on-surface ' +
  'focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition'

const SELECT_CLS =
  'rounded-xl border border-outline-variant bg-surface-container-lowest px-3 py-2 text-sm text-on-surface ' +
  'focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition'

const PIE_COLORS = ['#43543f', '#dde5d3']
const ALL_TABS = ['Overview', 'Users', 'Attendees', 'Transport', 'Payments', 'Income & Expenses', 'Refunds', 'Camps', 'Rooms', 'Churches', 'Help Videos', 'Notifications', 'Admin Users', 'Audit Log']

const TAB_ICONS = {
  'Overview':          'insights',
  'Users':             'group',
  'Attendees':         'checklist',
  'Transport':         'directions_bus',
  'Payments':          'payments',
  'Income & Expenses': 'account_balance_wallet',
  'Refunds':           'currency_exchange',
  'Camps':             'camping',
  'Rooms':             'bed',
  'Churches':          'church',
  'Help Videos':       'help',
  'Notifications':     'outgoing_mail',
  'Admin Users':       'admin_panel_settings',
  'Audit Log':         'history',
}

const STATUS_STYLES = {
  Paid:              'bg-green-100 text-green-800 border-green-200',
  'Partial Payment': 'bg-blue-100 text-blue-800 border-blue-200',
  Confirmed:         'bg-lime-100 text-lime-800 border-lime-200',
  Pending:           'bg-amber-100 text-amber-800 border-amber-200',
  Cancelled:         'bg-red-100 text-red-800 border-red-200',
  Sponsored:         'bg-purple-100 text-purple-800 border-purple-200',
}

const RECEIPT_STATUS_STYLES = {
  'Pending Review': 'bg-amber-100 text-amber-800 border-amber-200',
  Approved:         'bg-green-100 text-green-800 border-green-200',
  Rejected:         'bg-red-100 text-red-800 border-red-200',
}

function ReceiptStatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${RECEIPT_STATUS_STYLES[status] ?? 'bg-surface-container text-on-surface-variant border-outline-variant/60'}`}>
      {status}
    </span>
  )
}

/* ── Micro-components ────────────────────────────────────────────────────── */

function StatCard({ label, value, sub, colorClass }) {
  return (
    <div className={`rounded-2xl border p-5 ${colorClass}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] opacity-70">{label}</p>
      <p className="mt-1.5 font-display text-3xl font-semibold tracking-tight">{value}</p>
      {sub && <p className="mt-0.5 text-xs opacity-60">{sub}</p>}
    </div>
  )
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] ?? 'bg-surface-container text-on-surface-variant border-outline-variant/60'}`}>
      {status}
    </span>
  )
}

function TH({ children }) {
  return (
    <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/80 whitespace-nowrap">
      {children}
    </th>
  )
}

function ModalWrap({ onClose, children, wide = false }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#2c241c]/50 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        className={`w-full bg-surface-container-lowest rounded-2xl shadow-xl border border-outline-variant/60 ${wide ? 'max-w-2xl' : 'max-w-lg'}`}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}

/* ── Main dashboard ──────────────────────────────────────────────────────── */

export default function AdminDashboard() {
  const { user, profile, canEdit, isSuperAdmin, isStaff } = useAuth()
  const { t } = useTranslation()

  const tabLabel = (tab) => {
    const map = {
      'Overview':          t('admin.tabOverview'),
      'Users':             t('admin.tabUsers'),
      'Attendees':         t('admin.tabAttendees'),
      'Transport':         t('admin.tabTransport'),
      'Payments':          t('admin.tabPayments'),
      'Income & Expenses': t('admin.tabIncomeExpenses'),
      'Refunds':           t('admin.tabRefunds'),
      'Camps':             t('admin.tabCamps'),
      'Rooms':             t('admin.tabRooms'),
      'Churches':          t('admin.tabChurches'),
      'Help Videos':       t('admin.tabHelpVideos'),
      'Notifications':     t('admin.tabNotifications'),
      'Admin Users':       t('admin.tabAdminUsers'),
      'Audit Log':         t('admin.tabAuditLog'),
    }
    return map[tab] ?? tab
  }

  // Compute visible tabs immediately — must be before any useEffect that references it
  const visibleTabs = useMemo(() => {
    const role = profile?.role
    if (role === 'superadmin')     return ALL_TABS
    if (role === 'admin')          return ALL_TABS.filter(t => t !== 'Admin Users' && t !== 'Audit Log')
    if (role === 'readonly_admin') return ALL_TABS.filter(t => t !== 'Admin Users' && t !== 'Audit Log' && t !== 'Notifications')
    if (role === 'staff')          return ['Attendees', 'Transport', 'Rooms']
    return []
  }, [profile?.role])

  // Core data
  const [camp, setCamp] = useState(null)
  const [registrations, setRegistrations] = useState([])
  const [ageGroups, setAgeGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('Overview')

  // Attendees filters
  const [search, setSearch] = useState('')
  const [filterSex, setFilterSex] = useState('All')
  const [filterAgeGroup, setFilterAgeGroup] = useState('All')
  const [filterStatus, setFilterStatus] = useState('All')
  const [filterChurch, setFilterChurch] = useState('All')

  // Edit registration modal — full form
  const [editingReg, setEditingReg] = useState(null)
  const [editForm, setEditForm] = useState(null)
  const [editCampTiers, setEditCampTiers] = useState([])
  const [modalSaving, setModalSaving] = useState(false)
  const [modalError, setModalError] = useState('')

  // Receipt review modal (legacy — single receipt per registration)
  const [receiptModal, setReceiptModal] = useState({ open: false, reg: null })
  const [receiptEditForm, setReceiptEditForm] = useState({ receipt_amount: '', payment_method: '', admin_note: '' })
  const [receiptSaving, setReceiptSaving] = useState(false)
  const [receiptNoteError, setReceiptNoteError] = useState(false)

  // Per-receipt approve/reject modal (new partial-payment system)
  const [paymentReceipts, setPaymentReceipts] = useState([])
  const [expandedRegIds, setExpandedRegIds] = useState(new Set())
  const [approveModal, setApproveModal] = useState({ open: false, receipt: null, reg: null })
  const [approveForm, setApproveForm] = useState({ amount: '', admin_note: '' })
  const [approveSaving, setApproveSaving] = useState(false)
  const [approveNoteError, setApproveNoteError] = useState(false)
  const [approveAllocations, setApproveAllocations] = useState({}) // { installmentId: amountStr }
  const [adminUpload, setAdminUpload]         = useState({ open: false, reg: null })
  const [adminUploadForm, setAdminUploadForm] = useState({ file: null, amount: '', payment_method: '' })
  const [adminUploadError, setAdminUploadError]   = useState('')
  const [adminUploadSaving, setAdminUploadSaving] = useState(false)

  // Payments tab filters
  const [paymentSearch, setPaymentSearch] = useState('')
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('All')

  // Refund modal
  const [refunds, setRefunds] = useState([])
  const [refundModal, setRefundModal] = useState({ open: false, reg: null })
  const [refundForm, setRefundForm] = useState({ amount: '', reason: '' })
  const [refundSaving, setRefundSaving] = useState(false)
  const [refundError, setRefundError] = useState('')

  // Refunds tab filters
  const [refundSearch, setRefundSearch] = useState('')
  const [refundDateFrom, setRefundDateFrom] = useState('')
  const [refundDateTo, setRefundDateTo] = useState('')

  // Expenses
  const [expenses, setExpenses] = useState([])
  const [expenseModal, setExpenseModal] = useState({ open: false, mode: 'new', expense: null })
  const [expenseForm, setExpenseForm] = useState({ description: '', category: 'Food', amount: '', invoice_file: null })
  const [expenseSaving, setExpenseSaving] = useState(false)
  const [expenseError, setExpenseError] = useState('')
  const [deletingExpenseId, setDeletingExpenseId] = useState(null)
  const [transactionDetailsOpen, setTransactionDetailsOpen] = useState(false)

  // Donations
  const [donations, setDonations] = useState([])
  const [donationModal, setDonationModal] = useState({ open: false, mode: 'new', donation: null })
  const [donationForm, setDonationForm] = useState({
    donor_name: '', description: '', category: 'Cash',
    donated_at: new Date().toISOString().split('T')[0], amount: '', receipt_file: null,
  })
  const [donationSaving, setDonationSaving] = useState(false)
  const [donationError, setDonationError] = useState('')
  const [deletingDonationId, setDeletingDonationId] = useState(null)

  // Toast & per-row sending state
  const [toast, setToast] = useState(null)
  const [sendingIds, setSendingIds] = useState(new Set())

  // Bulk reminder
  const [confirmBulk, setConfirmBulk] = useState(false)
  const [bulkSending, setBulkSending] = useState(false)
  const [bulkProgress, setBulkProgress] = useState({ sent: 0, total: 0 })

  // Users tab
  const [users, setUsers] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersSending, setUsersSending] = useState(new Set())
  const [bulkUserSending, setBulkUserSending] = useState(null) // { type, sent, total }

  // Camps tab
  const [allCamps, setAllCamps] = useState([])
  const [campModal, setCampModal] = useState({ open: false, mode: 'new', camp: null })
  const [campForm, setCampForm] = useState({
    name: '', description: '', location: '',
    start_date: '', end_date: '', payment_due_date: '', registration_open: false,
    transport_cost: '',
    packing_list: '', map_link: '', arrival_time: '',
    transport_meeting_point: '', transport_meeting_time: '',
    payment_bank_name: '', payment_account_number: '', payment_card_url: '',
    payment_in_person_note: '', payment_extra_info: '',
  })
  const [campSaving, setCampSaving] = useState(false)
  const [tierModal, setTierModal] = useState({ open: false, campId: null, campName: '', tiers: [] })
  const [tierForm, setTierForm] = useState({ label: '', arrival_date: '', price: '' })
  const [tierSaving, setTierSaving] = useState(false)

  // Rooms tab
  const [rooms, setRooms] = useState([])
  const [roomAssignments, setRoomAssignments] = useState([])
  const [roomModal, setRoomModal] = useState({ open: false, mode: 'new', room: null })
  const [roomForm, setRoomForm] = useState({ name: '', capacity: '', sex: 'Male', min_age: '', max_age: '', photo_url: '' })
  const [roomPhotoFile, setRoomPhotoFile] = useState(null)
  const [roomSaving, setRoomSaving] = useState(false)
  const [manageRoom, setManageRoom] = useState(null)
  const [autoAssigning, setAutoAssigning] = useState(false)
  const [assignSearch, setAssignSearch] = useState('')

  // Registrations for the rooms tab camp (used by auto-assign)
  const [roomCampRegs, setRoomCampRegs] = useState([])
  const [roomAgreementRegIds, setRoomAgreementRegIds] = useState(new Set())

  // Admin Users tab (superadmin only)
  const [adminUsers, setAdminUsers] = useState([])
  const [adminUsersLoading, setAdminUsersLoading] = useState(false)
  const [grantEmail, setGrantEmail] = useState('')
  const [grantRoleValue, setGrantRoleValue] = useState('admin')
  const [grantSaving, setGrantSaving] = useState(false)
  const [grantError, setGrantError] = useState('')

  // Audit Log tab (superadmin only)
  const [auditLogEntries, setAuditLogEntries] = useState([])
  const [auditLogLoading, setAuditLogLoading] = useState(false)
  const [auditLogSearch, setAuditLogSearch] = useState('')
  const [auditLogDateFrom, setAuditLogDateFrom] = useState('')
  const [auditLogDateTo, setAuditLogDateTo] = useState('')

  // Global camp selector for Overview / Attendees / Transport / Payments / Finances
  const [selectedDashboardCampId, setSelectedDashboardCampId] = useState(null)
  // Rooms + assignments for the dashboard camp — used by the Attendees tab Room column
  const [dashboardRooms, setDashboardRooms] = useState([])
  const [dashboardRoomAssignments, setDashboardRoomAssignments] = useState([])

  const [agreements, setAgreements] = useState([])
  const [installments, setInstallments] = useState([])
  const [allocations, setAllocations] = useState([])
  const [agreementModal, setAgreementModal] = useState({ open: false, reg: null, editing: null })
  const [agreementRows, setAgreementRows] = useState([]) // [{ due_date, amount }]
  const [agreementNote, setAgreementNote] = useState('')
  const [agreementSaving, setAgreementSaving] = useState(false)

  useEffect(() => { loadData() }, [])

  useEffect(() => {
    if (activeTab === 'Users') loadUsers()
    if (activeTab === 'Attendees' && users.length === 0) loadUsers()
    if (activeTab === 'Admin Users') loadAdminUsers()
    if (activeTab === 'Audit Log') loadAuditLogEntries()
  }, [activeTab])

  // If the current tab isn't visible for this role, snap to the first visible one
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0])
    }
  }, [visibleTabs])

  // Re-fetch all tab data whenever the admin selects a different camp
  useEffect(() => {
    if (!selectedDashboardCampId) {
      setRegistrations([])
      setPaymentReceipts([])
      setRooms([])
      setRoomAssignments([])
      setRoomCampRegs([])
      return
    }
    setRegistrations([])       // clear stale data before fetch
    setPaymentReceipts([])
    setRefunds([])
    setExpenses([])
    setDonations([])
    setDashboardRooms([])
    setDashboardRoomAssignments([])
    setRooms([])
    setRoomAssignments([])
    setRoomCampRegs([])
    setExpandedRegIds(new Set())
    loadDashboardData(selectedDashboardCampId)
    loadRoomTabData(selectedDashboardCampId)
  }, [selectedDashboardCampId])

  async function loadData() {
    setLoading(true)

    const [{ data: campData }, { data: campsAll }] = await Promise.all([
      supabase
        .from('camps').select('*')
        .eq('registration_open', true)
        .order('start_date', { ascending: true })
        .limit(1).maybeSingle(),
      supabase
        .from('camps').select('*')
        .order('created_at', { ascending: false }),
    ])

    setCamp(campData ?? null)
    setAllCamps(campsAll ?? [])

    // Default dashboard camp: prefer the active (registration-open) camp, fall back to most recently created
    const initialDashboardCampId = campData?.id ?? (campsAll?.[0]?.id ?? null)
    setSelectedDashboardCampId(initialDashboardCampId)

    setLoading(false)
  }

  async function loadRoomTabData(campId) {
    if (!campId) return
    const [{ data: roomsData }, { data: assignData }, { data: roomRegs }, { data: agreementRegs }] = await Promise.all([
      supabase.from('rooms').select('*').eq('camp_id', campId).order('name'),
      supabase.from('room_assignments')
        .select('*, registrations(full_name, sex, date_of_birth)')
        .eq('camp_id', campId),
      supabase.from('registrations').select('*, pricing_tiers(*)')
        .eq('camp_id', campId)
        .not('payment_status', 'eq', 'Cancelled')
        .order('full_name'),
      supabase.from('payment_agreements')
        .select('registration_id').eq('camp_id', campId).eq('status', 'Active'),
    ])
    setRooms(roomsData ?? [])
    setRoomAssignments(assignData ?? [])
    setRoomCampRegs(roomRegs ?? [])
    setRoomAgreementRegIds(new Set((agreementRegs ?? []).map(a => a.registration_id)))
  }

  async function loadDashboardData(campId) {
    if (!campId) return
    const [
      { data: regs, error: regsError },
      { data: groups, error: groupsError },
      { data: receiptsData },
      { data: refundsData },
      { data: expensesData },
      { data: donationsData },
      { data: dashRoomsData },
      { data: dashAssignData },
      { data: agreementsData },
      { data: installmentsData },
      { data: allocationsData },
    ] = await Promise.all([
      supabase
        .from('registrations').select('*, pricing_tiers(*)')
        .eq('camp_id', campId)
        .order('full_name', { ascending: true }),
      supabase
        .from('age_group_config').select('*')
        .order('min_age', { ascending: true }),
      supabase
        .from('payment_receipts').select('*')
        .eq('camp_id', campId)
        .order('created_at', { ascending: false }),
      supabase
        .from('refunds').select('*')
        .eq('camp_id', campId)
        .order('created_at', { ascending: false }),
      supabase
        .from('expenses').select('*')
        .eq('camp_id', campId)
        .order('created_at', { ascending: false }),
      supabase
        .from('donations').select('*')
        .eq('camp_id', campId)
        .order('donated_at', { ascending: false }),
      supabase.from('rooms').select('*').eq('camp_id', campId).order('name'),
      supabase.from('room_assignments')
        .select('*, registrations(full_name, sex, date_of_birth)')
        .eq('camp_id', campId),
      supabase.from('payment_agreements').select('*').eq('camp_id', campId),
      supabase.from('payment_installments').select('*').eq('camp_id', campId).order('seq'),
      supabase.from('installment_allocations').select('*').eq('camp_id', campId),
    ])
    setRegistrations(regs ?? [])
    setAgeGroups(groups ?? [])
    setPaymentReceipts(receiptsData ?? [])
    setRefunds(refundsData ?? [])
    setExpenses(expensesData ?? [])
    setDonations(donationsData ?? [])
    setDashboardRooms(dashRoomsData ?? [])
    setDashboardRoomAssignments(dashAssignData ?? [])
    setAgreements(agreementsData ?? [])
    setInstallments(installmentsData ?? [])
    setAllocations(allocationsData ?? [])
  }

  function handleDashboardCampChange(campId) {
    setSelectedDashboardCampId(campId)
    // The selectedDashboardCampId useEffect handles clearing + re-fetching
  }

  /* ── Derived data ────────────────────────────────────────────────────────── */

  const selectedDashboardCamp = allCamps.find(c => c.id === selectedDashboardCampId) ?? null

  // All registrations with age/group enrichment (includes Cancelled for lookups)
  const allEnriched = useMemo(
    () => registrations.map(r => {
      const age = getAge(r.date_of_birth)
      return { ...r, age, ageGroup: getAgeGroup(age, ageGroups) }
    }),
    [registrations, ageGroups],
  )

  // Non-cancelled only — used by Overview, Attendees, Transport, Payments, Finances
  const enriched = useMemo(
    () => allEnriched.filter(r => r.payment_status !== 'Cancelled'),
    [allEnriched],
  )

  // Paid-then-refunded registrations that still have an approved receipt —
  // shown as a separate section in the printed Receipt Report to prove income.
  const refundedWithReceipts = useMemo(
    () => selectRefundedWithReceipts(allEnriched, paymentReceipts),
    [allEnriched, paymentReceipts],
  )

  // user_id → account email, sourced from the admin get-users function.
  // Used by the Attendees tab to show each attendee's login email.
  const userEmailMap = useMemo(() => {
    const map = {}
    users.forEach(u => { map[u.id] = u.email })
    return map
  }, [users])

  const filtered = useMemo(() => {
    let list = enriched
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(r => r.full_name.toLowerCase().includes(q))
    }
    if (filterSex !== 'All')      list = list.filter(r => r.sex === filterSex)
    if (filterAgeGroup !== 'All') list = list.filter(r => r.ageGroup?.label === filterAgeGroup)
    if (filterStatus !== 'All')   list = list.filter(r => r.payment_status === filterStatus)
    if (filterChurch !== 'All')   list = list.filter(r => (r.church?.trim() || '') === filterChurch)
    return list
  }, [enriched, search, filterSex, filterAgeGroup, filterStatus, filterChurch])

  const stats = useMemo(() => {
    const paid      = enriched.filter(r => r.payment_status === 'Paid')
    const sponsored = enriched.filter(r => r.payment_status === 'Sponsored')
    const pending   = enriched.filter(r => r.payment_status === 'Pending')
    const confirmed = enriched.filter(r => r.payment_status === 'Confirmed')
    // Exclude Sponsored — they pay nothing, so don't count toward revenue
    const collected = enriched
      .filter(r => r.payment_status !== 'Sponsored')
      .reduce((s, r) => s + (r.amount_paid ?? 0), 0)
    const outstanding = enriched
      .filter(r => r.payment_status !== 'Paid' && r.payment_status !== 'Sponsored')
      .reduce((s, r) => s + Math.max(0, (r.amount_owed ?? 0) - (r.amount_paid ?? 0)), 0)
    return {
      total:              enriched.length,
      totalPaid:          paid.length,
      totalSponsored:     sponsored.length,
      totalPending:       pending.length,
      totalWomen:         enriched.filter(r => r.sex === 'Female').length,
      totalMen:           enriched.filter(r => r.sex === 'Male').length,
      collected,
      confirmedAmount:    confirmed.reduce((s, r) => s + (r.amount_owed ?? 0), 0),
      outstanding,
      needTransportCount: enriched.filter(r => r.needs_transport).length,
    }
  }, [enriched])

  const barChartData = useMemo(
    () => ageGroups.map(g => {
      const inGroup = enriched.filter(r => r.ageGroup?.id === g.id)
      return {
        name:   g.label,
        Male:   inGroup.filter(r => r.sex === 'Male').length,
        Female: inGroup.filter(r => r.sex === 'Female').length,
      }
    }),
    [enriched, ageGroups],
  )

  const paymentByArrival = useMemo(() => {
    const map = {}
    enriched.forEach(r => {
      const date = r.pricing_tiers?.arrival_date ?? r.arrival_date
      if (!date) return
      if (!map[date]) map[date] = { date, label: formatDate(date), Paid: 0, Sponsored: 0, Unpaid: 0 }
      if (r.payment_status === 'Paid') map[date].Paid++
      else if (r.payment_status === 'Sponsored') map[date].Sponsored++
      else map[date].Unpaid++
    })
    return Object.values(map).sort((a, b) => a.date.localeCompare(b.date))
  }, [enriched])

  const transportPieData = useMemo(() => [
    { name: 'Needs Transport', value: enriched.filter(r =>  r.needs_transport).length },
    { name: 'No Transport',    value: enriched.filter(r => !r.needs_transport).length },
  ], [enriched])

  // Distinct church values present in the current registrations (includes
  // manually-entered "Other" churches), sorted alphabetically for the filter.
  const churchOptions = useMemo(() => {
    const set = new Set()
    enriched.forEach(r => { const c = r.church?.trim(); if (c) set.add(c) })
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [enriched])

  const churchChartData = useMemo(() => {
    const map = {}
    enriched.forEach(r => {
      const name = r.church?.trim() || t('admin.churchUnspecified')
      map[name] = (map[name] ?? 0) + 1
    })
    return Object.entries(map)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
  }, [enriched, t])

  const needTransport = useMemo(() => enriched.filter(r => r.needs_transport), [enriched])
  const pendingRegs   = useMemo(() => enriched.filter(r => r.payment_status === 'Pending' || r.payment_status === 'Partial Payment'), [enriched])

  const financeData = useMemo(() => {
    // ── 1. Bucketize receipts ─────────────────────────────────────────────
    const approvedReceipts = paymentReceipts.filter(r => r.status === 'Approved')
    const inReviewReceipts = paymentReceipts.filter(r => r.status === 'Pending Review')

    // ── 2. Summary card values ─────────────────────────────────────────────
    // Total Collected = sum of amount_paid, excluding Sponsored (they pay nothing)
    const totalCollected = enriched
      .filter(r => r.payment_status !== 'Sponsored')
      .reduce((s, r) => s + (r.amount_paid ?? 0), 0)

    // Confirmed (In Review) = sum of Pending Review receipt amounts
    const totalInReview = inReviewReceipts.reduce((s, r) => s + (r.amount ?? 0), 0)

    // Outstanding = remaining balance, excluding Paid and Sponsored
    const totalOutstanding = enriched
      .filter(r => r.payment_status !== 'Paid' && r.payment_status !== 'Sponsored')
      .reduce((s, r) => s + Math.max(0, (r.amount_owed ?? 0) - (r.amount_paid ?? 0)), 0)

    // Sponsored count (for Financial Summary info line)
    const totalSponsored = enriched.filter(r => r.payment_status === 'Sponsored').length

    // Total Expected = sum of amount_owed for all non-cancelled registrations
    const totalExpected = enriched.reduce((s, r) => s + (r.amount_owed ?? 0), 0)

    // ── 3. Payment method breakdown (approved receipts only) ──────────────
    const methodMap = {}
    approvedReceipts.forEach(r => {
      const method = r.payment_method ?? 'Unknown'
      if (!methodMap[method]) methodMap[method] = { method, count: 0, total: 0 }
      methodMap[method].count++
      methodMap[method].total += r.amount ?? 0
    })
    const methodBreakdown = Object.values(methodMap).sort((a, b) => b.total - a.total)

    // ── 4. Refund totals ──────────────────────────────────────────────────
    const totalRefunded = refunds.reduce((s, r) => s + (r.amount ?? 0), 0)
    const netRevenue    = totalCollected - totalRefunded

    // ── 5. Transaction list — receipts + refunds, enriched with reg info ──
    const regMap = {}
    allEnriched.forEach(r => { regMap[r.id] = r })

    const receiptEntries = [...approvedReceipts, ...inReviewReceipts].map(r => ({
      ...r,
      _type:        'receipt',
      registration: regMap[r.registration_id] ?? null,
    }))

    const refundEntries = refunds.map(r => ({
      ...r,
      _type:        'refund',
      registration: regMap[r.registration_id] ?? null,
    }))

    const transactions = [...receiptEntries, ...refundEntries]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    return {
      totalCollected,
      totalConfirmed:   totalInReview,
      totalPending:     totalOutstanding,
      totalExpected,
      totalRefunded,
      totalSponsored,
      netRevenue,
      methodBreakdown,
      transactions,
    }
  }, [enriched, allEnriched, paymentReceipts, refunds])

  // Refunds tab — derived stats
  const refundStats = useMemo(() => ({
    count:              refunds.length,
    totalAmount:        refunds.reduce((s, r) => s + (r.amount ?? 0), 0),
    cancelledCount:     new Set(refunds.map(r => r.registration_id)).size,
  }), [refunds])

  // Refunds tab — filtered list
  const filteredRefunds = useMemo(() => {
    const regMap = {}
    allEnriched.forEach(r => { regMap[r.id] = r })

    return refunds
      .map(r => ({ ...r, registration: regMap[r.registration_id] ?? null }))
      .filter(r => {
        if (refundSearch.trim()) {
          const q = refundSearch.toLowerCase()
          const name = r.registration?.full_name?.toLowerCase() ?? ''
          if (!name.includes(q)) return false
        }
        if (refundDateFrom) {
          if (r.created_at < refundDateFrom) return false
        }
        if (refundDateTo) {
          // include all of the "to" day
          if (r.created_at.slice(0, 10) > refundDateTo) return false
        }
        return true
      })
  }, [refunds, allEnriched, refundSearch, refundDateFrom, refundDateTo])

  // Income & Expenses tab — income breakdown by payment method
  const incomeBreakdown = useMemo(() => {
    const approvedReceipts = paymentReceipts.filter(r => r.status === 'Approved')
    const methodMap = {}
    approvedReceipts.forEach(r => {
      const method = r.payment_method ?? 'Unknown'
      if (!methodMap[method]) methodMap[method] = { method, count: 0, total: 0 }
      methodMap[method].count++
      methodMap[method].total += r.amount ?? 0
    })
    const methods = Object.values(methodMap).sort((a, b) => b.total - a.total)
    const grossIncome  = methods.reduce((s, m) => s + m.total, 0)
    const totalRefunded = refunds.reduce((s, r) => s + (r.amount ?? 0), 0)
    const netIncome    = grossIncome - totalRefunded
    return { methods, grossIncome, totalRefunded, netIncome }
  }, [paymentReceipts, refunds])

  // Income & Expenses tab — donations summary (income)
  const donationsSummary = useMemo(() => {
    const total = sumDonations(donations)
    return { list: donations, total }
  }, [donations])

  // Income & Expenses tab — expense summary + net result (includes donations)
  const expenseSummary = useMemo(() => {
    const totalGeneral = expenses.reduce((s, e) => s + (e.amount ?? 0), 0)
    const netResult    = computeNetResult(incomeBreakdown.netIncome, donationsSummary.total, totalGeneral)
    return { general: expenses, totalGeneral, netResult }
  }, [expenses, incomeBreakdown, donationsSummary])

  const projectedSummary = useMemo(() => {
    const pending = pendingAgreementIncome(agreements, installments, allocations)
    return {
      pendingAgreementIncome: pending,
      projectedNetResult: expenseSummary.netResult + pending,
    }
  }, [agreements, installments, allocations, expenseSummary])

  const assignmentMap = useMemo(() => {
    const map = {}
    dashboardRoomAssignments.forEach(a => { map[a.registration_id] = a })
    return map
  }, [dashboardRoomAssignments])

  const agreementByReg = useMemo(() => {
    const map = {}
    agreements.forEach(a => { if (a.status !== 'Cancelled') map[a.registration_id] = a })
    return map
  }, [agreements])

  const installmentsByAgreement = useMemo(() => {
    const map = {}
    installments.forEach(i => { (map[i.agreement_id] ??= []).push(i) })
    Object.values(map).forEach(list => list.sort((a, b) => a.seq - b.seq))
    return map
  }, [installments])

  const filteredAuditLog = useMemo(() => {
    return auditLogEntries.filter(e => {
      if (auditLogSearch.trim()) {
        const q = auditLogSearch.toLowerCase()
        if (!(
          (e.user_email ?? '').toLowerCase().includes(q) ||
          (e.action    ?? '').toLowerCase().includes(q) ||
          (e.entity    ?? '').toLowerCase().includes(q)
        )) return false
      }
      if (auditLogDateFrom && e.created_at < auditLogDateFrom) return false
      if (auditLogDateTo   && e.created_at.slice(0, 10) > auditLogDateTo) return false
      return true
    })
  }, [auditLogEntries, auditLogSearch, auditLogDateFrom, auditLogDateTo])

  const roomOccupancy = useMemo(() => {
    const counts = {}
    rooms.forEach(r => { counts[r.id] = 0 })
    roomAssignments.forEach(a => { if (counts[a.room_id] !== undefined) counts[a.room_id]++ })
    return counts
  }, [rooms, roomAssignments])

  const roomsReportData = useMemo(
    () => buildRoomsReportData(rooms, roomAssignments, roomCampRegs),
    [rooms, roomAssignments, roomCampRegs],
  )

  /* ── Handlers ────────────────────────────────────────────────────────────── */

  function showToast(msg, type = 'neutral') {
    setToast({ message: msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  async function openEditModal(reg) {
    setEditingReg(reg)
    setModalError('')
    // Fetch pricing tiers for this camp so the dropdown is populated
    const { data: tiersData } = await supabase
      .from('pricing_tiers')
      .select('*')
      .eq('camp_id', reg.camp_id)
      .order('arrival_date', { ascending: true })
    setEditCampTiers(tiersData ?? [])
    setEditForm({
      full_name:               reg.full_name,
      date_of_birth:           reg.date_of_birth,
      sex:                     reg.sex,
      phone:                   reg.phone,
      emergency_contact_name:  reg.emergency_contact_name,
      emergency_contact_phone: reg.emergency_contact_phone,
      pricing_tier_id:         reg.pricing_tier_id ?? '',
      needs_transport:         reg.needs_transport,
      payment_status:          reg.payment_status,
      admin_note:              '',
    })
  }

  async function handleModalSave() {
    if (!editForm) return
    setModalError('')
    setModalSaving(true)
    const editTier = editCampTiers.find(t => t.id === editForm.pricing_tier_id) ?? null
    const editCamp = allCamps.find(c => c.id === editingReg?.camp_id) ?? null
    const campTransportCost = parseFloat(editCamp?.transport_cost ?? 0)
    const transportAdd = editForm.needs_transport ? campTransportCost : 0
    // Fix #6: always recalculate amount_owed — fall back to the reg's existing
    // tier price when pricing_tier_id is unchanged / unset.
    const baseFee = editTier?.price ?? editingReg?.pricing_tiers?.price ?? 0
    const newAmountOwed = baseFee + transportAdd
    const updateObj = {
      full_name:               editForm.full_name,
      date_of_birth:           editForm.date_of_birth,
      sex:                     editForm.sex,
      phone:                   editForm.phone,
      emergency_contact_name:  editForm.emergency_contact_name,
      emergency_contact_phone: editForm.emergency_contact_phone,
      pricing_tier_id:         editForm.pricing_tier_id || null,
      needs_transport:         editForm.needs_transport,
      payment_status:          editForm.payment_status,
      amount_owed:             newAmountOwed,
      ...(editForm.payment_status === 'Cancelled'
        ? { cancelled_by: 'admin', cancelled_at: new Date().toISOString() }
        : { cancelled_by: null, cancelled_at: null }),
      ...(editTier ? { arrival_date: editTier.arrival_date } : {}),
      ...(editForm.admin_note ? { admin_note: editForm.admin_note } : {}),
    }
    const { error } = await supabase.from('registrations').update(updateObj).eq('id', editingReg.id)
    if (error) { setModalError(error.message); setModalSaving(false); return }
    setRegistrations(prev => prev.map(r =>
      r.id === editingReg.id
        ? { ...r, ...updateObj, pricing_tiers: editTier ?? r.pricing_tiers }
        : r,
    ))
    logAction({ user, profile, action: 'edit_registration', entity: 'registrations', entity_id: editingReg.id, details: { full_name: editForm.full_name, status: editForm.payment_status } })
    setEditingReg(null)
    setEditForm(null)
    setModalSaving(false)
    showToast(t('admin.toastRegUpdated'), 'success')
  }

  async function markAsPaid(regId) {
    const { error } = await supabase.from('registrations').update({ payment_status: 'Paid' }).eq('id', regId)
    // Fix #1: show error toast instead of silently doing nothing on failure
    if (error) {
      showToast(error.message, 'error')
    } else {
      setRegistrations(prev => prev.map(r => r.id === regId ? { ...r, payment_status: 'Paid' } : r))
      logAction({ user, profile, action: 'mark_as_paid', entity: 'registrations', entity_id: regId })
      showToast('Marked as paid.', 'success')
    }
  }

  function openReceiptModal(reg) {
    setReceiptModal({ open: true, reg })
    setReceiptNoteError(false)
    setReceiptEditForm({
      receipt_amount: reg.receipt_amount ?? '',
      payment_method: reg.payment_method ?? '',
      admin_note: '',
    })
  }

  async function handleReceiptMarkAsPaid() {
    setReceiptSaving(true)
    const amountPaid = receiptEditForm.receipt_amount
      ? parseFloat(receiptEditForm.receipt_amount)
      : receiptModal.reg.amount_owed
    const method = receiptEditForm.payment_method || receiptModal.reg.payment_method
    const updateObj = {
      payment_status: 'Paid',
      amount_owed: amountPaid,
      payment_method: method,
      ...(receiptEditForm.admin_note ? { admin_note: receiptEditForm.admin_note } : {}),
    }
    const { error } = await supabase.from('registrations').update(updateObj).eq('id', receiptModal.reg.id)
    if (!error) {
      setRegistrations(prev => prev.map(r => r.id === receiptModal.reg.id ? { ...r, ...updateObj } : r))
      setReceiptModal({ open: false, reg: null })
      showToast(t('admin.toastMarkedPaid'), 'success')
    } else {
      showToast(error.message, 'error')
    }
    setReceiptSaving(false)
  }

  async function handleRejectReceipt() {
    // Rejection reason is required before we can proceed
    if (!receiptEditForm.admin_note.trim()) {
      setReceiptNoteError(true)
      return
    }
    setReceiptNoteError(false)
    setReceiptSaving(true)

    const updateObj = {
      payment_status: 'Pending',
      receipt_url: null,
      receipt_amount: null,
      payment_method: null,
      admin_note: receiptEditForm.admin_note,
    }

    const { error } = await supabase.from('registrations').update(updateObj).eq('id', receiptModal.reg.id)
    if (error) {
      showToast(error.message, 'error')
      setReceiptSaving(false)
      return
    }

    setRegistrations(prev => prev.map(r =>
      r.id === receiptModal.reg.id ? { ...r, ...updateObj } : r,
    ))

    // Send rejection email (non-blocking — fire and don't wait to fail the UI)
    supabase.functions.invoke('send-rejection', {
      body: {
        registration_id: receiptModal.reg.id,
        rejection_note:  receiptEditForm.admin_note,
      },
    }).catch(() => {/* handled via toast below if needed */})

    setReceiptModal({ open: false, reg: null })
    showToast(t('admin.toastReceiptRejected'), 'neutral')
    setReceiptSaving(false)
  }

  /* ── Admin: upload receipt on behalf of attendee ─────────────────────── */

  const canUploadOnBehalf = profile?.role === 'admin' || profile?.role === 'superadmin'

  function openAdminUpload(reg) {
    setAdminUpload({ open: true, reg })
    setAdminUploadForm({ file: null, amount: '', payment_method: '' })
    setAdminUploadError('')
  }

  function closeAdminUpload() {
    setAdminUpload({ open: false, reg: null })
    setAdminUploadForm({ file: null, amount: '', payment_method: '' })
    setAdminUploadError('')
  }

  async function handleAdminUploadReceipt() {
    const reg = adminUpload.reg
    if (!reg) return
    setAdminUploadError('')
    setAdminUploadSaving(true)

    const ERR_KEYS = {
      NO_FILE:        'myReg.errorNoFile',
      FILE_TOO_LARGE: 'myReg.errorFileTooLarge',
      INVALID_AMOUNT: 'myReg.errorInvalidAmount',
      NO_METHOD:      'myReg.errorNoPaymentMethod',
    }

    try {
      const receipt = await uploadReceipt({
        file:           adminUploadForm.file,
        registrationId: reg.id,
        campId:         reg.camp_id,
        userId:         reg.user_id,
        amount:         adminUploadForm.amount,
        paymentMethod:  adminUploadForm.payment_method,
      })

      logAction({
        user, profile,
        action: 'upload_receipt_on_behalf',
        entity: 'payment_receipts',
        entity_id: receipt.id,
        details: { registration_id: reg.id, full_name: reg.full_name, amount: receipt.amount, payment_method: receipt.payment_method },
      })

      setPaymentReceipts(prev => [...prev, receipt])
      closeAdminUpload()
      showToast(t('admin.toastReceiptUploaded', { name: reg.full_name }), 'success')
      // Reuse the existing approve modal so the admin confirms the amount + credits payment.
      openApproveModal(receipt, reg)
    } catch (err) {
      setAdminUploadError(ERR_KEYS[err?.code] ? t(ERR_KEYS[err.code]) : (err?.message || String(err)))
    } finally {
      setAdminUploadSaving(false)
    }
  }

  /* ── Per-receipt approve / reject ────────────────────────────────────── */

  function openApproveModal(receipt, reg) {
    setApproveModal({ open: true, receipt, reg })
    setApproveForm({ amount: String(receipt.amount ?? ''), admin_note: '' })
    setApproveNoteError(false)

    // Pre-fill allocations if this registration has an active agreement
    const agreement = agreementByReg[reg.id]
    const alloc = {}
    if (agreement) {
      let remaining = receipt.amount ?? 0
      const insts = (installmentsByAgreement[agreement.id] ?? [])
        .filter(i => deriveInstallmentStatus(i, allocations) === 'Unpaid')
      for (const inst of insts) {
        if (remaining <= 0) break
        const need = installmentRemaining(inst, allocations)
        const apply = Math.min(need, remaining)
        alloc[inst.id] = String(apply)
        remaining -= apply
      }
    }
    setApproveAllocations(alloc)
  }

  function closeApproveModal() {
    setApproveModal({ open: false, receipt: null, reg: null })
    setApproveForm({ amount: '', admin_note: '' })
    setApproveNoteError(false)
    setApproveAllocations({})
  }

  async function handleApproveReceipt() {
    const { receipt, reg } = approveModal
    if (!receipt || !reg) return
    setApproveSaving(true)

    const approvedAmount = parseFloat(approveForm.amount) || receipt.amount
    const newAmountPaid  = (reg.amount_paid ?? 0) + approvedAmount
    const newStatus      = newAmountPaid >= (reg.amount_owed ?? 0) ? 'Paid' : 'Partial Payment'

    // Update the receipt status
    const { error: receiptErr } = await supabase
      .from('payment_receipts')
      .update({
        status:     'Approved',
        amount:     approvedAmount,
        admin_note: approveForm.admin_note || null,
      })
      .eq('id', receipt.id)

    if (receiptErr) {
      showToast(receiptErr.message, 'error')
      setApproveSaving(false)
      return
    }

    // Update the registration
    const { error: regErr } = await supabase
      .from('registrations')
      .update({ amount_paid: newAmountPaid, payment_status: newStatus })
      .eq('id', reg.id)

    if (regErr) {
      showToast(regErr.message, 'error')
      setApproveSaving(false)
      return
    }

    // ── Installment allocations (only if the reg has an active agreement) ──
    const agreement = agreementByReg[reg.id]
    if (agreement) {
      const allocRows = Object.entries(approveAllocations)
        .map(([installment_id, v]) => ({ installment_id, amount: parseFloat(v) || 0 }))
        .filter(a => a.amount > 0)
        .map(a => ({ ...a, receipt_id: receipt.id, camp_id: selectedDashboardCampId }))

      const totalAllocated = allocRows.reduce((s, a) => s + a.amount, 0)
      if (totalAllocated > approvedAmount + 0.005) {
        showToast(t('admin.allocationExceeds'), 'error')
        setApproveSaving(false)
        return
      }

      if (allocRows.length) {
        const { error: allocErr } = await supabase.from('installment_allocations').insert(allocRows)
        if (allocErr) { showToast(allocErr.message, 'error'); setApproveSaving(false); return }

        // Recompute affected installment statuses with the new allocations merged in
        const merged = [...allocations, ...allocRows]
        const agInstallments = installmentsByAgreement[agreement.id] ?? []
        for (const inst of agInstallments) {
          const newStatus = deriveInstallmentStatus(inst, merged)
          if (newStatus !== inst.status) {
            await supabase.from('payment_installments').update({ status: newStatus }).eq('id', inst.id)
          }
        }
        const agStatus = deriveAgreementStatus(agInstallments, merged)
        if (agStatus !== agreement.status) {
          await supabase.from('payment_agreements').update({ status: agStatus }).eq('id', agreement.id)
        }
      }
    }

    logAction({ user, profile, action: 'approve_receipt', entity: 'payment_receipts', entity_id: receipt.id, details: { amount: approvedAmount, registration_id: reg.id, full_name: reg.full_name } })
    closeApproveModal()
    showToast(t('admin.toastReceiptApproved', { name: reg.full_name, status: newStatus }), 'success')
    await loadDashboardData(selectedDashboardCampId)
    setApproveSaving(false)
  }

  function openAgreementModal(reg) {
    const existing = agreementByReg[reg.id] ?? null
    if (existing) {
      const rows = (installmentsByAgreement[existing.id] ?? []).map(i => ({
        id: i.id,
        due_date: i.due_date,
        amount: String(i.amount),
        locked: installmentPaidAmount(i.id, allocations) > 0,
      }))
      setAgreementRows(rows.length ? rows : [{ due_date: '', amount: '' }])
      setAgreementNote(existing.note ?? '')
    } else {
      setAgreementRows([{ due_date: '', amount: '' }])
      setAgreementNote('')
    }
    setAgreementModal({ open: true, reg, editing: existing })
  }

  function closeAgreementModal() {
    setAgreementModal({ open: false, reg: null, editing: null })
    setAgreementRows([])
    setAgreementNote('')
  }

  async function saveAgreement() {
    const { reg, editing } = agreementModal
    if (!reg) return
    const rows = agreementRows
      .filter(r => r.due_date && parseFloat(r.amount) > 0)
      .map((r, idx) => ({ ...r, seq: idx + 1, amountNum: parseFloat(r.amount) }))
    if (rows.length === 0) {
      showToast(t('admin.agreementNeedsRows'), 'error')
      return
    }
    setAgreementSaving(true)
    const total = rows.reduce((s, r) => s + r.amountNum, 0)

    let agreementId = editing?.id
    if (editing) {
      const { error } = await supabase.from('payment_agreements')
        .update({ total_amount: total, note: agreementNote || null })
        .eq('id', editing.id)
      if (error) { showToast(error.message, 'error'); setAgreementSaving(false); return }
      // Replace only unlocked installments (rows with no id or not locked)
      const lockedIds = rows.filter(r => r.id && r.locked).map(r => r.id)
      const { error: delErr } = await supabase.from('payment_installments')
        .delete().eq('agreement_id', editing.id)
        .not('id', 'in', `(${lockedIds.length ? lockedIds.join(',') : '00000000-0000-0000-0000-000000000000'})`)
      if (delErr) { showToast(delErr.message, 'error'); setAgreementSaving(false); return }
    } else {
      const { data, error } = await supabase.from('payment_agreements')
        .insert({ registration_id: reg.id, camp_id: selectedDashboardCampId, total_amount: total, status: 'Active', note: agreementNote || null, created_by: user.id })
        .select('*').single()
      if (error) { showToast(error.message, 'error'); setAgreementSaving(false); return }
      agreementId = data.id
    }

    const toInsert = rows
      .filter(r => !(r.id && r.locked))
      .map(r => ({ agreement_id: agreementId, camp_id: selectedDashboardCampId, seq: r.seq, due_date: r.due_date, amount: r.amountNum, status: 'Unpaid' }))
    if (toInsert.length) {
      const { error } = await supabase.from('payment_installments').insert(toInsert)
      if (error) { showToast(error.message, 'error'); setAgreementSaving(false); return }
    }

    logAction({ user, profile, action: editing ? 'edit_agreement' : 'create_agreement', entity: 'payment_agreements', entity_id: agreementId, details: { registration_id: reg.id, full_name: reg.full_name, total } })
    await loadDashboardData(selectedDashboardCampId)
    closeAgreementModal()
    showToast(t('admin.agreementSaved'), 'success')
    setAgreementSaving(false)
  }

  async function cancelAgreement(agreement) {
    if (!agreement) return
    const { error } = await supabase.from('payment_agreements')
      .update({ status: 'Cancelled' }).eq('id', agreement.id)
    if (error) { showToast(error.message, 'error'); return }
    logAction({ user, profile, action: 'cancel_agreement', entity: 'payment_agreements', entity_id: agreement.id, details: { registration_id: agreement.registration_id } })
    await loadDashboardData(selectedDashboardCampId)
    showToast(t('admin.agreementCancelled'), 'neutral')
  }

  async function handleRejectFromApproveModal() {
    const { receipt, reg } = approveModal
    if (!approveForm.admin_note.trim()) {
      setApproveNoteError(true)
      return
    }
    setApproveNoteError(false)
    setApproveSaving(true)

    const { error } = await supabase
      .from('payment_receipts')
      .update({ status: 'Rejected', admin_note: approveForm.admin_note })
      .eq('id', receipt.id)

    if (error) {
      showToast(error.message, 'error')
      setApproveSaving(false)
      return
    }

    setPaymentReceipts(prev => prev.map(r =>
      r.id === receipt.id ? { ...r, status: 'Rejected', admin_note: approveForm.admin_note } : r,
    ))

    // Fire rejection email (non-blocking)
    supabase.functions.invoke('send-rejection', {
      body: { registration_id: reg.id, rejection_note: approveForm.admin_note },
    }).catch(() => {})

    logAction({ user, profile, action: 'reject_receipt', entity: 'payment_receipts', entity_id: receipt.id, details: { registration_id: reg.id, note: approveForm.admin_note } })
    closeApproveModal()
    showToast(t('admin.toastReceiptRejected'), 'neutral')
    setApproveSaving(false)
  }

  function exportRoomsCSV() {
    const csv = roomsToCsv(roomsReportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `rooms-${selectedDashboardCamp?.name ?? 'camp'}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function exportFinancesCSV() {
    const rows = [
      ['Attendee', 'Amount', 'Payment Method', 'Receipt Status', 'Submitted Date'],
      ...financeData.transactions.map(r => [
        r.registration?.full_name ?? '',
        r._type === 'refund' ? -(r.amount ?? 0) : (r.amount ?? 0),
        paymentMethodLabel(r.payment_method, t) ?? '',
        r._type === 'refund' ? 'Refund' : r.status,
        new Date(r.created_at).toLocaleDateString(),
      ]),
    ]
    const csv = rows
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `payment-receipts-${selectedDashboardCamp?.name ?? 'camp'}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function buildAttendeeRows() {
    return filtered.map(r => {
      const assignment = assignmentMap[r.id]
      const room = assignment
        ? (dashboardRooms.find(rm => rm.id === assignment.room_id)?.name ?? '—')
        : '—'
      return {
        fullName: r.full_name ?? '—',
        email: userEmailMap[r.user_id] ?? '—',
        phone: r.phone ?? '—',
        age: r.age ?? '—',
        sex: r.sex ?? '—',
        church: r.church?.trim() || '—',
        ageGroup: r.ageGroup?.label ?? '—',
        arrivalDate: formatDate(r.pricing_tiers?.arrival_date ?? r.arrival_date),
        transport: r.needs_transport ? 'Yes' : 'No',
        room,
        status: r.payment_status ?? '—',
      }
    })
  }

  function attendeeColumnHeaders() {
    return [
      t('admin.colFullName'), t('admin.colEmail'), t('admin.colPhone'),
      t('admin.colAge'), t('admin.colSex'), t('admin.colChurch'),
      t('admin.colAgeGroup'), t('admin.colArrivalDate'), t('admin.colTransport'),
      t('admin.colRoom'), t('admin.colStatus'),
    ]
  }

  function exportAttendeesCSV() {
    const csv = attendeesToCsv(buildAttendeeRows(), attendeeColumnHeaders())
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `attendees-${selectedDashboardCamp?.name ?? 'camp'}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  /* ── Refund handlers ─────────────────────────────────────────────────── */

  function openRefundModal(reg) {
    setRefundModal({ open: true, reg })
    setRefundForm({ amount: String(reg.amount_paid ?? ''), reason: '' })
    setRefundError('')
  }

  function closeRefundModal() {
    setRefundModal({ open: false, reg: null })
    setRefundForm({ amount: '', reason: '' })
    setRefundError('')
  }

  async function handleProcessRefund() {
    const { reg } = refundModal
    if (!reg) return

    const amount = parseFloat(refundForm.amount)
    if (!amount || amount <= 0) {
      setRefundError(t('admin.errorRefundAmount'))
      return
    }
    if (amount > (reg.amount_paid ?? 0) + 0.005) {
      setRefundError(t('admin.errorRefundExceeds', { amount: formatCurrency(reg.amount_paid ?? 0) }))
      return
    }
    if (!refundForm.reason.trim()) {
      setRefundError(t('admin.errorRefundReason'))
      return
    }

    setRefundError('')
    setRefundSaving(true)

    // Insert refund record
    const { data: newRefund, error: insertErr } = await supabase
      .from('refunds')
      .insert({
        registration_id:   reg.id,
        camp_id:           reg.camp_id,
        user_id:           reg.user_id,
        amount,
        reason:            refundForm.reason.trim(),
        refunded_by:       user.id,
        refunded_by_email: user.email ?? null,
      })
      .select()
      .single()

    if (insertErr) {
      setRefundError(insertErr.message)
      setRefundSaving(false)
      return
    }

    // Update registration: cancel it and adjust amount_paid / refund_amount
    const newAmountPaid    = Math.max(0, (reg.amount_paid ?? 0) - amount)
    const newRefundAmount  = (reg.refund_amount ?? 0) + amount

    const { error: updateErr } = await supabase
      .from('registrations')
      .update({
        payment_status: 'Cancelled',
        amount_paid:    newAmountPaid,
        refund_amount:  newRefundAmount,
        cancelled_by:   'admin',
        cancelled_at:   new Date().toISOString(),
      })
      .eq('id', reg.id)

    if (updateErr) {
      setRefundError(updateErr.message)
      setRefundSaving(false)
      return
    }

    // Update local state
    const cancelledAt = new Date().toISOString()
    setRegistrations(prev => prev.map(r =>
      r.id === reg.id
        ? { ...r, payment_status: 'Cancelled', amount_paid: newAmountPaid, refund_amount: newRefundAmount, cancelled_by: 'admin', cancelled_at: cancelledAt }
        : r,
    ))
    setRefunds(prev => [newRefund, ...prev])

    // Fire refund email (non-blocking)
    supabase.functions.invoke('send-refund', {
      body: {
        registration_id: reg.id,
        refund_amount:   amount,
        reason:          refundForm.reason.trim(),
      },
    }).catch(() => {})

    logAction({ user, profile, action: 'process_refund', entity: 'registrations', entity_id: reg.id, details: { amount, reason: refundForm.reason.trim(), full_name: reg.full_name } })
    closeRefundModal()
    showToast(t('admin.toastRefundProcessed', { amount: formatCurrency(amount), name: reg.full_name }), 'success')
    setRefundSaving(false)
  }

  function exportRefundsCSV() {
    const rows = [
      ['Name', 'Camp', 'Amount Refunded', 'Reason', 'Processed Date', 'Refunded By'],
      ...filteredRefunds.map(r => [
        r.registration?.full_name ?? '',
        selectedDashboardCamp?.name ?? '',
        r.amount ?? 0,
        r.reason ?? '',
        new Date(r.created_at).toLocaleDateString(),
        r.refunded_by_email ?? r.refunded_by ?? '',
      ]),
    ]
    const csv = rows
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `refunds-${selectedDashboardCamp?.name ?? 'camp'}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function exportFinancialReportCSV() {
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`
    const campName = selectedDashboardCamp?.name ?? 'camp'

    const rows = []

    // Section A — Income Breakdown
    rows.push([esc('INCOME BREAKDOWN')])
    rows.push([esc('Payment Method'), esc('Amount')])
    incomeBreakdown.methods.forEach(m => {
      rows.push([esc(paymentMethodLabel(m.method, t) ?? m.method), esc(formatCurrency(m.total))])
    })
    rows.push([esc('Gross Income'), esc(formatCurrency(incomeBreakdown.grossIncome))])
    rows.push([esc('Refunds'), esc(`-${formatCurrency(incomeBreakdown.totalRefunded)}`)])
    rows.push([esc('Net Income'), esc(formatCurrency(incomeBreakdown.netIncome))])
    rows.push([])

    // Section A2 — Donations (income)
    rows.push([esc('DONATIONS')])
    rows.push([esc('Donor'), esc('Note'), esc('Category'), esc('Amount'), esc('Date')])
    donationsSummary.list.forEach(d => {
      rows.push([
        esc(donorDisplayName(d, t('admin.donationAnonymous'))),
        esc(d.description ?? ''),
        esc(d.category ?? 'Other'),
        esc(formatCurrency(d.amount)),
        esc(d.donated_at ? new Date(d.donated_at + 'T00:00:00').toLocaleDateString() : ''),
      ])
    })
    rows.push([esc('Total Donations'), esc(''), esc(''), esc(formatCurrency(donationsSummary.total)), esc('')])
    rows.push([])

    // Section B — General Expenses
    rows.push([esc('GENERAL EXPENSES')])
    rows.push([esc('Description'), esc('Category'), esc('Amount'), esc('Date')])
    expenseSummary.general.forEach(e => {
      rows.push([
        esc(e.description),
        esc(e.category ?? 'Other'),
        esc(formatCurrency(e.amount)),
        esc(e.created_at ? new Date(e.created_at).toLocaleDateString() : ''),
      ])
    })
    rows.push([esc('Total Expenses'), esc(''), esc(formatCurrency(expenseSummary.totalGeneral)), esc('')])
    rows.push([])

    // Section C — Financial Summary
    rows.push([esc('FINANCIAL SUMMARY')])
    rows.push([esc('Net Income'), esc(formatCurrency(incomeBreakdown.netIncome))])
    rows.push([esc('Donations'), esc(`+${formatCurrency(donationsSummary.total)}`)])
    rows.push([esc('General Expenses'), esc(formatCurrency(expenseSummary.totalGeneral))])
    const sign = expenseSummary.netResult >= 0 ? '+' : ''
    rows.push([esc('Net Result'), esc(`${sign}${formatCurrency(expenseSummary.netResult)}`)])
    rows.push([])

    // Section D — Expense Detail
    rows.push([esc('EXPENSE DETAIL')])
    rows.push([esc('Description'), esc('Category'), esc('Amount'), esc('Date'), esc('Invoice URL')])
    expenseSummary.general.forEach(e => {
      rows.push([
        esc(e.description),
        esc(e.category ?? 'Other'),
        esc(formatCurrency(e.amount)),
        esc(e.created_at ? new Date(e.created_at).toLocaleDateString() : ''),
        esc(e.invoice_url ?? ''),
      ])
    })
    rows.push([esc('Total Expenses'), esc(''), esc(formatCurrency(expenseSummary.totalGeneral)), esc(''), esc('')])

    rows.push([])

    // Section E — Donation Detail
    rows.push([esc('DONATION DETAIL')])
    rows.push([esc('Donor'), esc('Note'), esc('Category'), esc('Amount'), esc('Date'), esc('Receipt URL')])
    donationsSummary.list.forEach(d => {
      rows.push([
        esc(donorDisplayName(d, t('admin.donationAnonymous'))),
        esc(d.description ?? ''),
        esc(d.category ?? 'Other'),
        esc(formatCurrency(d.amount)),
        esc(d.donated_at ? new Date(d.donated_at + 'T00:00:00').toLocaleDateString() : ''),
        esc(d.receipt_url ?? ''),
      ])
    })
    rows.push([esc('Total Donations'), esc(''), esc(''), esc(formatCurrency(donationsSummary.total)), esc(''), esc('')])

    const csv  = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `financial-report-${campName}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  /* ── Expense handlers ────────────────────────────────────────────────── */

  function openNewExpenseModal() {
    setExpenseForm({ description: '', category: 'Food', amount: '', invoice_file: null })
    setExpenseError('')
    setExpenseModal({ open: true, mode: 'new', expense: null })
  }

  function openEditExpenseModal(expense) {
    setExpenseForm({
      description:  expense.description,
      category:     expense.category ?? 'Food',
      amount:       String(expense.amount ?? ''),
      invoice_file: null,
    })
    setExpenseError('')
    setExpenseModal({ open: true, mode: 'edit', expense })
  }

  function closeExpenseModal() {
    setExpenseModal({ open: false, mode: 'new', expense: null })
    setExpenseForm({ description: '', category: 'Food', amount: '', invoice_file: null })
    setExpenseError('')
  }

  async function handleSaveExpense() {
    if (!expenseForm.description.trim()) { setExpenseError(t('admin.errorExpenseDescription')); return }
    const amount = parseFloat(expenseForm.amount)
    if (!amount || amount <= 0) { setExpenseError(t('admin.errorExpenseAmount')); return }
    if (!selectedDashboardCampId) return

    setExpenseSaving(true)
    setExpenseError('')

    let invoiceUrl = expenseModal.expense?.invoice_url ?? null

    // Upload invoice if a file was selected
    if (expenseForm.invoice_file) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
      if (expenseForm.invoice_file.size > MAX_FILE_SIZE) {
        setExpenseError(t('admin.errorInvoiceTooLarge'))
        setExpenseSaving(false)
        return
      }
      const file = expenseForm.invoice_file
      const ext  = file.name.split('.').pop()
      const path = `${selectedDashboardCampId}/${Date.now()}.${ext}`
      const { error: uploadErr } = await supabase.storage
        .from('invoices')
        .upload(path, file, { upsert: false })
      if (uploadErr) {
        setExpenseError(`Invoice upload failed: ${uploadErr.message}`)
        setExpenseSaving(false)
        return
      }
      const { data: urlData } = supabase.storage.from('invoices').getPublicUrl(path)
      invoiceUrl = urlData.publicUrl
    }

    const payload = {
      camp_id:      selectedDashboardCampId,
      description:  expenseForm.description.trim(),
      amount,
      expense_type: 'general',
      category:     expenseForm.category,
      invoice_url:  invoiceUrl,
      created_by:   user.id,
    }

    if (expenseModal.mode === 'new') {
      const { data, error } = await supabase.from('expenses').insert(payload).select().single()
      if (error) { setExpenseError(error.message); setExpenseSaving(false); return }
      setExpenses(prev => [data, ...prev])
    } else {
      const { data, error } = await supabase
        .from('expenses').update(payload).eq('id', expenseModal.expense.id).select().single()
      if (error) { setExpenseError(error.message); setExpenseSaving(false); return }
      setExpenses(prev => prev.map(e => e.id === expenseModal.expense.id ? data : e))
    }

    setExpenseSaving(false)
    logAction({ user, profile, action: expenseModal.mode === 'new' ? 'add_expense' : 'edit_expense', entity: 'expenses', details: { description: expenseForm.description.trim(), amount, camp_id: selectedDashboardCampId } })
    closeExpenseModal()
    showToast(t('admin.toastExpenseSaved'), 'success')
  }

  async function handleDeleteExpense(id) {
    setDeletingExpenseId(id)
    const { error } = await supabase.from('expenses').delete().eq('id', id)
    if (!error) setExpenses(prev => prev.filter(e => e.id !== id))
    else showToast(error.message, 'error')
    setDeletingExpenseId(null)
  }

  /* ── Donation handlers ───────────────────────────────────────────────── */

  function openNewDonationModal() {
    setDonationForm({
      donor_name: '', description: '', category: 'Cash',
      donated_at: new Date().toISOString().split('T')[0], amount: '', receipt_file: null,
    })
    setDonationError('')
    setDonationModal({ open: true, mode: 'new', donation: null })
  }

  function openEditDonationModal(donation) {
    setDonationForm({
      donor_name:   donation.donor_name ?? '',
      description:  donation.description ?? '',
      category:     donation.category ?? 'Cash',
      donated_at:   donation.donated_at ?? new Date().toISOString().split('T')[0],
      amount:       String(donation.amount ?? ''),
      receipt_file: null,
    })
    setDonationError('')
    setDonationModal({ open: true, mode: 'edit', donation })
  }

  function closeDonationModal() {
    setDonationModal({ open: false, mode: 'new', donation: null })
    setDonationForm({
      donor_name: '', description: '', category: 'Cash',
      donated_at: new Date().toISOString().split('T')[0], amount: '', receipt_file: null,
    })
    setDonationError('')
  }

  async function handleSaveDonation() {
    const amount = parseFloat(donationForm.amount)
    if (!amount || amount <= 0) { setDonationError(t('admin.errorDonationAmount')); return }
    if (!selectedDashboardCampId) return

    setDonationSaving(true)
    setDonationError('')

    let receiptUrl = donationModal.donation?.receipt_url ?? null

    // Upload receipt if a file was selected
    if (donationForm.receipt_file) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
      if (donationForm.receipt_file.size > MAX_FILE_SIZE) {
        setDonationError(t('admin.errorInvoiceTooLarge'))
        setDonationSaving(false)
        return
      }
      const file = donationForm.receipt_file
      const ext  = file.name.split('.').pop()
      const path = `${selectedDashboardCampId}/donations/${Date.now()}.${ext}`
      const { error: uploadErr } = await supabase.storage
        .from('invoices')
        .upload(path, file, { upsert: false })
      if (uploadErr) {
        setDonationError(`Receipt upload failed: ${uploadErr.message}`)
        setDonationSaving(false)
        return
      }
      const { data: urlData } = supabase.storage.from('invoices').getPublicUrl(path)
      receiptUrl = urlData.publicUrl
    }

    const payload = {
      camp_id:     selectedDashboardCampId,
      donor_name:  donationForm.donor_name.trim() || null,
      description: donationForm.description.trim() || null,
      amount,
      category:    donationForm.category,
      donated_at:  donationForm.donated_at,
      receipt_url: receiptUrl,
      created_by:  user.id,
    }

    if (donationModal.mode === 'new') {
      const { data, error } = await supabase.from('donations').insert(payload).select().single()
      if (error) { setDonationError(error.message); setDonationSaving(false); return }
      setDonations(prev => [data, ...prev])
    } else {
      const { data, error } = await supabase
        .from('donations').update(payload).eq('id', donationModal.donation.id).select().single()
      if (error) { setDonationError(error.message); setDonationSaving(false); return }
      setDonations(prev => prev.map(d => d.id === donationModal.donation.id ? data : d))
    }

    setDonationSaving(false)
    logAction({ user, profile, action: donationModal.mode === 'new' ? 'add_donation' : 'edit_donation', entity: 'donations', details: { donor_name: payload.donor_name, amount, camp_id: selectedDashboardCampId } })
    closeDonationModal()
    showToast(t('admin.toastDonationSaved'), 'success')
  }

  async function handleDeleteDonation(id) {
    setDeletingDonationId(id)
    const { error } = await supabase.from('donations').delete().eq('id', id)
    if (!error) {
      setDonations(prev => prev.filter(d => d.id !== id))
      logAction({ user, profile, action: 'delete_donation', entity: 'donations', details: { id, camp_id: selectedDashboardCampId } })
    } else {
      showToast(error.message, 'error')
    }
    setDeletingDonationId(null)
  }

  async function sendReminder(reg) {
    setSendingIds(prev => new Set([...prev, reg.id]))
    const { error } = await supabase.functions.invoke('send-reminder', { body: { registration_id: reg.id } })
    setSendingIds(prev => { const next = new Set(prev); next.delete(reg.id); return next })
    if (error) showToast(t('admin.toastReminderFailed', { name: reg.full_name }), 'error')
    else showToast(t('admin.toastReminderSent', { name: reg.full_name }), 'success')
  }

  async function sendBulkReminders() {
    setBulkSending(true)
    setConfirmBulk(false)
    const total = pendingRegs.length
    setBulkProgress({ sent: 0, total })
    let sent = 0
    for (const reg of pendingRegs) {
      await supabase.functions.invoke('send-reminder', { body: { registration_id: reg.id } })
      sent++
      setBulkProgress({ sent, total })
    }
    setBulkSending(false)
    logAction({ user, profile, action: 'send_bulk_reminders', entity: 'registrations', details: { count: sent, camp_id: selectedDashboardCampId } })
    showToast(`${sent} reminder${sent !== 1 ? 's' : ''} sent successfully.`, 'success')
  }

  async function loadUsers() {
    setUsersLoading(true)
    const { data, error } = await supabase.functions.invoke('get-users')
    setUsersLoading(false)
    if (error) { showToast(t('admin.toastFailedLoadUsers', { message: error.message }), 'error'); return }
    setUsers(data?.users ?? [])
  }

  async function sendUserReminder(userId, reminderType, extra = {}) {
    // Use registration_id as the Set key when present, so per-row state
    // is scoped to the specific registration rather than the user account.
    const key = (extra.registration_id ?? userId) + ':' + reminderType
    setUsersSending(prev => new Set([...prev, key]))
    const { error } = await supabase.functions.invoke('send-user-reminder', {
      body: { user_id: userId, reminder_type: reminderType, ...extra },
    })
    setUsersSending(prev => { const n = new Set(prev); n.delete(key); return n })
    if (error) showToast(t('admin.toastReminderFailedGeneric', { message: error.message }), 'error')
    else showToast(t('admin.toastReminderSentGeneric'), 'success')
  }

  async function sendBulkUserReminders(userList, reminderType) {
    const total = userList.length
    setBulkUserSending({ type: reminderType, sent: 0, total })
    let sent = 0
    for (const u of userList) {
      await supabase.functions.invoke('send-user-reminder', {
        body: { user_id: u.id, reminder_type: reminderType },
      })
      sent++
      setBulkUserSending({ type: reminderType, sent, total })
    }
    setBulkUserSending(null)
    showToast(`${sent} reminder${sent !== 1 ? 's' : ''} sent successfully.`, 'success')
  }

  // Bulk send for sections C & D — each row carries user_id + registration id
  async function sendBulkCancelledReminders(rows, reminderType) {
    const total = rows.length
    setBulkUserSending({ type: reminderType, sent: 0, total })
    let sent = 0
    for (const row of rows) {
      await supabase.functions.invoke('send-user-reminder', {
        body: { user_id: row.user_id, reminder_type: reminderType, registration_id: row.id },
      })
      sent++
      setBulkUserSending({ type: reminderType, sent, total })
    }
    setBulkUserSending(null)
    showToast(`${sent} reminder${sent !== 1 ? 's' : ''} sent successfully.`, 'success')
  }

  // ── Admin Users handlers (superadmin only) ───────────────────────────────

  async function loadAdminUsers() {
    setAdminUsersLoading(true)
    const { data, error } = await supabase.rpc('get_admin_users_with_email')
    setAdminUsersLoading(false)
    if (error) { showToast(t('admin.toastFailedLoadAdminUsers', { message: error.message }), 'error'); return }
    setAdminUsers(data ?? [])
  }

  async function handleGrantAccess() {
    if (!grantEmail.trim()) { setGrantError(t('admin.errorGrantEmail')); return }
    setGrantSaving(true)
    setGrantError('')

    const { data: userId, error: lookupErr } = await supabase.rpc('get_user_id_by_email', { lookup_email: grantEmail.trim() })
    if (lookupErr || !userId) {
      setGrantError(t('admin.errorGrantNoUser'))
      setGrantSaving(false)
      return
    }

    const existingAdmin = adminUsers.find(a => a.id === userId)
    if (existingAdmin?.role === 'superadmin') {
      setGrantError(t('admin.errorGrantSuperadmin'))
      setGrantSaving(false)
      return
    }

    const { error } = await supabase.rpc('set_user_role', { target_user: userId, new_role: grantRoleValue })
    if (error) { setGrantError(error.message); setGrantSaving(false); return }

    logAction({ user, profile, action: 'grant_admin_access', entity: 'profiles', entity_id: userId, details: { email: grantEmail.trim(), role: grantRoleValue } })

    const savedEmail = grantEmail.trim()
    setGrantEmail('')
    setGrantSaving(false)
    showToast(t('admin.toastGrantedAccess', { role: grantRoleValue, email: savedEmail }), 'success')
    loadAdminUsers()
  }

  async function handleRevokeAccess(adminUser) {
    if (adminUser.id === user?.id) {
      showToast(t('admin.toastCannotRevokeSelf'), 'error')
      return
    }
    if (adminUser.role === 'superadmin') {
      showToast(t('admin.toastCannotRevokeSuperadmin'), 'error')
      return
    }
    const { error } = await supabase.rpc('set_user_role', { target_user: adminUser.id, new_role: 'user' })
    if (error) { showToast(error.message, 'error'); return }

    logAction({ user, profile, action: 'revoke_admin_access', entity: 'profiles', entity_id: adminUser.id, details: { email: adminUser.email, previous_role: adminUser.role } })

    setAdminUsers(prev => prev.filter(u => u.id !== adminUser.id))
    showToast(t('admin.toastRevokedAccess', { email: adminUser.email }), 'success')
  }

  // ── Audit Log handlers (superadmin only) ────────────────────────────────

  async function loadAuditLogEntries() {
    setAuditLogLoading(true)
    const { data, error } = await supabase
      .from('audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(500)
    setAuditLogLoading(false)
    if (error) { showToast(t('admin.toastFailedLoadAuditLog', { message: error.message }), 'error'); return }
    setAuditLogEntries(data ?? [])
  }

  function exportAuditLogCSV() {
    const rows = [
      ['Date', 'User Email', 'Role', 'Action', 'Entity', 'Entity ID', 'Details'],
      ...filteredAuditLog.map(e => [
        new Date(e.created_at).toLocaleString(),
        e.user_email ?? '',
        e.user_role  ?? '',
        e.action     ?? '',
        e.entity     ?? '',
        e.entity_id  ?? '',
        e.details ? JSON.stringify(e.details) : '',
      ]),
    ]
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function handlePrint() {
    const content = document.getElementById('transport-printable')
    if (!content) return
    const win = window.open('', '_blank')
    if (!win) {
      // eslint-disable-next-line no-alert
      alert(t('admin.reportTransportPopupBlocked'))
      return
    }
    win.document.write(`
      <html>
        <head>
          <title>${t('admin.reportTransportListTitle', { campName: selectedDashboardCamp?.name ?? 'Camp' })}</title>
          <style>
            body { font-family: system-ui, sans-serif; padding: 24px; color: #111; }
            h2  { font-size: 1.25rem; font-weight: 700; margin: 0 0 4px; }
            p.sub { font-size: .875rem; color: #6b7280; margin: 0 0 20px; }
            table { width: 100%; border-collapse: collapse; font-size: .875rem; }
            th { text-align: left; padding: 8px 12px; font-weight: 600;
                 border-bottom: 2px solid #e5e7eb; background: #f9fafb; }
            td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
          </style>
        </head>
        <body>${content.innerHTML}</body>
      </html>
    `)
    win.document.close()
    win.print()
  }

  function handlePrintAttendees() {
    const win = window.open('', '_blank')
    if (!win) {
      // eslint-disable-next-line no-alert
      alert(t('admin.reportAttendeesPopupBlocked'))
      return
    }
    const esc = s => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    const title = t('admin.reportAttendeesListTitle', { campName: selectedDashboardCamp?.name ?? 'Camp' })
    const headers = attendeeColumnHeaders()
    const rows = buildAttendeeRows()
    const thead = headers.map(h => `<th>${esc(h)}</th>`).join('')
    const body = rows.map(row => `<tr>${[
      row.fullName, row.email, row.phone, row.age, row.sex, row.church,
      row.ageGroup, row.arrivalDate, row.transport, row.room, row.status,
    ].map(v => `<td>${esc(v)}</td>`).join('')}</tr>`).join('')
    win.document.write(`
      <html>
        <head>
          <title>${esc(title)}</title>
          <style>
            body { font-family: system-ui, sans-serif; padding: 24px; color: #111; }
            h2  { font-size: 1.25rem; font-weight: 700; margin: 0 0 4px; }
            p.sub { font-size: .875rem; color: #6b7280; margin: 0 0 20px; }
            table { width: 100%; border-collapse: collapse; font-size: .875rem; }
            th { text-align: left; padding: 8px 12px; font-weight: 600;
                 border-bottom: 2px solid #e5e7eb; background: #f9fafb; }
            td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
          </style>
        </head>
        <body>
          <h2>${esc(title)}</h2>
          <p class="sub">${filtered.length} of ${enriched.length}</p>
          <table><thead><tr>${thead}</tr></thead><tbody>${body}</tbody></table>
        </body>
      </html>
    `)
    win.document.close()
    win.print()
  }

  // ── Camp CRUD ────────────────────────────────────────────────────────────

  function openNewCampModal() {
    setCampForm({ name: '', description: '', location: '', start_date: '', end_date: '', payment_due_date: '', registration_open: false, transport_cost: '', packing_list: '', map_link: '', arrival_time: '', transport_meeting_point: '', transport_meeting_time: '', payment_bank_name: '', payment_account_number: '', payment_card_url: '', payment_in_person_note: '', payment_extra_info: '' })
    setCampModal({ open: true, mode: 'new', camp: null })
  }

  function openEditCampModal(c) {
    setCampForm({
      name: c.name,
      description: c.description ?? '',
      location: c.location ?? '',
      start_date: c.start_date ?? '',
      end_date: c.end_date ?? '',
      payment_due_date: c.payment_due_date ?? '',
      registration_open: c.registration_open,
      transport_cost: c.transport_cost != null ? String(c.transport_cost) : '',
      packing_list:            c.packing_list ?? '',
      map_link:                c.map_link ?? '',
      arrival_time:            c.arrival_time ?? '',
      transport_meeting_point: c.transport_meeting_point ?? '',
      transport_meeting_time:  c.transport_meeting_time ?? '',
      payment_bank_name:       c.payment_bank_name ?? '',
      payment_account_number:  c.payment_account_number ?? '',
      payment_card_url:        c.payment_card_url ?? '',
      payment_in_person_note:  c.payment_in_person_note ?? '',
      payment_extra_info:      c.payment_extra_info ?? '',
    })
    setCampModal({ open: true, mode: 'edit', camp: c })
  }

  async function saveCamp() {
    setCampSaving(true)
    const payload = {
      name:              campForm.name,
      description:       campForm.description || null,
      location:          campForm.location    || null,
      start_date:        campForm.start_date  || null,
      end_date:          campForm.end_date    || null,
      payment_due_date:  campForm.payment_due_date || null,
      registration_open: campForm.registration_open,
      transport_cost:    parseFloat(campForm.transport_cost) || 0,
      packing_list:            campForm.packing_list || null,
      map_link:                campForm.map_link || null,
      arrival_time:            campForm.arrival_time || null,
      transport_meeting_point: campForm.transport_meeting_point || null,
      transport_meeting_time:  campForm.transport_meeting_time || null,
      payment_bank_name:       campForm.payment_bank_name || null,
      payment_account_number:  campForm.payment_account_number || null,
      payment_card_url:        campForm.payment_card_url || null,
      payment_in_person_note:  campForm.payment_in_person_note || null,
      payment_extra_info:      campForm.payment_extra_info || null,
    }

    if (payload.registration_open) {
      const deactivateQuery = campModal.mode === 'edit'
        ? supabase.from('camps').update({ registration_open: false }).neq('id', campModal.camp.id)
        : supabase.from('camps').update({ registration_open: false }).eq('registration_open', true)
      await deactivateQuery
      setAllCamps(prev => prev.map(c =>
        campModal.mode === 'edit' && c.id === campModal.camp.id ? c : { ...c, registration_open: false },
      ))
    }

    let error
    if (campModal.mode === 'new') {
      const res = await supabase.from('camps').insert(payload).select().single()
      error = res.error
      if (!error) {
        setAllCamps(prev => [res.data, ...prev])
        if (payload.registration_open) setCamp(res.data)
      }
    } else {
      const res = await supabase.from('camps').update(payload).eq('id', campModal.camp.id).select().single()
      error = res.error
      if (!error) {
        const updated = res.data
        setAllCamps(prev => prev.map(c => c.id === campModal.camp.id ? updated : c))
        if (camp?.id === campModal.camp.id) setCamp(updated)
        else if (payload.registration_open) setCamp(updated)
      }
    }

    setCampSaving(false)
    if (!error) {
      logAction({ user, profile, action: campModal.mode === 'new' ? 'create_camp' : 'edit_camp', entity: 'camps', details: { name: campForm.name } })
      setCampModal({ open: false, mode: 'new', camp: null })
      showToast(t('admin.toastCampSaved'), 'success')
    } else {
      showToast(error.message, 'error')
    }
  }

  // ── Pricing Tiers ─────────────────────────────────────────────────────────

  async function openTierModal(c) {
    const { data: tiers } = await supabase
      .from('pricing_tiers').select('*')
      .eq('camp_id', c.id)
      .order('arrival_date', { ascending: true })
    setTierModal({ open: true, campId: c.id, campName: c.name, tiers: tiers ?? [] })
    setTierForm({ label: '', arrival_date: '', price: '' })
  }

  async function saveTier() {
    if (!tierForm.label || !tierForm.price) return
    setTierSaving(true)
    const { data, error } = await supabase
      .from('pricing_tiers')
      .insert({
        camp_id:      tierModal.campId,
        label:        tierForm.label,
        arrival_date: tierForm.arrival_date || null,
        price:        parseFloat(tierForm.price),
      })
      .select().single()
    setTierSaving(false)
    if (!error) {
      setTierModal(prev => ({ ...prev, tiers: [...prev.tiers, data] }))
      setTierForm({ label: '', arrival_date: '', price: '' })
    } else {
      showToast(error.message, 'error')
    }
  }

  async function deleteTier(tierId) {
    const { error } = await supabase.from('pricing_tiers').delete().eq('id', tierId)
    if (!error) setTierModal(prev => ({ ...prev, tiers: prev.tiers.filter(t => t.id !== tierId) }))
    else showToast(error.message, 'error')
  }

  async function setDefaultTier(tierId) {
    // Fix #9: record the previous default so we can restore it if the second
    // call fails, avoiding a state where no tier is marked as default.
    const previousDefaultId = tierModal.tiers.find(t => t.is_default)?.id ?? null

    const { error: clearErr } = await supabase
      .from('pricing_tiers')
      .update({ is_default: false })
      .eq('camp_id', tierModal.campId)
    if (clearErr) { showToast(clearErr.message, 'error'); return }

    const { error: setErr } = await supabase
      .from('pricing_tiers')
      .update({ is_default: true })
      .eq('id', tierId)
    if (setErr) {
      // Attempt to restore the previous default so the camp isn't left with none
      if (previousDefaultId) {
        await supabase.from('pricing_tiers').update({ is_default: true }).eq('id', previousDefaultId)
      }
      showToast(setErr.message, 'error')
      return
    }

    setTierModal(prev => ({
      ...prev,
      tiers: prev.tiers.map(t => ({ ...t, is_default: t.id === tierId })),
    }))
    showToast(t('admin.toastDefaultTierUpdated'), 'success')
  }

  // ── Room CRUD ─────────────────────────────────────────────────────────────

  function openNewRoomModal() {
    setRoomForm({ name: '', capacity: '', sex: 'Male', min_age: '', max_age: '', photo_url: '' })
    setRoomPhotoFile(null)
    setRoomModal({ open: true, mode: 'new', room: null })
  }

  function openEditRoomModal(r) {
    setRoomForm({ name: r.name, capacity: r.capacity, sex: r.sex, min_age: r.min_age ?? '', max_age: r.max_age ?? '', photo_url: r.photo_url ?? '' })
    setRoomPhotoFile(null)
    setRoomModal({ open: true, mode: 'edit', room: r })
  }

  async function saveRoom() {
    if (!selectedDashboardCampId) return
    setRoomSaving(true)

    let photoUrl = roomForm.photo_url || null
    if (roomPhotoFile) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024
      if (roomPhotoFile.size > MAX_FILE_SIZE) {
        showToast(t('admin.roomPhotoTooLarge'), 'error')
        setRoomSaving(false)
        return
      }
      const ext = roomPhotoFile.name.split('.').pop()
      const path = `${selectedDashboardCampId}/${Date.now()}.${ext}`
      const { error: uploadError } = await supabase.storage
        .from('room-photos')
        .upload(path, roomPhotoFile, { upsert: false })
      if (uploadError) {
        showToast(uploadError.message, 'error')
        setRoomSaving(false)
        return
      }
      const { data: urlData } = supabase.storage.from('room-photos').getPublicUrl(path)
      photoUrl = urlData.publicUrl
    }

    const payload = {
      camp_id:   selectedDashboardCampId,
      name:      roomForm.name,
      capacity:  parseInt(roomForm.capacity),
      sex:       roomForm.sex,
      min_age:   roomForm.min_age ? parseInt(roomForm.min_age) : null,
      max_age:   roomForm.max_age ? parseInt(roomForm.max_age) : null,
      photo_url: photoUrl,
    }
    let error
    if (roomModal.mode === 'new') {
      const res = await supabase.from('rooms').insert(payload).select().single()
      error = res.error
      if (!error) setRooms(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)))
    } else {
      const res = await supabase.from('rooms').update(payload).eq('id', roomModal.room.id).select().single()
      error = res.error
      if (!error) setRooms(prev => prev.map(r => r.id === roomModal.room.id ? res.data : r))
    }
    setRoomSaving(false)
    if (!error) {
      setRoomModal({ open: false, mode: 'new', room: null })
      showToast(t('admin.toastRoomSaved'), 'success')
    } else {
      showToast(error.message, 'error')
    }
  }

  function isRoomEligible(reg) {
    return reg.payment_status === 'Paid'
      || reg.payment_status === 'Sponsored'
      || roomAgreementRegIds.has(reg.id)
  }

  async function autoAssign() {
    if (!selectedDashboardCampId) return
    setAutoAssigning(true)

    const enrichedRoomRegs = roomCampRegs.map(r => ({ ...r, age: getAge(r.date_of_birth) }))
    const assignedIds = new Set(roomAssignments.map(a => a.registration_id))
    const eligible = enrichedRoomRegs.filter(r =>
      isRoomEligible(r) && !assignedIds.has(r.id),
    )

    const newAssignments = []

    for (const room of rooms) {
      const currentCount = roomAssignments.filter(a => a.room_id === room.id).length
      const pendingCount = newAssignments.filter(a => a.room_id === room.id).length
      const available = room.capacity - currentCount - pendingCount

      if (available <= 0) continue

      const alreadyQueued = new Set(newAssignments.map(a => a.registration_id))
      const roomEligible = eligible.filter(r => {
        if (alreadyQueued.has(r.id)) return false
        if (room.sex !== 'Mixed' && r.sex !== room.sex) return false
        if (room.min_age !== null && r.age !== null && r.age < room.min_age) return false
        if (room.max_age !== null && r.age !== null && r.age > room.max_age) return false
        return true
      })

      roomEligible.slice(0, available).forEach(r =>
        newAssignments.push({ room_id: room.id, registration_id: r.id, camp_id: selectedDashboardCampId }),
      )
    }

    if (newAssignments.length === 0) {
      showToast(t('admin.toastNoEligibleAttendees'), 'neutral')
      setAutoAssigning(false)
      return
    }

    const { error } = await supabase.from('room_assignments').insert(newAssignments)

    if (error) {
      showToast(error.message, 'error')
    } else {
      await loadRoomTabData(selectedDashboardCampId)
      logAction({ user, profile, action: 'auto_assign_rooms', entity: 'room_assignments', details: { count: newAssignments.length, camp_id: selectedDashboardCampId } })
      showToast(t('admin.toastAutoAssignComplete', { count: newAssignments.length }), 'success')
    }

    setAutoAssigning(false)
  }

  async function removeAssignment(assignmentId) {
    const { error } = await supabase.from('room_assignments').delete().eq('id', assignmentId)
    if (!error) setRoomAssignments(prev => prev.filter(a => a.id !== assignmentId))
    else showToast(error.message, 'error')
  }

  async function addAssignment(registrationId) {
    if (!manageRoom || !selectedDashboardCampId) return
    const reg = roomCampRegs.find(r => r.id === registrationId)
    if (reg && !isRoomEligible(reg)) {
      showToast(t('admin.notRoomEligible'), 'error')
      return
    }
    const { data, error } = await supabase
      .from('room_assignments')
      .insert({ room_id: manageRoom.id, registration_id: registrationId, camp_id: selectedDashboardCampId })
      .select('*, registrations(full_name, sex, date_of_birth)')
      .single()
    if (!error) {
      setRoomAssignments(prev => [...prev, data])
      setAssignSearch('')
    } else {
      showToast(error.message, 'error')
    }
  }

  /* ── Loading ─────────────────────────────────────────────────────────────── */

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  /* ── Main render ─────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-6">

      {/* Page header */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-secondary">Oasis Joven</p>
        <div className="mt-1 flex items-center gap-3 flex-wrap">
          <h1 className="font-display text-3xl font-semibold tracking-tight text-on-surface">{t('admin.title')}</h1>
        {profile?.role && profile.role !== 'user' && (
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
            profile.role === 'superadmin'    ? 'bg-purple-100 text-purple-800 border-purple-200' :
            profile.role === 'admin'         ? 'bg-oasis-100 text-oasis-800 border-oasis-200' :
            profile.role === 'readonly_admin'? 'bg-surface-container text-on-surface-variant border-outline-variant/60' :
            profile.role === 'staff'         ? 'bg-teal-100 text-teal-800 border-teal-200' :
            'bg-surface-container text-on-surface-variant border-outline-variant/60'
          }`}>
            {profile.role.replace('_', ' ')}
          </span>
        )}
        </div>
      </div>

      {/* Global camp selector — controls Overview, Attendees, Transport, Payments, Finances */}
      <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-4">
        <label className="block text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-2">
          {t('admin.viewingCamp')}
        </label>
        {allCamps.length === 0 ? (
          <p className="text-sm text-amber-600">{t('admin.noCampsYet')}</p>
        ) : (
          <>
            <select
              data-testid="camp-selector"
              value={selectedDashboardCampId ?? ''}
              onChange={e => handleDashboardCampChange(e.target.value)}
              className="w-full rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2.5 text-sm font-medium text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
            >
              {allCamps.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.registration_open ? '  ✓ Open' : ''}
                </option>
              ))}
            </select>
            {selectedDashboardCamp && (selectedDashboardCamp.location || selectedDashboardCamp.start_date) && (
              <p className="mt-2 text-sm text-on-surface-variant">
                {selectedDashboardCamp.location && <span>{selectedDashboardCamp.location}</span>}
                {selectedDashboardCamp.location && selectedDashboardCamp.start_date && <span> · </span>}
                {selectedDashboardCamp.start_date && selectedDashboardCamp.end_date && (
                  <span>{formatDate(selectedDashboardCamp.start_date)} – {formatDate(selectedDashboardCamp.end_date)}</span>
                )}
              </p>
            )}
          </>
        )}
      </div>

      {/* Sidebar rail + tab content */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-6">

      {/* Tab rail — horizontal wrap on mobile, sticky vertical rail on desktop */}
      <nav className="flex flex-wrap gap-1.5 rounded-2xl border border-outline-variant/60 bg-surface-container-lowest p-1.5 w-fit lg:w-56 lg:shrink-0 lg:flex-col lg:flex-nowrap lg:sticky lg:top-6">
        {visibleTabs.map(tab => (
          <button
            key={tab}
            data-testid={`tab-${tab}`}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium transition-colors lg:w-full lg:text-left ${
              activeTab === tab
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
            }`}
          >
            {TAB_ICONS[tab] && (
              <span className="material-symbols-outlined text-[18px] leading-none" aria-hidden="true">
                {TAB_ICONS[tab]}
              </span>
            )}
            {tabLabel(tab)}
          </button>
        ))}
      </nav>

      {/* Active tab content */}
      <div className="flex-1 min-w-0 space-y-6">

      {/* ── OVERVIEW ───────────────────────────────────────────────────────── */}
      {activeTab === 'Overview' && (
        <div className="space-y-6">
          {!selectedDashboardCampId ? (
            <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-12 text-center">
              <span className="text-4xl">🏕️</span>
              <p className="mt-3 text-sm text-on-surface-variant">{t('admin.createCampPrompt')}</p>
            </div>
          ) : (
            <>
              {/* Row 1 — headcounts */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard label={t('admin.statTotalRegistered')} value={stats.total}
                  colorClass="bg-primary border-transparent text-on-primary" />
                <StatCard label={t('admin.statTotalPaid')} value={stats.totalPaid}
                  colorClass="bg-green-50 border-green-200 text-green-800" />
                <StatCard label={t('admin.statSponsored')} value={stats.totalSponsored}
                  colorClass="bg-purple-50 border-purple-200 text-purple-800" />
                <StatCard label={t('admin.statTotalWomen')} value={stats.totalWomen}
                  colorClass="bg-secondary-container border-outline-variant/60 text-on-secondary-container" />
                <StatCard label={t('admin.statTotalMen')} value={stats.totalMen}
                  colorClass="bg-primary-container border-outline-variant/60 text-on-primary-container" />
              </div>

              {/* Row 2 — financials + transport */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard label={t('admin.statAmountCollected')} value={formatCurrency(stats.collected)}
                  colorClass="bg-green-50 border-green-200 text-green-800" />
                <StatCard label={t('admin.statAmountOutstanding')} value={formatCurrency(stats.outstanding)}
                  colorClass="bg-amber-50 border-amber-200 text-amber-800" />
                <StatCard label={t('admin.statNeedTransport')} value={stats.needTransportCount}
                  colorClass="bg-surface-container-low border-outline-variant/60 text-on-surface" />
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Bar chart — age group × sex */}
                <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-6">
                  <h3 className="text-sm font-semibold text-on-surface-variant mb-5">
                    {t('admin.chartRegsByAgeGroup')}
                  </h3>
                  {barChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={barChartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e9dfc8" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="Male"   fill="#43543f" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Female" fill="#c98c58" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-sm text-on-surface-variant/70 text-center leading-relaxed">
                      {t('admin.chartNoAgeGroups')}<br />
                      Add rows to <code className="bg-surface-container px-1 rounded">age_group_config</code>.
                    </div>
                  )}
                </div>

                {/* Bar chart — payment status by arrival date */}
                <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-6">
                  <h3 className="text-sm font-semibold text-on-surface-variant mb-5">
                    {t('admin.chartPaymentByArrival')}
                  </h3>
                  {paymentByArrival.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={paymentByArrival} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e9dfc8" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="Paid"      fill="#43543f" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Sponsored" fill="#c98c58" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Unpaid"    fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-sm text-on-surface-variant/70">
                      {t('admin.chartNoRegistrations')}
                    </div>
                  )}
                </div>

                {/* Pie chart — transport */}
                <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-6">
                  <h3 className="text-sm font-semibold text-on-surface-variant mb-5">{t('admin.chartTransportBreakdown')}</h3>
                  {enriched.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={transportPieData}
                          cx="50%" cy="50%"
                          outerRadius={100}
                          dataKey="value"
                        >
                          {transportPieData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, name) => [`${v} people`, name]} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-sm text-on-surface-variant/70">
                      {t('admin.chartNoRegistrations')}
                    </div>
                  )}
                </div>

                {/* Bar chart — registrations by church */}
                <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-6">
                  <h3 className="text-sm font-semibold text-on-surface-variant mb-5">
                    {t('admin.chartRegsByChurch')}
                  </h3>
                  {churchChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart
                        data={churchChartData}
                        layout="vertical"
                        margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e9dfc8" />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={v => [`${v}`, t('admin.chartRegsByChurchTooltip')]} />
                        <Bar dataKey="count" fill="#43543f" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-64 text-sm text-on-surface-variant/70">
                      {t('admin.chartNoRegistrations')}
                    </div>
                  )}
                </div>

              </div>
            </>
          )}
        </div>
      )}

      {/* ── USERS ──────────────────────────────────────────────────────────── */}
      {activeTab === 'Users' && (() => {
        const unconfirmed    = users.filter(u => !u.has_confirmed_email && !u.is_admin)
        const noReg          = users.filter(u => u.has_confirmed_email && !u.has_any_registration && !u.is_admin)
        const selfCancelled  = users.flatMap(u =>
          (u.self_cancelled_registrations ?? []).map(r => ({ ...r, user_id: u.id, user_email: u.email }))
        )
        const adminCancelled = users.flatMap(u =>
          (u.admin_cancelled_registrations ?? []).map(r => ({ ...r, user_id: u.id, user_email: u.email }))
        )
        return (
          <div className="space-y-6">

            {/* Header row with refresh */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('admin.userMgmtTitle')}</h2>
                <p className="text-sm text-on-surface-variant">{users.length} total account{users.length !== 1 ? 's' : ''}</p>
              </div>
              <button
                onClick={loadUsers}
                disabled={usersLoading}
                className="flex items-center gap-2 rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-50 transition"
              >
                {usersLoading ? (
                  <span className="w-4 h-4 border-2 border-outline border-t-transparent rounded-full animate-spin" />
                ) : (
                  <span>↻</span>
                )}
                {t('admin.refresh')}
              </button>
            </div>

            {usersLoading ? (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Section A — Pending Email Confirmation */}
                <div className="bg-surface-container-lowest rounded-2xl border border-amber-100 overflow-hidden">
                  <div className="bg-amber-50 border-b border-amber-100 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="text-sm font-bold text-amber-900">
                        {t('admin.pendingEmailTitle')}
                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-900">
                          {unconfirmed.length}
                        </span>
                      </h3>
                      <p className="text-xs text-amber-700 mt-0.5">{t('admin.pendingEmailDesc')}</p>
                    </div>
                    {unconfirmed.length > 0 && (
                      <button
                        onClick={() => sendBulkUserReminders(unconfirmed, 'confirm_email')}
                        disabled={bulkUserSending !== null}
                        className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-50 transition"
                      >
                        {bulkUserSending?.type === 'confirm_email'
                          ? t('admin.sendingProgress', { sent: bulkUserSending.sent, total: bulkUserSending.total })
                          : t('admin.sendAllReminders', { count: unconfirmed.length })}
                      </button>
                    )}
                  </div>

                  {unconfirmed.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-on-surface-variant/70">
                      {t('admin.allEmailsConfirmed')}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-surface-container-low border-b border-outline-variant/50">
                          <tr>
                            <TH>{t('admin.colEmail')}</TH>
                            <TH>{t('admin.colSignedUp')}</TH>
                            <TH>{t('admin.colWaiting')}</TH>
                            <TH></TH>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-outline-variant/30">
                          {unconfirmed.map(u => {
                            const key = u.id + ':confirm_email'
                            const isSending = usersSending.has(key)
                            const days = daysSince(u.created_at)
                            return (
                              <tr key={u.id} className="hover:bg-surface-container-low">
                                <td className="px-4 py-3 font-medium text-on-surface">{u.email}</td>
                                <td className="px-4 py-3 text-on-surface-variant">{formatDate(u.created_at?.slice(0, 10))}</td>
                                <td className="px-4 py-3 text-on-surface-variant">
                                  {days !== null ? `${days} day${days !== 1 ? 's' : ''}` : '—'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => sendUserReminder(u.id, 'confirm_email')}
                                    disabled={isSending || bulkUserSending !== null}
                                    className="rounded-lg bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-200 disabled:opacity-50 transition"
                                  >
                                    {isSending ? t('admin.sendingBtn') : t('admin.sendReminders')}
                                  </button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Section B — Confirmed but No Camp Registration */}
                <div className="bg-surface-container-lowest rounded-2xl border border-oasis-100 overflow-hidden">
                  <div className="bg-oasis-50 border-b border-oasis-100 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="text-sm font-bold text-oasis-900">
                        {t('admin.noRegTitle')}
                        <span className="ml-2 inline-flex items-center rounded-full bg-oasis-200 px-2 py-0.5 text-xs font-semibold text-oasis-900">
                          {noReg.length}
                        </span>
                      </h3>
                      <p className="text-xs text-oasis-700 mt-0.5">{t('admin.noRegDesc')}</p>
                    </div>
                    {noReg.length > 0 && (
                      <button
                        onClick={() => sendBulkUserReminders(noReg, 'register_camp')}
                        disabled={bulkUserSending !== null}
                        className="rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white hover:bg-oasis-800 disabled:opacity-50 transition"
                      >
                        {bulkUserSending?.type === 'register_camp'
                          ? t('admin.sendingProgress', { sent: bulkUserSending.sent, total: bulkUserSending.total })
                          : t('admin.sendAllReminders', { count: noReg.length })}
                      </button>
                    )}
                  </div>

                  {noReg.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-on-surface-variant/70">
                      {t('admin.allUsersHaveReg')}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-surface-container-low border-b border-outline-variant/50">
                          <tr>
                            <TH>{t('admin.colEmail')}</TH>
                            <TH>{t('admin.colAccountCreated')}</TH>
                            <TH>{t('admin.colWaiting')}</TH>
                            <TH></TH>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-outline-variant/30">
                          {noReg.map(u => {
                            const key = u.id + ':register_camp'
                            const isSending = usersSending.has(key)
                            const days = daysSince(u.created_at)
                            return (
                              <tr key={u.id} className="hover:bg-surface-container-low">
                                <td className="px-4 py-3 font-medium text-on-surface">{u.email}</td>
                                <td className="px-4 py-3 text-on-surface-variant">{formatDate(u.created_at?.slice(0, 10))}</td>
                                <td className="px-4 py-3 text-on-surface-variant">
                                  {days !== null ? `${days} day${days !== 1 ? 's' : ''}` : '—'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => sendUserReminder(u.id, 'register_camp')}
                                    disabled={isSending || bulkUserSending !== null}
                                    className="rounded-lg bg-oasis-100 px-3 py-1.5 text-xs font-semibold text-oasis-800 hover:bg-oasis-200 disabled:opacity-50 transition"
                                  >
                                    {isSending ? t('admin.sendingBtn') : t('admin.sendReminders')}
                                  </button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Section C — Self Unregistered */}
                <div className="bg-surface-container-lowest rounded-2xl border border-orange-100 overflow-hidden">
                  <div className="bg-orange-50 border-b border-orange-100 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="text-sm font-bold text-orange-900">
                        {t('admin.selfUnregTitle')}
                        <span className="ml-2 inline-flex items-center rounded-full bg-orange-200 px-2 py-0.5 text-xs font-semibold text-orange-900">
                          {selfCancelled.length}
                        </span>
                      </h3>
                      <p className="text-xs text-orange-700 mt-0.5">{t('admin.selfUnregDesc')}</p>
                    </div>
                    {selfCancelled.length > 0 && (
                      <button
                        onClick={() => sendBulkCancelledReminders(selfCancelled, 're_register')}
                        disabled={bulkUserSending !== null}
                        className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-semibold text-white hover:bg-orange-600 disabled:opacity-50 transition"
                      >
                        {bulkUserSending?.type === 're_register'
                          ? t('admin.sendingProgress', { sent: bulkUserSending.sent, total: bulkUserSending.total })
                          : t('admin.sendAllReRegReminders', { count: selfCancelled.length })}
                      </button>
                    )}
                  </div>

                  {selfCancelled.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-on-surface-variant/70">
                      {t('admin.selfUnregEmpty')}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-surface-container-low border-b border-outline-variant/50">
                          <tr>
                            <TH>{t('admin.colFullName')}</TH>
                            <TH>{t('admin.colEmail')}</TH>
                            <TH>{t('admin.colCamp')}</TH>
                            <TH>{t('admin.colRegistered')}</TH>
                            <TH>{t('admin.colUnregistered')}</TH>
                            <TH>{t('admin.colAmountPaid')}</TH>
                            <TH></TH>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-outline-variant/30">
                          {selfCancelled.map(row => {
                            const key = row.id + ':re_register'
                            const isSending = usersSending.has(key)
                            return (
                              <tr key={row.id} className="hover:bg-surface-container-low">
                                <td className="px-4 py-3 font-medium text-on-surface">{row.full_name ?? '—'}</td>
                                <td className="px-4 py-3 text-on-surface-variant">{row.user_email}</td>
                                <td className="px-4 py-3 text-on-surface-variant">{row.camp_name ?? '—'}</td>
                                <td className="px-4 py-3 text-on-surface-variant">{formatDate(row.created_at?.slice(0, 10))}</td>
                                <td className="px-4 py-3 text-on-surface-variant">
                                  {row.cancelled_at ? formatDate(row.cancelled_at.slice(0, 10)) : '—'}
                                </td>
                                <td className="px-4 py-3 text-on-surface-variant">{formatCurrency(row.amount_paid)}</td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => sendUserReminder(row.user_id, 're_register', { registration_id: row.id })}
                                    disabled={isSending || bulkUserSending !== null}
                                    className="rounded-lg bg-orange-100 px-3 py-1.5 text-xs font-semibold text-orange-800 hover:bg-orange-200 disabled:opacity-50 transition"
                                  >
                                    {isSending ? t('admin.sendingBtn') : t('admin.sendReRegReminder')}
                                  </button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Section D — Cancelled by Admin */}
                <div className="bg-surface-container-lowest rounded-2xl border border-red-100 overflow-hidden">
                  <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="text-sm font-bold text-red-900">
                        {t('admin.adminCancelledTitle')}
                        <span className="ml-2 inline-flex items-center rounded-full bg-red-200 px-2 py-0.5 text-xs font-semibold text-red-900">
                          {adminCancelled.length}
                        </span>
                      </h3>
                      <p className="text-xs text-red-700 mt-0.5">{t('admin.adminCancelledDesc')}</p>
                    </div>
                    {adminCancelled.length > 0 && (
                      <button
                        onClick={() => sendBulkCancelledReminders(adminCancelled, 'admin_cancelled')}
                        disabled={bulkUserSending !== null}
                        className="rounded-xl bg-tertiary px-4 py-2 text-xs font-semibold text-white hover:bg-[#7c3f26] disabled:opacity-50 transition"
                      >
                        {bulkUserSending?.type === 'admin_cancelled'
                          ? t('admin.sendingProgress', { sent: bulkUserSending.sent, total: bulkUserSending.total })
                          : t('admin.sendAllNotifications', { count: adminCancelled.length })}
                      </button>
                    )}
                  </div>

                  {adminCancelled.length === 0 ? (
                    <div className="px-6 py-10 text-center text-sm text-on-surface-variant/70">
                      {t('admin.adminCancelledEmpty')}
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-surface-container-low border-b border-outline-variant/50">
                          <tr>
                            <TH>{t('admin.colFullName')}</TH>
                            <TH>{t('admin.colEmail')}</TH>
                            <TH>{t('admin.colCamp')}</TH>
                            <TH>{t('admin.colRegistered')}</TH>
                            <TH>{t('admin.colCancelled')}</TH>
                            <TH>{t('admin.colAmountPaid')}</TH>
                            <TH>{t('admin.colRefund')}</TH>
                            <TH></TH>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-outline-variant/30">
                          {adminCancelled.map(row => {
                            const key = row.id + ':admin_cancelled'
                            const isSending = usersSending.has(key)
                            return (
                              <tr key={row.id} className="hover:bg-surface-container-low">
                                <td className="px-4 py-3 font-medium text-on-surface">{row.full_name ?? '—'}</td>
                                <td className="px-4 py-3 text-on-surface-variant">{row.user_email}</td>
                                <td className="px-4 py-3 text-on-surface-variant">{row.camp_name ?? '—'}</td>
                                <td className="px-4 py-3 text-on-surface-variant">{formatDate(row.created_at?.slice(0, 10))}</td>
                                <td className="px-4 py-3 text-on-surface-variant">
                                  {row.cancelled_at ? formatDate(row.cancelled_at.slice(0, 10)) : '—'}
                                </td>
                                <td className="px-4 py-3 text-on-surface-variant">{formatCurrency(row.amount_paid)}</td>
                                <td className="px-4 py-3 text-on-surface-variant">
                                  {row.refund_amount ? formatCurrency(row.refund_amount) : '—'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => sendUserReminder(row.user_id, 'admin_cancelled', { registration_id: row.id })}
                                    disabled={isSending || bulkUserSending !== null}
                                    className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-800 hover:bg-red-200 disabled:opacity-50 transition"
                                  >
                                    {isSending ? t('admin.sendingBtn') : t('admin.sendNotification')}
                                  </button>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

              </>
            )}
          </div>
        )
      })()}

      {/* ── ATTENDEES ──────────────────────────────────────────────────────── */}
      {activeTab === 'Attendees' && (
        <div className="space-y-4">

          {/* Filter bar */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-44">
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.filterSearchName')}</label>
              <input
                type="text" placeholder={t('admin.filterSearchPlaceholder')}
                value={search} onChange={e => setSearch(e.target.value)}
                className={SELECT_CLS + ' w-full'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.filterSex')}</label>
              <select value={filterSex} onChange={e => setFilterSex(e.target.value)} className={SELECT_CLS}>
                <option value="All">{t('admin.filterAll')}</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.filterAgeGroup')}</label>
              <select value={filterAgeGroup} onChange={e => setFilterAgeGroup(e.target.value)} className={SELECT_CLS}>
                <option value="All">{t('admin.filterAllGroups')}</option>
                {ageGroups.map(g => <option key={g.id} value={g.label}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.filterStatus')}</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={SELECT_CLS}>
                <option value="All">{t('admin.filterAll')}</option>
                <option value="Pending">Pending</option>
                <option value="Partial Payment">Partial Payment</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Paid">Paid</option>
                <option value="Sponsored">Sponsored</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.filterChurch')}</label>
              <select value={filterChurch} onChange={e => setFilterChurch(e.target.value)} className={SELECT_CLS}>
                <option value="All">{t('admin.filterAllChurches')}</option>
                {churchOptions.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <p className="text-xs text-on-surface-variant/70 pb-2">{filtered.length} of {enriched.length}</p>
          </div>

          {/* Export / Print header row */}
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-on-surface-variant">
              {filtered.length} of {enriched.length}
            </p>
            <div className="flex-1" />
            <button
              onClick={exportAttendeesCSV}
              className="flex items-center gap-2 rounded-xl border border-oasis-300 bg-surface-container-lowest px-5 py-2.5 text-sm font-medium text-oasis-700 hover:bg-oasis-50 transition shadow-sm"
            >
              {t('admin.exportCsvBtn')}
            </button>
            <button
              onClick={handlePrintAttendees}
              className="flex items-center gap-2 rounded-xl border border-oasis-300 bg-surface-container-lowest px-5 py-2.5 text-sm font-medium text-oasis-700 hover:bg-oasis-50 transition shadow-sm"
            >
              {t('admin.printBtn')}
            </button>
          </div>

          {/* Table */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-outline-variant/50 bg-surface-container-low">
                    <TH>{t('admin.colFullName')}</TH>
                    <TH>{t('admin.colEmail')}</TH>
                    <TH>{t('admin.colPhone')}</TH>
                    <TH>{t('admin.colAge')}</TH>
                    <TH>{t('admin.colSex')}</TH>
                    <TH>{t('admin.colChurch')}</TH>
                    <TH>{t('admin.colAgeGroup')}</TH>
                    <TH>{t('admin.colArrivalDate')}</TH>
                    <TH>{t('admin.colTransport')}</TH>
                    <TH>{t('admin.colRoom')}</TH>
                    <TH>{t('admin.colStatus')}</TH>
                    <TH></TH>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/30">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-4 py-12 text-center text-sm text-on-surface-variant/70">
                        {t('admin.noResultsFound')}
                      </td>
                    </tr>
                  ) : filtered.map(r => {
                    const assignment = assignmentMap[r.id]
                    const roomName   = assignment
                      ? (dashboardRooms.find(rm => rm.id === assignment.room_id)?.name ?? '—')
                      : null
                    return (
                      <tr key={r.id} className="hover:bg-oasis-50/30 transition">
                        <td className="px-4 py-3 font-medium text-on-surface whitespace-nowrap">{r.full_name}</td>
                        <td className="px-4 py-3 text-on-surface-variant whitespace-nowrap">{userEmailMap[r.user_id] ?? '—'}</td>
                        <td className="px-4 py-3 text-on-surface-variant whitespace-nowrap">
                          {toWhatsAppNumber(r.phone)
                            ? <a
                                href={`https://wa.me/${toWhatsAppNumber(r.phone)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-oasis-700 hover:text-oasis-800 hover:underline transition"
                              >
                                {r.phone}
                              </a>
                            : <span className="text-on-surface-variant/70">—</span>
                          }
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant">{r.age ?? '—'}</td>
                        <td className="px-4 py-3 text-on-surface-variant">{r.sex}</td>
                        <td className="px-4 py-3 text-on-surface-variant">{r.church?.trim() || '—'}</td>
                        <td className="px-4 py-3 text-on-surface-variant">{r.ageGroup?.label ?? '—'}</td>
                        <td className="px-4 py-3 text-on-surface-variant whitespace-nowrap">
                          {formatDate(r.pricing_tiers?.arrival_date ?? r.arrival_date)}
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant">{r.needs_transport ? '✓ Yes' : 'No'}</td>
                        <td className="px-4 py-3 text-xs">
                          {roomName
                            ? <span className="font-medium text-on-surface-variant">{roomName}</span>
                            : <span className="text-on-surface-variant/70">{t('admin.unassigned')}</span>
                          }
                          {agreementByReg[r.id] && r.payment_status !== 'Paid' && r.payment_status !== 'Sponsored' && (
                            <span className="ml-2 text-[0.65rem] px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                              {t('admin.agreementBadge')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={r.payment_status} /></td>
                        <td className="px-4 py-3">
                          {canEdit() && (
                            <button
                              onClick={() => openEditModal(r)}
                              className="rounded-lg border border-oasis-200 px-3 py-1 text-xs font-medium text-oasis-700 hover:bg-oasis-50 transition"
                            >
                              {t('admin.editBtn')}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TRANSPORT ──────────────────────────────────────────────────────── */}
      {activeTab === 'Transport' && (
        <div className="space-y-4">

          <div className="flex items-center gap-4 flex-wrap">
            <StatCard
              label={t('admin.statNeedingTransport')} value={needTransport.length}
              colorClass="bg-oasis-50 border-oasis-100 text-oasis-700"
            />
            <div className="flex-1" />
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 rounded-xl border border-oasis-300 bg-surface-container-lowest px-5 py-2.5 text-sm font-medium text-oasis-700 hover:bg-oasis-50 transition shadow-sm"
            >
              {t('admin.printExportBtn')}
            </button>
          </div>

          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
            <div id="transport-printable">
              <div className="px-6 py-4 border-b border-outline-variant/50">
                <h2 className="text-base font-semibold text-on-surface">Transport List — {selectedDashboardCamp?.name}</h2>
                <p className="text-sm text-on-surface-variant">
                  {needTransport.length} attendee{needTransport.length !== 1 ? 's' : ''} require transport
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-outline-variant/50 bg-surface-container-low">
                      <TH>#</TH><TH>{t('admin.colFullName')}</TH><TH>{t('admin.colPhone')}</TH>
                      <TH>{t('admin.colAge')}</TH><TH>{t('admin.colSex')}</TH><TH>{t('admin.colArrivalDate')}</TH>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/30">
                    {needTransport.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12 text-center text-sm text-on-surface-variant/70">
                          {t('admin.noTransportNeeded')}
                        </td>
                      </tr>
                    ) : needTransport.map((r, i) => (
                      <tr key={r.id}>
                        <td className="px-4 py-3 text-on-surface-variant/70">{i + 1}</td>
                        <td className="px-4 py-3 font-medium text-on-surface">{r.full_name}</td>
                        <td className="px-4 py-3 text-on-surface-variant">{r.phone}</td>
                        <td className="px-4 py-3 text-on-surface-variant">{r.age ?? '—'}</td>
                        <td className="px-4 py-3 text-on-surface-variant">{r.sex}</td>
                        <td className="px-4 py-3 text-on-surface-variant whitespace-nowrap">
                          {formatDate(r.pricing_tiers?.arrival_date ?? r.arrival_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PAYMENTS ───────────────────────────────────────────────────────── */}
      {activeTab === 'Payments' && (
        <div className="space-y-4">

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard label={t('admin.statTotalCollected')}    value={formatCurrency(stats.collected)}
              colorClass="bg-green-50 border-green-100 text-green-700" />
            <StatCard label={t('admin.statReceiptsInReview')} value={formatCurrency(financeData.totalConfirmed)}
              colorClass="bg-amber-50 border-amber-100 text-amber-700" />
            <StatCard label={t('admin.statTotalOutstanding')} value={formatCurrency(stats.outstanding)}
              colorClass="bg-rose-50 border-rose-100 text-rose-700" />
          </div>

          {/* Bulk reminder bar */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-4 flex items-center gap-4">
            {bulkSending ? (
              <>
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                <span className="text-sm text-on-surface-variant flex-shrink-0">
                  Sending… {bulkProgress.sent} / {bulkProgress.total}
                </span>
                <div className="flex-1 bg-surface-container rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${bulkProgress.total ? (bulkProgress.sent / bulkProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-on-surface-variant flex-1">
                  {pendingRegs.length} registration{pendingRegs.length !== 1 ? 's' : ''} with outstanding balance
                </p>
                {canEdit() && (
                  <button
                    onClick={() => setConfirmBulk(true)}
                    disabled={pendingRegs.length === 0}
                    className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    {t('admin.sendRemindersToAll')}
                  </button>
                )}
              </>
            )}
          </div>

          {/* Payments filter bar */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-44">
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.filterSearchName')}</label>
              <input
                type="text" placeholder={t('admin.filterSearchPlaceholder')}
                value={paymentSearch} onChange={e => setPaymentSearch(e.target.value)}
                className={SELECT_CLS + ' w-full'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.filterStatus')}</label>
              <select value={paymentStatusFilter} onChange={e => setPaymentStatusFilter(e.target.value)} className={SELECT_CLS}>
                <option value="All">{t('admin.filterAll')}</option>
                <option value="Pending">Pending</option>
                <option value="Partial Payment">Partial Payment</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Paid">Paid</option>
                <option value="Sponsored">Sponsored</option>
                {/* Fix #7: no Cancelled option — enriched excludes cancelled rows */}
              </select>
            </div>
            <p className="text-xs text-on-surface-variant/70 pb-2">
              {(() => {
                const count = enriched.filter(r => {
                  if (paymentStatusFilter !== 'All' && r.payment_status !== paymentStatusFilter) return false
                  if (paymentSearch.trim() && !r.full_name.toLowerCase().includes(paymentSearch.trim().toLowerCase())) return false
                  return true
                }).length
                return `Showing ${count} of ${enriched.length} registrations`
              })()}
            </p>
          </div>

          {/* Payments table — grouped by registration */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
            {(() => {
              const paymentFilteredRegs = enriched.filter(r => {
                if (paymentStatusFilter !== 'All' && r.payment_status !== paymentStatusFilter) return false
                if (paymentSearch.trim() && !r.full_name.toLowerCase().includes(paymentSearch.trim().toLowerCase())) return false
                return true
              })
              return paymentFilteredRegs.length === 0 ? (
                <div className="py-12 text-center text-sm text-on-surface-variant/70">
                  {enriched.length === 0 ? 'No registrations yet.' : 'No registrations match the filter.'}
                </div>
              ) : paymentFilteredRegs.map(r => {
              const regReceipts    = paymentReceipts.filter(pr => pr.registration_id === r.id)
              const pendingCount   = regReceipts.filter(pr => pr.status === 'Pending Review').length
              const amountPaid     = r.amount_paid ?? 0
              const remaining      = Math.max(0, (r.amount_owed ?? 0) - amountPaid)
              const progressPct    = r.amount_owed > 0 ? Math.min(100, (amountPaid / r.amount_owed) * 100) : 0
              const isExpanded     = expandedRegIds.has(r.id)
              const isSponsored = r.payment_status === 'Sponsored'
              const rowBg = r.payment_status === 'Paid'
                ? 'bg-green-50/40'
                : r.payment_status === 'Partial Payment'
                ? 'bg-blue-50/40'
                : r.payment_status === 'Pending'
                ? 'bg-amber-50/40'
                : isSponsored
                ? 'bg-purple-50/40'
                : ''

              return (
                <div key={r.id} className={`border-b border-outline-variant/50 last:border-b-0 ${rowBg}`}>
                  {/* Main row */}
                  <div className="flex items-center gap-4 px-4 py-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-on-surface truncate">{r.full_name}</p>
                      <p className="text-xs text-on-surface-variant mt-0.5">
                        {formatDate(r.pricing_tiers?.arrival_date ?? r.arrival_date)}
                      </p>
                    </div>
                    <div className="hidden sm:block text-right min-w-[100px]">
                      <p className="text-xs text-on-surface-variant/70 mb-0.5">Fee / Paid</p>
                      <p className="text-sm font-semibold text-on-surface">
                        {formatCurrency(amountPaid)}{' '}
                        <span className="font-normal text-on-surface-variant/70">/ {formatCurrency(r.amount_owed)}</span>
                      </p>
                    </div>
                    {/* Mini progress bar — hidden for Sponsored */}
                    {!isSponsored && (
                      <div className="hidden sm:flex flex-col justify-center min-w-[80px]">
                        <div className="w-20 h-1.5 rounded-full bg-surface-container-highest overflow-hidden">
                          <div className="h-full rounded-full bg-primary" style={{ width: `${progressPct}%` }} />
                        </div>
                        {remaining > 0 && (
                          <p className="text-xs text-rose-600 mt-0.5">{formatCurrency(remaining)} left</p>
                        )}
                      </div>
                    )}
                    <StatusBadge status={r.payment_status} />
                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {pendingCount > 0 && (
                        <button
                          onClick={() => setExpandedRegIds(prev => {
                            const next = new Set(prev)
                            next.has(r.id) ? next.delete(r.id) : next.add(r.id)
                            return next
                          })}
                          className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 transition"
                        >
                          Review {pendingCount} Receipt{pendingCount !== 1 ? 's' : ''}
                        </button>
                      )}
                      {r.payment_status !== 'Paid' && !isSponsored && (
                        <button
                          onClick={() => sendReminder(r)}
                          disabled={sendingIds.has(r.id)}
                          className="rounded-lg border border-outline-variant px-3 py-1.5 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-50 disabled:cursor-not-allowed transition"
                        >
                          {sendingIds.has(r.id) ? t('admin.sendingBtn') : t('admin.sendReminders')}
                        </button>
                      )}
                      {canEdit() && (r.payment_status === 'Paid' || r.payment_status === 'Partial Payment') &&
                        (r.amount_paid ?? 0) > 0 && (
                        <button
                          onClick={() => openRefundModal(r)}
                          className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 transition"
                        >
                          {t('admin.refundBtn')}
                        </button>
                      )}
                      {canUploadOnBehalf && r.payment_status !== 'Paid' && r.payment_status !== 'Cancelled' && r.payment_status !== 'Sponsored' && (
                        <button
                          onClick={() => openAdminUpload(r)}
                          className="rounded-lg border border-oasis-300 bg-oasis-50 px-3 py-1.5 text-xs font-semibold text-oasis-700 hover:bg-oasis-100 transition"
                        >
                          {t('admin.uploadOnBehalfBtn')}
                        </button>
                      )}
                      {canEdit() && r.payment_status !== 'Sponsored' && r.payment_status !== 'Cancelled' && (
                        agreementByReg[r.id] ? (
                          <button
                            type="button"
                            onClick={() => openAgreementModal(r)}
                            className="text-sm px-3 py-1.5 rounded-lg border border-primary/40 text-primary hover:bg-primary/5"
                          >
                            {t('admin.viewEditAgreement')}
                          </button>
                        ) : r.payment_status !== 'Paid' ? (
                          <button
                            type="button"
                            onClick={() => openAgreementModal(r)}
                            className="text-sm px-3 py-1.5 rounded-lg border border-outline text-on-surface-variant hover:bg-surface-variant"
                          >
                            {t('admin.createAgreement')}
                          </button>
                        ) : null
                      )}
                      {regReceipts.length > 0 && (
                        <button
                          onClick={() => setExpandedRegIds(prev => {
                            const next = new Set(prev)
                            next.has(r.id) ? next.delete(r.id) : next.add(r.id)
                            return next
                          })}
                          className="rounded-lg border border-outline-variant/60 px-2 py-1.5 text-xs text-on-surface-variant hover:bg-surface-container-low transition"
                        >
                          {isExpanded ? '▲' : `▼ ${regReceipts.length}`}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Agreement schedule panel */}
                  {agreementByReg[r.id] && (
                    <div className="mt-3 rounded-lg bg-surface-variant/40 p-3 text-sm mx-4 mb-2">
                      <p className="font-semibold mb-2">{t('admin.agreementSchedule')}</p>
                      {(installmentsByAgreement[agreementByReg[r.id].id] ?? []).map(inst => {
                        const status = deriveInstallmentStatus(inst, allocations)
                        return (
                          <div key={inst.id} className="flex justify-between py-0.5">
                            <span>#{inst.seq} · {inst.due_date}</span>
                            <span>{formatCurrency(inst.amount)}
                              <span className={status === 'Paid' ? 'ml-2 text-green-700' : 'ml-2 text-amber-700'}>
                                {status === 'Paid' ? t('admin.installmentPaid') : t('admin.installmentUnpaid')}
                              </span>
                            </span>
                          </div>
                        )
                      })}
                      {canEdit() && (
                        <button type="button" onClick={() => cancelAgreement(agreementByReg[r.id])}
                          className="mt-2 text-xs text-red-600 hover:underline">
                          {t('admin.cancelAgreement')}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Expanded receipt rows */}
                  {isExpanded && regReceipts.length > 0 && (
                    <div className="border-t border-outline-variant/50 divide-y divide-outline-variant/40 bg-surface-container-lowest/60">
                      {regReceipts.map(receipt => (
                        <div key={receipt.id} className="flex items-center gap-4 px-6 py-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-on-surface">{formatCurrency(receipt.amount)}</p>
                            <p className="text-xs text-on-surface-variant mt-0.5">
                              {paymentMethodLabel(receipt.payment_method, t) ?? '—'} &nbsp;·&nbsp;{' '}
                              {new Date(receipt.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                            {receipt.admin_note && (
                              <p className="mt-1 text-xs text-on-surface-variant italic">{receipt.admin_note}</p>
                            )}
                          </div>
                          {receipt.receipt_url && (
                            <a
                              href={receipt.receipt_url} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-oasis-500 hover:text-oasis-800 underline whitespace-nowrap"
                            >
                              {t('admin.viewFile')}
                            </a>
                          )}
                          <ReceiptStatusBadge status={receipt.status} />
                          {canEdit() && receipt.status === 'Pending Review' && (
                            <button
                              onClick={() => openApproveModal(receipt, r)}
                              className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 transition"
                            >
                              {t('admin.approveRejectBtn')}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })
          })()}
          </div>
        </div>
      )}

      {/* ── INCOME & EXPENSES ──────────────────────────────────────────────── */}
      {activeTab === 'Income & Expenses' && (
        <div className="space-y-6">
          {!selectedDashboardCampId ? (
            <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-12 text-center">
              <span className="text-4xl">💰</span>
              <p className="mt-3 text-sm text-on-surface-variant">{t('admin.selectCampIncomePrompt')}</p>
            </div>
          ) : (
            <>
              {/* ── Export buttons ── */}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => void generateFinancialReport(selectedDashboardCamp, incomeBreakdown, expenseSummary, donationsSummary, projectedSummary)}
                  className="flex items-center gap-1.5 rounded-xl border border-oasis-200 bg-surface-container-lowest px-4 py-2 text-xs font-medium text-oasis-700 hover:bg-oasis-50 transition"
                >
                  {t('admin.exportPdfBtn')}
                </button>
                <button
                  onClick={exportFinancialReportCSV}
                  className="flex items-center gap-1.5 rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low transition"
                >
                  {t('admin.exportCsvBtn')}
                </button>
              </div>

              {/* ── Section 1: Income Breakdown ── */}
              <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
                <div className="px-5 py-4 border-b border-outline-variant/50">
                  <h3 className="text-sm font-semibold text-on-surface-variant">{t('admin.incomeBreakdownTitle')}</h3>
                  <p className="text-xs text-on-surface-variant/70 mt-0.5">{t('admin.incomeBreakdownSub')}</p>
                </div>
                <div className="p-5 space-y-4">
                  {/* By-method ledger */}
                  {incomeBreakdown.methods.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-outline-variant/50">
                          <th className="py-2 text-left text-xs font-semibold text-on-surface-variant">{t('admin.colPaymentMethod')}</th>
                          <th className="py-2 text-right text-xs font-semibold text-on-surface-variant">{t('admin.colCount')}</th>
                          <th className="py-2 text-right text-xs font-semibold text-on-surface-variant">{t('admin.colAmount')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-outline-variant/30">
                        {incomeBreakdown.methods.map(row => (
                          <tr key={row.method}>
                            <td className="py-2.5 text-on-surface-variant">{paymentMethodLabel(row.method, t) ?? row.method}</td>
                            <td className="py-2.5 text-right text-on-surface-variant">{row.count}</td>
                            <td className="py-2.5 text-right font-semibold text-on-surface">{formatCurrency(row.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-on-surface-variant/70">{t('admin.noApprovedReceipts')}</p>
                  )}
                  {/* Totals */}
                  <div className="border-t border-outline-variant/50 pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-on-surface-variant">{t('admin.grossIncome')}</span>
                      <span className="font-semibold text-on-surface">{formatCurrency(incomeBreakdown.grossIncome)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-on-surface-variant">{t('admin.totalRefunded')}</span>
                      <span className="font-semibold text-red-600">− {formatCurrency(incomeBreakdown.totalRefunded)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-outline-variant/50 pt-2">
                      <span className="font-semibold text-on-surface">{t('admin.netIncome')}</span>
                      <span className={`font-bold text-base ${incomeBreakdown.netIncome >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        {formatCurrency(incomeBreakdown.netIncome)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Section 1B: Donations (income) ── */}
              <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
                <div className="px-5 py-4 border-b border-outline-variant/50 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-on-surface-variant">{t('admin.donationsTitle')}</h3>
                    <p className="text-xs text-on-surface-variant/70 mt-0.5">{t('admin.donationsSub')}</p>
                  </div>
                  {canEdit() && (
                    <button
                      onClick={() => openNewDonationModal()}
                      className="flex items-center gap-1.5 rounded-xl border border-oasis-200 bg-oasis-50 px-3 py-1.5 text-xs font-semibold text-oasis-700 hover:bg-oasis-100 transition"
                    >
                      {t('admin.addDonationBtn')}
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-outline-variant/50 bg-surface-container-low">
                        <TH>{t('admin.colDonor')}</TH><TH>{t('admin.colNote')}</TH><TH>{t('admin.colCategory')}</TH><TH>{t('admin.colDate')}</TH><TH>{t('admin.colAmount')}</TH><TH>{t('admin.colReceipt')}</TH><TH>{t('admin.colActions')}</TH>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/30">
                      {donationsSummary.list.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-sm text-on-surface-variant/70">
                            {t('admin.noDonationsYet')}
                          </td>
                        </tr>
                      ) : donationsSummary.list.map(d => (
                        <tr key={d.id} className="hover:bg-surface-container-low/60 transition">
                          <td className="px-4 py-3 text-on-surface">
                            {(d.donor_name ?? '').trim()
                              ? donorDisplayName(d, t('admin.donationAnonymous'))
                              : <span className="italic text-on-surface-variant/80">{t('admin.donationAnonymous')}</span>}
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant">{d.description || '—'}</td>
                          <td className="px-4 py-3 text-on-surface-variant text-xs">
                            <span className="inline-flex items-center rounded-full border border-outline-variant/60 bg-surface-container px-2.5 py-0.5 font-semibold">
                              {d.category ?? 'Other'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant text-xs">
                            {d.donated_at ? new Date(d.donated_at + 'T00:00:00').toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3 font-semibold text-on-surface">{formatCurrency(d.amount)}</td>
                          <td className="px-4 py-3">
                            {d.receipt_url
                              ? <a href={d.receipt_url} target="_blank" rel="noreferrer"
                                  className="text-xs text-oasis-500 underline hover:text-oasis-800">{t('admin.viewReceipt')}</a>
                              : <span className="text-xs text-on-surface-variant/70">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {canEdit() && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => openEditDonationModal(d)}
                                  className="rounded-lg border border-outline-variant bg-surface-container-lowest px-2.5 py-1 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low transition"
                                >{t('admin.editBtn')}</button>
                                <button
                                  onClick={() => handleDeleteDonation(d.id)}
                                  disabled={deletingDonationId === d.id}
                                  className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50 transition"
                                >{deletingDonationId === d.id ? '…' : t('admin.deleteBtn')}</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {donationsSummary.list.length > 0 && (
                      <tfoot>
                        <tr className="border-t border-outline-variant/60 bg-surface-container-low">
                          <td colSpan={4} className="px-4 py-2.5 text-xs font-semibold text-on-surface-variant uppercase tracking-wide">{t('admin.colTotal')}</td>
                          <td className="px-4 py-2.5 font-bold text-on-surface">{formatCurrency(donationsSummary.total)}</td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* ── Section 2A: General Expenses ── */}
              <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
                <div className="px-5 py-4 border-b border-outline-variant/50 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-on-surface-variant">{t('admin.generalExpensesTitle')}</h3>
                    <p className="text-xs text-on-surface-variant/70 mt-0.5">{t('admin.generalExpensesSub')}</p>
                  </div>
                  {canEdit() && (
                    <button
                      onClick={() => openNewExpenseModal()}
                      className="flex items-center gap-1.5 rounded-xl border border-oasis-200 bg-oasis-50 px-3 py-1.5 text-xs font-semibold text-oasis-700 hover:bg-oasis-100 transition"
                    >
                      {t('admin.addExpenseBtn')}
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-outline-variant/50 bg-surface-container-low">
                        <TH>{t('admin.colDescription')}</TH><TH>{t('admin.colCategory')}</TH><TH>{t('admin.colAmount')}</TH><TH>{t('admin.colInvoice')}</TH><TH>{t('admin.colActions')}</TH>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/30">
                      {expenseSummary.general.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-8 text-center text-sm text-on-surface-variant/70">
                            {t('admin.noExpensesYet')}
                          </td>
                        </tr>
                      ) : expenseSummary.general.map(e => (
                        <tr key={e.id} className="hover:bg-surface-container-low/60 transition">
                          <td className="px-4 py-3 text-on-surface">{e.description}</td>
                          <td className="px-4 py-3 text-on-surface-variant text-xs">
                            <span className="inline-flex items-center rounded-full border border-outline-variant/60 bg-surface-container px-2.5 py-0.5 font-semibold">
                              {e.category ?? 'Other'}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-on-surface">{formatCurrency(e.amount)}</td>
                          <td className="px-4 py-3">
                            {e.invoice_url
                              ? <a href={e.invoice_url} target="_blank" rel="noreferrer"
                                  className="text-xs text-oasis-500 underline hover:text-oasis-800">{t('admin.viewInvoice')}</a>
                              : <span className="text-xs text-on-surface-variant/70">—</span>}
                          </td>
                          <td className="px-4 py-3">
                            {canEdit() && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => openEditExpenseModal(e)}
                                  className="rounded-lg border border-outline-variant bg-surface-container-lowest px-2.5 py-1 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low transition"
                                >{t('admin.editBtn')}</button>
                                <button
                                  onClick={() => handleDeleteExpense(e.id)}
                                  disabled={deletingExpenseId === e.id}
                                  className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50 transition"
                                >{deletingExpenseId === e.id ? '…' : t('admin.deleteBtn')}</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {expenseSummary.general.length > 0 && (
                      <tfoot>
                        <tr className="border-t border-outline-variant/60 bg-surface-container-low">
                          <td colSpan={2} className="px-4 py-2.5 text-xs font-semibold text-on-surface-variant uppercase tracking-wide">{t('admin.colTotal')}</td>
                          <td className="px-4 py-2.5 font-bold text-on-surface">{formatCurrency(expenseSummary.totalGeneral)}</td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* ── Section 3: Financial Summary ── */}
              <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-6">
                <h3 className="text-sm font-semibold text-on-surface-variant mb-4">{t('admin.financialSummaryTitle')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Actual — existing summary block (unchanged content) */}
                  <div className="rounded-xl border border-outline p-4">
                    <h4 className="font-semibold mb-3">{t('admin.summaryActual')}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-on-surface-variant">{t('admin.netIncome')}</span>
                        <span className="font-semibold text-on-surface">{formatCurrency(incomeBreakdown.netIncome)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-on-surface-variant">{t('admin.donations')}</span>
                        <span className="font-semibold text-on-surface">+ {formatCurrency(donationsSummary.total)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-on-surface-variant">{t('admin.generalExpenses')}</span>
                        <span className="font-semibold text-on-surface">− {formatCurrency(expenseSummary.totalGeneral)}</span>
                      </div>
                      <div className="flex justify-between text-sm border-t border-outline-variant/60 pt-3">
                        <span className="font-bold text-on-surface">{t('admin.netResult')}</span>
                        <span className={`font-bold text-lg ${expenseSummary.netResult >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {expenseSummary.netResult >= 0 ? '+' : ''}{formatCurrency(expenseSummary.netResult)}
                        </span>
                      </div>
                    </div>
                    {financeData.totalSponsored > 0 && (
                      <div className="mt-4 flex items-center gap-2 rounded-lg bg-purple-50 border border-purple-100 px-3 py-2.5">
                        <span className="text-purple-500 text-sm">🎁</span>
                        <p className="text-xs text-purple-700">
                          <span className="font-semibold">{financeData.totalSponsored} sponsored registration{financeData.totalSponsored !== 1 ? 's' : ''}</span>
                          {' '}— not included in revenue figures.
                        </p>
                      </div>
                    )}
                    {expenseSummary.totalGeneral === 0 && (
                      <p className="mt-3 text-xs text-on-surface-variant/70">
                        {t('admin.addExpensesPrompt')}
                      </p>
                    )}
                  </div>

                  {/* Projected with agreements */}
                  <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
                    <h4 className="font-semibold mb-3">{t('admin.summaryProjected')}</h4>
                    <div className="flex justify-between text-sm py-0.5">
                      <span>{t('admin.reportNetIncomeLabel')}</span><span>{formatCurrency(incomeBreakdown.netIncome)}</span>
                    </div>
                    <div className="flex justify-between text-sm py-0.5">
                      <span>{t('admin.reportDonationsLabel')}</span><span>+ {formatCurrency(donationsSummary.total)}</span>
                    </div>
                    <div className="flex justify-between text-sm py-0.5">
                      <span>{t('admin.reportGeneralExpensesLabel')}</span><span>− {formatCurrency(expenseSummary.totalGeneral)}</span>
                    </div>
                    <div className="flex justify-between text-sm py-0.5 text-primary">
                      <span>{t('admin.pendingAgreementIncome')}</span><span>+ {formatCurrency(projectedSummary.pendingAgreementIncome)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t border-outline mt-2 pt-2">
                      <span>{t('admin.projectedNetResult')}</span><span>{formatCurrency(projectedSummary.projectedNetResult)}</span>
                    </div>
                    <p className="text-xs text-on-surface-variant mt-2">{t('admin.projectedCaption')}</p>
                  </div>
                </div>
              </div>

              {/* ── Section 4: Transaction Details (collapsible) ── */}
              <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
                <button
                  className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-surface-container-low transition"
                  onClick={() => setTransactionDetailsOpen(v => !v)}
                >
                  <h3 className="text-sm font-semibold text-on-surface-variant">{t('admin.transactionDetailsTitle')}</h3>
                  <span className="text-xs text-on-surface-variant/70">{transactionDetailsOpen ? t('admin.transactionDetailsHide') : t('admin.transactionDetailsShow')}</span>
                </button>

                {transactionDetailsOpen && (
                  <div className="border-t border-outline-variant/50 p-5 space-y-6">
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard
                        label={t('admin.statTotalCollected')}
                        value={formatCurrency(financeData.totalCollected)}
                        sub="sum of amount_paid — all registrations"
                        colorClass="bg-green-50 border-green-100 text-green-700"
                      />
                      <StatCard
                        label={t('admin.statReceiptsInReview')}
                        value={formatCurrency(financeData.totalConfirmed)}
                        sub="pending receipt amounts awaiting approval"
                        colorClass="bg-amber-50 border-amber-100 text-amber-700"
                      />
                      <StatCard
                        label={t('admin.statOutstandingBalance')}
                        value={formatCurrency(financeData.totalPending)}
                        sub="unpaid remainder — all non-Paid registrations"
                        colorClass="bg-rose-50 border-rose-100 text-rose-700"
                      />
                      <StatCard
                        label={t('admin.statTotalExpected')}
                        value={formatCurrency(financeData.totalExpected)}
                        sub="sum of amount_owed — all non-cancelled"
                        colorClass="bg-oasis-50 border-oasis-100 text-oasis-700"
                      />
                    </div>

                    {/* Charts row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Payment method chart */}
                      <div className="bg-surface-container-low rounded-xl p-5">
                        <h4 className="text-xs font-semibold text-on-surface-variant mb-4">{t('admin.breakdownByMethod')}</h4>
                        {financeData.methodBreakdown.length > 0 ? (
                          <>
                            <ResponsiveContainer width="100%" height={160}>
                              <BarChart data={financeData.methodBreakdown.map(m => ({ ...m, method: paymentMethodLabel(m.method, t) ?? m.method }))} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="method" tick={{ fontSize: 11 }} />
                                <YAxis tickFormatter={v => `$${v}`} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={v => [formatCurrency(v), 'Total']} />
                                <Bar dataKey="total" name="Amount" fill="#43543f" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                            <table className="w-full text-sm mt-3">
                              <thead>
                                <tr className="border-b border-outline-variant/60">
                                  <th className="py-1.5 text-left text-xs font-semibold text-on-surface-variant">{t('admin.colMethod')}</th>
                                  <th className="py-1.5 text-right text-xs font-semibold text-on-surface-variant">{t('admin.colCount')}</th>
                                  <th className="py-1.5 text-right text-xs font-semibold text-on-surface-variant">{t('admin.colTotal')}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-outline-variant/40">
                                {financeData.methodBreakdown.map(row => (
                                  <tr key={row.method}>
                                    <td className="py-2 text-on-surface-variant">{paymentMethodLabel(row.method, t) ?? row.method}</td>
                                    <td className="py-2 text-right text-on-surface-variant">{row.count}</td>
                                    <td className="py-2 text-right font-semibold text-on-surface">{formatCurrency(row.total)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </>
                        ) : (
                          <div className="flex items-center justify-center h-24 text-sm text-on-surface-variant/70">
                            {t('admin.noApprovedReceipts')}
                          </div>
                        )}
                      </div>

                      {/* Payment breakdown chart */}
                      <div className="bg-surface-container-low rounded-xl p-5">
                        <h4 className="text-xs font-semibold text-on-surface-variant mb-4">{t('admin.paymentBreakdown')}</h4>
                        {enriched.length > 0 ? (
                          <>
                            <ResponsiveContainer width="100%" height={160}>
                              <BarChart
                                data={[
                                  { name: 'Collected',   amount: financeData.totalCollected, fill: '#43543f' },
                                  { name: 'In Review',   amount: financeData.totalConfirmed, fill: '#f59e0b' },
                                  { name: 'Outstanding', amount: financeData.totalPending,   fill: '#f43f5e' },
                                ]}
                                margin={{ top: 4, right: 8, left: -8, bottom: 0 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                <YAxis tickFormatter={v => `$${v}`} tick={{ fontSize: 11 }} />
                                <Tooltip formatter={v => [formatCurrency(v), 'Amount']} />
                                <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                                  {[{ fill: '#43543f' }, { fill: '#f59e0b' }, { fill: '#f43f5e' }].map((entry, i) => (
                                    <Cell key={i} fill={entry.fill} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                            <p className="mt-2 text-xs text-on-surface-variant/70 text-right">
                              Collected + Outstanding = {formatCurrency(financeData.totalCollected + financeData.totalPending)}
                              &nbsp;·&nbsp; Expected = {formatCurrency(financeData.totalExpected)}
                            </p>
                          </>
                        ) : (
                          <div className="flex items-center justify-center h-24 text-sm text-on-surface-variant/70">
                            {t('admin.chartNoRegistrations')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Transaction list */}
                    <div className="rounded-xl border border-outline-variant/60 overflow-hidden">
                      <div className="px-5 py-3 border-b border-outline-variant/50 flex items-center justify-between gap-3 flex-wrap bg-surface-container-low">
                        <span className="text-xs font-semibold text-on-surface-variant">
                          {t('admin.paymentReceiptsRefunds', { count: financeData.transactions.length })}
                        </span>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => generateReceiptReport(selectedDashboardCamp, enriched, paymentReceipts, refundedWithReceipts)}
                            disabled={enriched.filter(r => r.payment_status !== 'Cancelled').length === 0}
                            className="flex items-center gap-1.5 rounded-xl border border-oasis-200 bg-surface-container-lowest px-3 py-1.5 text-xs font-medium text-oasis-700 hover:bg-oasis-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                          >
                            {t('admin.receiptReportPdf')}
                          </button>
                          <button
                            onClick={exportFinancesCSV}
                            disabled={financeData.transactions.length === 0}
                            className="flex items-center gap-1.5 rounded-xl border border-outline-variant bg-surface-container-lowest px-3 py-1.5 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-40 disabled:cursor-not-allowed transition"
                          >
                            {t('admin.exportCsvBtn')}
                          </button>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-outline-variant/50 bg-surface-container-low">
                              <TH>{t('admin.colAttendee')}</TH><TH>{t('admin.colAmount')}</TH><TH>{t('admin.colMethod')}</TH><TH>{t('admin.colReceiptStatus')}</TH><TH>{t('admin.colSubmitted')}</TH>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-outline-variant/30">
                            {financeData.transactions.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-sm text-on-surface-variant/70">
                                  {t('admin.noTransactionsYet')}
                                </td>
                              </tr>
                            ) : financeData.transactions.map(r => (
                              <tr key={r.id}
                                className={`hover:bg-surface-container-low/60 transition ${r._type === 'refund' ? 'bg-red-50/40' : ''}`}
                              >
                                <td className="px-4 py-3 font-medium text-on-surface whitespace-nowrap">
                                  {r.registration?.full_name ?? '—'}
                                </td>
                                <td className={`px-4 py-3 font-semibold whitespace-nowrap ${r._type === 'refund' ? 'text-red-600' : 'text-on-surface'}`}>
                                  {r._type === 'refund' ? '−' : ''}{formatCurrency(r.amount)}
                                </td>
                                <td className="px-4 py-3 text-on-surface-variant">
                                  {r._type === 'refund' ? (r.reason?.slice(0, 40) ?? '—') : (paymentMethodLabel(r.payment_method, t) ?? '—')}
                                </td>
                                <td className="px-4 py-3">
                                  {r._type === 'refund'
                                    ? <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700">{t('admin.refundBadge')}</span>
                                    : <ReceiptStatusBadge status={r.status} />
                                  }
                                </td>
                                <td className="px-4 py-3 text-on-surface-variant text-xs whitespace-nowrap">
                                  {new Date(r.created_at).toLocaleDateString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── REFUNDS ────────────────────────────────────────────────────────── */}
      {activeTab === 'Refunds' && (
        <div className="space-y-5">
          {!selectedDashboardCampId ? (
            <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-12 text-center">
              <span className="text-4xl">↩️</span>
              <p className="mt-3 text-sm text-on-surface-variant">{t('admin.selectCampRefundsPrompt')}</p>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard
                  label={t('admin.statRefundsProcessed')}
                  value={String(refundStats.count)}
                  sub={t('admin.statRefundsTotalSub')}
                  colorClass="bg-red-50 border-red-100 text-red-700"
                />
                <StatCard
                  label={t('admin.statTotalAmountRefunded')}
                  value={formatCurrency(refundStats.totalAmount)}
                  sub={t('admin.statRefundSumSub')}
                  colorClass="bg-rose-50 border-rose-100 text-rose-700"
                />
                <StatCard
                  label={t('admin.statRegsCancelled')}
                  value={String(refundStats.cancelledCount)}
                  sub={t('admin.statRegsUniqueRefundSub')}
                  colorClass="bg-orange-50 border-orange-100 text-orange-700"
                />
              </div>

              {/* Filters + export */}
              <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-4 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-1">{t('admin.filterSearchName')}</label>
                  <input
                    type="text"
                    placeholder={t('admin.refundFilterSearchPlaceholder')}
                    value={refundSearch}
                    onChange={e => setRefundSearch(e.target.value)}
                    className="w-full rounded-lg border border-outline-variant px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition bg-surface-container-lowest"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-1">{t('admin.refundFilterFromDate')}</label>
                  <input
                    type="date"
                    value={refundDateFrom}
                    onChange={e => setRefundDateFrom(e.target.value)}
                    className="rounded-lg border border-outline-variant px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition bg-surface-container-lowest"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-on-surface-variant mb-1">{t('admin.refundFilterToDate')}</label>
                  <input
                    type="date"
                    value={refundDateTo}
                    onChange={e => setRefundDateTo(e.target.value)}
                    className="rounded-lg border border-outline-variant px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition bg-surface-container-lowest"
                  />
                </div>
                {(refundSearch || refundDateFrom || refundDateTo) && (
                  <button
                    onClick={() => { setRefundSearch(''); setRefundDateFrom(''); setRefundDateTo('') }}
                    className="rounded-lg border border-outline-variant px-3 py-2 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low transition self-end"
                  >
                    {t('admin.clearBtn')}
                  </button>
                )}
                <div className="ml-auto self-end">
                  <button
                    onClick={exportRefundsCSV}
                    disabled={filteredRefunds.length === 0}
                    className="flex items-center gap-1.5 rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    {t('admin.exportCsvBtn')}
                  </button>
                </div>
              </div>

              {/* Refunds table */}
              <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
                <div className="px-5 py-3 border-b border-outline-variant/50">
                  <p className="text-sm font-semibold text-on-surface-variant">
                    {filteredRefunds.length} refund{filteredRefunds.length !== 1 ? 's' : ''}
                    {filteredRefunds.length !== refunds.length && ` (filtered from ${refunds.length})`}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-outline-variant/50 bg-surface-container-low">
                        <TH>{t('admin.colName')}</TH>
                        <TH>{t('admin.colAmountRefunded')}</TH>
                        <TH>{t('admin.colReason')}</TH>
                        <TH>{t('admin.colProcessedDate')}</TH>
                        <TH>{t('admin.colRefundedBy')}</TH>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/30">
                      {filteredRefunds.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-12 text-center text-sm text-on-surface-variant/70">
                            {refunds.length === 0 ? t('admin.noRefundsYet') : t('admin.noRefundsMatch')}
                          </td>
                        </tr>
                      ) : filteredRefunds.map(r => (
                        <tr key={r.id} className="hover:bg-red-50/30 transition">
                          <td className="px-4 py-3 font-medium text-on-surface whitespace-nowrap">
                            {r.registration?.full_name ?? '—'}
                          </td>
                          <td className="px-4 py-3 font-semibold text-red-600 whitespace-nowrap">
                            {formatCurrency(r.amount)}
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant max-w-xs">
                            <p className="truncate" title={r.reason}>{r.reason}</p>
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant whitespace-nowrap">
                            {new Date(r.created_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                            })}
                          </td>
                          <td className="px-4 py-3 text-on-surface-variant text-xs">
                            {r.refunded_by_email ?? r.refunded_by ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredRefunds.length > 0 && (
                  <div className="px-5 py-3 border-t border-outline-variant/50 flex justify-end">
                    <p className="text-sm font-semibold text-red-700">
                      Total: {formatCurrency(filteredRefunds.reduce((s, r) => s + (r.amount ?? 0), 0))}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── CAMPS ──────────────────────────────────────────────────────────── */}
      {activeTab === 'Camps' && (
        <div className="space-y-4">

          {canEdit() && (
            <div className="flex justify-end">
              <button
                onClick={openNewCampModal}
                className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 transition"
              >
                {t('admin.newCampBtn')}
              </button>
            </div>
          )}

          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
            {allCamps.length === 0 ? (
              <div className="p-12 text-center text-sm text-on-surface-variant/70">
                {t('admin.noCampsPrompt')}
              </div>
            ) : (
              <div className="divide-y divide-outline-variant/40">
                {allCamps.map(c => (
                  <div key={c.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-on-surface text-sm">{c.name}</p>
                        {c.registration_open
                          ? <span className="rounded-full bg-green-100 text-green-700 border border-green-200 px-2.5 py-0.5 text-xs font-semibold">{t('admin.campOpen')}</span>
                          : <span className="rounded-full bg-surface-container text-on-surface-variant border border-outline-variant/60 px-2.5 py-0.5 text-xs font-semibold">{t('admin.campClosed')}</span>
                        }
                      </div>
                      <p className="mt-0.5 text-xs text-on-surface-variant">
                        {c.location && <span>{c.location} · </span>}
                        {c.start_date && c.end_date
                          ? `${formatDate(c.start_date)} – ${formatDate(c.end_date)}`
                          : '—'
                        }
                        {c.payment_due_date && <span> · Due: {formatDate(c.payment_due_date)}</span>}
                      </p>
                      {c.description && (
                        <p className="mt-1 text-xs text-on-surface-variant/70 truncate">{c.description}</p>
                      )}
                    </div>
                    {canEdit() && (
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => openTierModal(c)}
                          className="rounded-lg border border-outline-variant px-3 py-1.5 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low transition"
                        >
                          {t('admin.pricingTiersBtn')}
                        </button>
                        <button
                          onClick={() => openEditCampModal(c)}
                          className="rounded-lg border border-oasis-200 px-3 py-1.5 text-xs font-medium text-oasis-700 hover:bg-oasis-50 transition"
                        >
                          {t('admin.editBtn')}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── ROOMS ──────────────────────────────────────────────────────────── */}
      {activeTab === 'Rooms' && (
        <div className="space-y-4">

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: t('admin.roomsSummaryAssigned'), value: roomsReportData.summary.assigned },
              { label: t('admin.roomsSummaryPending'), value: roomsReportData.summary.pending },
              { label: t('admin.roomsSummaryPendingMale'), value: roomsReportData.summary.pendingMale },
              { label: t('admin.roomsSummaryPendingFemale'), value: roomsReportData.summary.pendingFemale },
            ].map(stat => (
              <div key={stat.label} className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-4">
                <p className="text-xs text-on-surface-variant">{stat.label}</p>
                <p className="mt-1 text-2xl font-semibold text-on-surface">{stat.value}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-on-surface-variant">
              {rooms.length} room{rooms.length !== 1 ? 's' : ''}
            </p>
            {canEdit() && (
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => generateRoomsReport(selectedDashboardCamp, roomsReportData)}
                  className="rounded-xl border border-outline-variant/60 bg-surface-container-lowest px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition"
                >
                  {t('admin.roomsPrintBtn')}
                </button>
                <button
                  onClick={exportRoomsCSV}
                  className="rounded-xl border border-outline-variant/60 bg-surface-container-lowest px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition"
                >
                  {t('admin.roomsExportCsvBtn')}
                </button>
                <button
                  onClick={autoAssign}
                  disabled={autoAssigning || rooms.length === 0}
                  className="rounded-xl border border-oasis-300 bg-surface-container-lowest px-4 py-2 text-sm font-medium text-oasis-700 hover:bg-oasis-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {autoAssigning ? t('admin.assigningBtn') : t('admin.autoAssignBtn')}
                </button>
                <button
                  onClick={openNewRoomModal}
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-oasis-800 transition"
                >
                  {t('admin.addRoomBtn')}
                </button>
              </div>
            )}
          </div>

          {rooms.length === 0 ? (
            <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-12 text-center text-sm text-on-surface-variant/70">
              {t('admin.noRoomsYet')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rooms.map(room => {
                const occupied = roomOccupancy[room.id] ?? 0
                const pct  = room.capacity ? Math.min(100, Math.round((occupied / room.capacity) * 100)) : 0
                const full = occupied >= room.capacity
                return (
                  <div key={room.id} className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-on-surface text-sm">{room.name}</p>
                        <p className="text-xs text-on-surface-variant mt-0.5">
                          {room.sex}
                          {(room.min_age || room.max_age) && (
                            <span> · {room.min_age ?? 0}–{room.max_age ?? '∞'} yrs</span>
                          )}
                        </p>
                      </div>
                      <div className="flex gap-1.5">
                        {canEdit() && (
                          <button
                            onClick={() => openEditRoomModal(room)}
                            className="text-xs border border-outline-variant/60 rounded-lg px-2 py-1 text-on-surface-variant hover:bg-surface-container-low transition"
                          >
                            {t('admin.editBtn')}
                          </button>
                        )}
                        <button
                          onClick={() => { setManageRoom(room); setAssignSearch('') }}
                          className="text-xs border border-oasis-200 rounded-lg px-2 py-1 text-oasis-500 hover:bg-oasis-50 transition"
                        >
                          {t('admin.manageRoomBtn')}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-on-surface-variant mb-1.5">
                      <span>{occupied} / {room.capacity} occupied</span>
                      <span className={full ? 'text-red-500 font-semibold' : ''}>{pct}%</span>
                    </div>
                    <div className="w-full bg-surface-container rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          full ? 'bg-red-500' : pct > 75 ? 'bg-amber-400' : 'bg-primary'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'Churches' && (
        <ChurchesTab showToast={showToast} />
      )}

      {activeTab === 'Help Videos' && (
        <HelpVideosTab showToast={showToast} />
      )}

      {activeTab === 'Notifications' && (
        <NotificationsTab user={user} showToast={showToast} />
      )}

      {/* ── ADMIN USERS (superadmin only) ──────────────────────────────────── */}
      {activeTab === 'Admin Users' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('admin.adminUsersMgmtTitle')}</h2>
              <p className="text-sm text-on-surface-variant">{t('admin.adminUsersMgmtDesc')}</p>
            </div>
            <button
              onClick={loadAdminUsers}
              disabled={adminUsersLoading}
              className="flex items-center gap-2 rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-50 transition"
            >
              {adminUsersLoading
                ? <span className="w-4 h-4 border-2 border-outline border-t-transparent rounded-full animate-spin" />
                : <span>↻</span>}
              {t('admin.refresh')}
            </button>
          </div>

          {/* Grant access form */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-6">
            <h3 className="text-sm font-semibold text-on-surface-variant mb-4">{t('admin.grantAccessTitle')}</h3>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-52">
                <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{t('admin.userEmailLabel')}</label>
                <input
                  type="email" placeholder="user@example.com"
                  value={grantEmail} onChange={e => setGrantEmail(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-on-surface-variant mb-1.5">{t('admin.roleLabel')}</label>
                <select value={grantRoleValue} onChange={e => setGrantRoleValue(e.target.value)} className={INPUT_CLS}>
                  <option value="staff">staff</option>
                  <option value="readonly_admin">readonly_admin</option>
                  <option value="admin">admin</option>
                  <option value="superadmin">superadmin</option>
                </select>
              </div>
              <button
                onClick={handleGrantAccess}
                disabled={grantSaving || !grantEmail.trim()}
                className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {grantSaving ? t('admin.savingBtn') : t('admin.grantAccessBtn')}
              </button>
            </div>
            {grantError && <p className="mt-2 text-xs text-red-600">{grantError}</p>}
          </div>

          {/* Current admin users list */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
            <div className="px-5 py-4 border-b border-outline-variant/50">
              <h3 className="text-sm font-semibold text-on-surface-variant">{t('admin.currentAdminUsers')}</h3>
              <p className="text-xs text-on-surface-variant/70 mt-0.5">{adminUsers.length} admin account{adminUsers.length !== 1 ? 's' : ''}</p>
            </div>
            {adminUsersLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : adminUsers.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-on-surface-variant/70">
                {t('admin.noAdminUsers')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-container-low border-b border-outline-variant/50">
                    <tr>
                      <TH>{t('admin.colEmail')}</TH>
                      <TH>{t('admin.colFullName')}</TH>
                      <TH>{t('admin.colRole')}</TH>
                      <TH>{t('admin.colAdded')}</TH>
                      <TH></TH>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/30">
                    {adminUsers.map(u => (
                      <tr key={u.id} className="hover:bg-surface-container-low">
                        <td className="px-4 py-3 font-medium text-on-surface">{u.email}</td>
                        <td className="px-4 py-3 text-on-surface-variant">{u.full_name ?? '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                            u.role === 'superadmin'    ? 'bg-purple-100 text-purple-800 border-purple-200' :
                            u.role === 'admin'         ? 'bg-oasis-100 text-oasis-800 border-oasis-200' :
                            u.role === 'readonly_admin'? 'bg-surface-container text-on-surface-variant border-outline-variant/60' :
                            u.role === 'staff'         ? 'bg-teal-100 text-teal-800 border-teal-200' :
                            'bg-surface-container text-on-surface-variant border-outline-variant/60'
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant">{formatDate(u.created_at?.slice(0, 10))}</td>
                        <td className="px-4 py-3 text-right">
                          {/* Don't allow revoking your own access */}
                          {u.id !== user?.id && (
                            <button
                              onClick={() => handleRevokeAccess(u)}
                              className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 transition"
                            >
                              {t('admin.revokeBtn')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── AUDIT LOG (superadmin only) ─────────────────────────────────────── */}
      {activeTab === 'Audit Log' && (
        <div className="space-y-4">

          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('admin.auditLogTitle')}</h2>
              <p className="text-sm text-on-surface-variant">{t('admin.auditLogDesc')}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={exportAuditLogCSV}
                disabled={filteredAuditLog.length === 0}
                className="flex items-center gap-1.5 rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2 text-xs font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-40 transition"
              >
                {t('admin.exportCsvBtn')}
              </button>
              <button
                onClick={loadAuditLogEntries}
                disabled={auditLogLoading}
                className="flex items-center gap-2 rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-50 transition"
              >
                {auditLogLoading
                  ? <span className="w-4 h-4 border-2 border-outline border-t-transparent rounded-full animate-spin" />
                  : <span>↻</span>}
                {t('admin.refresh')}
              </button>
            </div>
          </div>

          {/* Filter bar */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 p-4 flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-44">
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.filterSearchName')}</label>
              <input
                type="text" placeholder={t('admin.auditLogFilterSearchPlaceholder')}
                value={auditLogSearch} onChange={e => setAuditLogSearch(e.target.value)}
                className={SELECT_CLS + ' w-full'}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.auditLogFilterFrom')}</label>
              <input type="date" value={auditLogDateFrom} onChange={e => setAuditLogDateFrom(e.target.value)} className={SELECT_CLS} />
            </div>
            <div>
              <label className="block text-xs font-medium text-on-surface-variant mb-1">{t('admin.auditLogFilterTo')}</label>
              <input type="date" value={auditLogDateTo} onChange={e => setAuditLogDateTo(e.target.value)} className={SELECT_CLS} />
            </div>
            <p className="text-xs text-on-surface-variant/70 pb-2">{filteredAuditLog.length} of {auditLogEntries.length} entries</p>
          </div>

          {/* Log table */}
          <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/60 overflow-hidden">
            {auditLogLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredAuditLog.length === 0 ? (
              <div className="py-12 text-center text-sm text-on-surface-variant/70">
                {auditLogEntries.length === 0 ? t('admin.noAuditLogYet') : t('admin.noAuditLogMatch')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-container-low border-b border-outline-variant/50">
                    <tr>
                      <TH>{t('admin.colDate')}</TH>
                      <TH>{t('admin.colUser')}</TH>
                      <TH>{t('admin.colRole')}</TH>
                      <TH>{t('admin.colAction')}</TH>
                      <TH>{t('admin.colEntity')}</TH>
                      <TH>{t('admin.colDetails')}</TH>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/30">
                    {filteredAuditLog.map(e => (
                      <tr key={e.id} className="hover:bg-surface-container-low">
                        <td className="px-4 py-3 text-on-surface-variant whitespace-nowrap text-xs">
                          {new Date(e.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant text-xs">{e.user_email ?? '—'}</td>
                        <td className="px-4 py-3">
                          {e.user_role && (
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                              e.user_role === 'superadmin'    ? 'bg-purple-100 text-purple-800 border-purple-200' :
                              e.user_role === 'admin'         ? 'bg-oasis-100 text-oasis-800 border-oasis-200' :
                              e.user_role === 'readonly_admin'? 'bg-surface-container text-on-surface-variant border-outline-variant/60' :
                              'bg-teal-100 text-teal-800 border-teal-200'
                            }`}>
                              {e.user_role}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-medium text-on-surface text-xs whitespace-nowrap">{e.action}</td>
                        <td className="px-4 py-3 text-on-surface-variant text-xs">
                          {e.entity ?? '—'}
                          {e.entity_id && <span className="text-on-surface-variant/70"> #{e.entity_id}</span>}
                        </td>
                        <td className="px-4 py-3 text-on-surface-variant text-xs max-w-xs">
                          {e.details
                            ? <span className="font-mono text-xs text-on-surface-variant/70 truncate block" title={JSON.stringify(e.details)}>
                                {JSON.stringify(e.details).slice(0, 80)}{JSON.stringify(e.details).length > 80 ? '…' : ''}
                              </span>
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/*  MODALS                                                            */}
      {/* ═══════════════════════════════════════════════════════════════════ */}

      {/* Edit Registration — full form */}
      {editingReg && editForm && (
        <ModalWrap onClose={() => { setEditingReg(null); setEditForm(null) }} wide>
          <div className="p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('admin.editRegTitle')}</h3>
                <p className="text-sm text-on-surface-variant mt-0.5">{editingReg.full_name}</p>
              </div>
              <button
                onClick={() => { setEditingReg(null); setEditForm(null) }}
                className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none mt-0.5"
              >×</button>
            </div>

            {modalError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{modalError}</div>
            )}

            <div className="space-y-5">
              {/* Personal details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelFullName')}</label>
                  <input type="text" value={editForm.full_name}
                    onChange={e => setEditForm(p => ({ ...p, full_name: e.target.value }))}
                    className={INPUT_CLS} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelDob')}</label>
                  <input type="date" value={editForm.date_of_birth}
                    onChange={e => setEditForm(p => ({ ...p, date_of_birth: e.target.value }))}
                    className={INPUT_CLS} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelSex')}</label>
                  <select value={editForm.sex}
                    onChange={e => setEditForm(p => ({ ...p, sex: e.target.value }))}
                    className={INPUT_CLS}>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPhone')}</label>
                  <input type="tel" value={editForm.phone}
                    onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))}
                    className={INPUT_CLS} />
                </div>
              </div>

              {/* Emergency contact */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-outline-variant/50">
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelEcName')}</label>
                  <input type="text" value={editForm.emergency_contact_name}
                    onChange={e => setEditForm(p => ({ ...p, emergency_contact_name: e.target.value }))}
                    className={INPUT_CLS} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelEcPhone')}</label>
                  <input type="tel" value={editForm.emergency_contact_phone}
                    onChange={e => setEditForm(p => ({ ...p, emergency_contact_phone: e.target.value }))}
                    className={INPUT_CLS} />
                </div>
              </div>

              {/* Arrival + transport + status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-outline-variant/50">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPricingTier')} <span className="text-xs font-normal text-oasis-500">{t('admin.labelPricingTierAdmin')}</span></label>
                  <select value={editForm.pricing_tier_id}
                    onChange={e => setEditForm(p => ({ ...p, pricing_tier_id: e.target.value }))}
                    className={INPUT_CLS}>
                    <option value="">{t('admin.noTierSelected')}</option>
                    {editCampTiers.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.label}{t.arrival_date ? ` — ${formatDate(t.arrival_date)}` : ''} — {formatCurrency(t.price)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelNeedsTransport')}</label>
                  <div className="flex gap-2 mt-1">
                    {[false, true].map(val => (
                      <button key={String(val)} type="button"
                        onClick={() => setEditForm(p => ({ ...p, needs_transport: val }))}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                          editForm.needs_transport === val
                            ? 'bg-primary text-white border-primary'
                            : 'bg-surface-container-lowest text-on-surface-variant border-outline-variant hover:bg-surface-container-low'
                        }`}>
                        {val ? 'Yes' : 'No'}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPaymentStatus')}</label>
                  <select value={editForm.payment_status}
                    onChange={e => setEditForm(p => ({ ...p, payment_status: e.target.value }))}
                    className={INPUT_CLS}>
                    <option value="Pending">Pending</option>
                    <option value="Partial Payment">Partial Payment</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Paid">Paid</option>
                    <option value="Sponsored">Sponsored</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Fee breakdown */}
              {(() => {
                const previewTier = editCampTiers.find(t => t.id === editForm.pricing_tier_id) ?? null
                const previewCamp = allCamps.find(c => c.id === editingReg?.camp_id) ?? null
                const previewTransport = parseFloat(previewCamp?.transport_cost ?? 0)
                if (!previewTier) return null
                const total = previewTier.price + (editForm.needs_transport ? previewTransport : 0)
                return (
                  <div className="rounded-xl bg-surface-container-low border border-outline-variant/50 px-4 py-3 space-y-1.5 text-sm">
                    <div className="flex justify-between text-on-surface-variant">
                      <span>{t('admin.labelBaseFee')}</span>
                      <span>{formatCurrency(previewTier.price)}</span>
                    </div>
                    {previewTransport > 0 && (
                      <div className={`flex justify-between ${editForm.needs_transport ? 'text-on-surface-variant' : 'text-on-surface-variant/70 line-through'}`}>
                        <span>{t('admin.labelTransport')}</span>
                        <span>{formatCurrency(previewTransport)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-on-surface border-t border-outline-variant/60 pt-1.5 mt-1">
                      <span>{t('admin.labelTotal')}</span>
                      <span>{formatCurrency(total)}</span>
                    </div>
                  </div>
                )
              })()}

              {/* Admin note */}
              <div className="pt-4 border-t border-outline-variant/50">
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">
                  {t('admin.labelAdminNote')} <span className="font-normal text-on-surface-variant/70">{t('admin.labelAdminNoteOptional')}</span>
                </label>
                <textarea
                  value={editForm.admin_note}
                  onChange={e => setEditForm(p => ({ ...p, admin_note: e.target.value }))}
                  rows={3} placeholder="e.g. Paid cash at the door"
                  className="w-full resize-none rounded-lg border border-outline-variant px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleModalSave} disabled={modalSaving}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {modalSaving ? t('admin.savingChangesBtn') : t('admin.saveChangesBtn')}
              </button>
              <button
                onClick={() => { setEditingReg(null); setEditForm(null) }}
                className="rounded-xl border border-outline-variant px-5 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition"
              >
                {t('admin.cancelBtn')}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Expense Modal */}
      {expenseModal.open && (
        <ModalWrap onClose={closeExpenseModal}>
          <div className="p-6">
            <h2 className="font-display text-xl font-semibold tracking-tight text-on-surface mb-1">
              {expenseModal.mode === 'new' ? t('admin.addExpenseTitle') : t('admin.editExpenseTitle')}
            </h2>
            <p className="text-sm text-on-surface-variant mb-5">{t('admin.expenseSubtitle')}</p>

            {expenseError && (
              <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {expenseError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">{t('admin.labelDescriptionRequired')}</label>
                <input
                  type="text"
                  value={expenseForm.description}
                  onChange={e => setExpenseForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="e.g. Venue rental, Catering, T-shirts…"
                  className={INPUT_CLS}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">Category</label>
                  <select
                    value={expenseForm.category}
                    onChange={e => setExpenseForm(f => ({ ...f, category: e.target.value }))}
                    className={`${SELECT_CLS} w-full`}
                  >
                    {['Food', 'Transport', 'Accommodation', 'Materials', 'Staff', 'Other'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">{t('admin.labelAmountUsd')}</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))}
                    placeholder="0.00"
                    className={INPUT_CLS}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">
                  {t('admin.labelInvoiceReceipt')} <span className="font-normal text-on-surface-variant/70">{t('admin.labelAdminNoteOptional')}</span>
                </label>
                {expenseModal.expense?.invoice_url && !expenseForm.invoice_file && (
                  <div className="mb-2 flex items-center gap-2 text-xs text-on-surface-variant">
                    <a href={expenseModal.expense.invoice_url} target="_blank" rel="noreferrer"
                       className="text-oasis-500 underline hover:text-oasis-800">{t('admin.currentInvoice')}</a>
                    <span>{t('admin.replaceInvoice')}</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={e => setExpenseForm(f => ({ ...f, invoice_file: e.target.files?.[0] ?? null }))}
                  className="block w-full text-sm text-on-surface-variant
                    file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0
                    file:text-xs file:font-semibold file:bg-oasis-50 file:text-oasis-700
                    hover:file:bg-oasis-100 transition"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-outline-variant/50">
              <button
                onClick={closeExpenseModal}
                className="rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition"
              >{t('admin.cancelBtn')}</button>
              <button
                onClick={handleSaveExpense}
                disabled={expenseSaving}
                className="rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-50 transition"
              >{expenseSaving ? t('admin.savingChangesBtn') : t('admin.saveExpenseBtn')}</button>
            </div>
          </div>
        </ModalWrap>
      )}

      {donationModal.open && (
        <ModalWrap onClose={closeDonationModal}>
          <div className="p-6">
            <h2 className="font-display text-xl font-semibold tracking-tight text-on-surface mb-1">
              {donationModal.mode === 'new' ? t('admin.addDonationTitle') : t('admin.editDonationTitle')}
            </h2>
            <p className="text-sm text-on-surface-variant mb-5">{t('admin.donationSubtitle')}</p>

            {donationError && (
              <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                {donationError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">{t('admin.labelDonorNameOptional')}</label>
                <input
                  type="text"
                  value={donationForm.donor_name}
                  onChange={e => setDonationForm(f => ({ ...f, donor_name: e.target.value }))}
                  placeholder={t('admin.donorNamePlaceholder')}
                  className={INPUT_CLS}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">{t('admin.labelNoteOptional')}</label>
                <input
                  type="text"
                  value={donationForm.description}
                  onChange={e => setDonationForm(f => ({ ...f, description: e.target.value }))}
                  placeholder={t('admin.notePlaceholder')}
                  className={INPUT_CLS}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">{t('admin.colCategory')}</label>
                  <select
                    value={donationForm.category}
                    onChange={e => setDonationForm(f => ({ ...f, category: e.target.value }))}
                    className={`${SELECT_CLS} w-full`}
                  >
                    {['Cash', 'Check', 'Bank transfer', 'In-kind', 'Other'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">{t('admin.labelDonationDate')}</label>
                  <input
                    type="date"
                    value={donationForm.donated_at}
                    onChange={e => setDonationForm(f => ({ ...f, donated_at: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">{t('admin.labelAmountUsd')}</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={donationForm.amount}
                  onChange={e => setDonationForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  className={INPUT_CLS}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-on-surface-variant mb-1.5">
                  {t('admin.labelReceiptOptional')}
                </label>
                {donationModal.donation?.receipt_url && !donationForm.receipt_file && (
                  <div className="mb-2 flex items-center gap-2 text-xs text-on-surface-variant">
                    <a href={donationModal.donation.receipt_url} target="_blank" rel="noreferrer"
                       className="text-oasis-500 underline hover:text-oasis-800">{t('admin.currentReceipt')}</a>
                    <span>{t('admin.replaceReceipt')}</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={e => setDonationForm(f => ({ ...f, receipt_file: e.target.files?.[0] ?? null }))}
                  className="block w-full text-sm text-on-surface-variant
                    file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0
                    file:text-xs file:font-semibold file:bg-oasis-50 file:text-oasis-700
                    hover:file:bg-oasis-100 transition"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-outline-variant/50">
              <button
                onClick={closeDonationModal}
                className="rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition"
              >{t('admin.cancelBtn')}</button>
              <button
                onClick={handleSaveDonation}
                disabled={donationSaving}
                className="rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-50 transition"
              >{donationSaving ? t('admin.savingChangesBtn') : t('admin.saveDonationBtn')}</button>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Refund Modal */}
      {refundModal.open && refundModal.reg && (
        <ModalWrap onClose={closeRefundModal}>
          <div className="p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('admin.refundModalTitle')}</h3>
                <p className="text-sm text-on-surface-variant mt-0.5">
                  {refundModal.reg.full_name} &nbsp;·&nbsp; {selectedDashboardCamp?.name}
                </p>
              </div>
              <button onClick={closeRefundModal} className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none">×</button>
            </div>

            {/* Amount paid summary */}
            <div className="rounded-xl bg-surface-container-low border border-outline-variant/50 p-4 mb-5 grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs font-semibold uppercase text-on-surface-variant/70 mb-0.5">{t('admin.labelAmountPaidModal')}</p>
                <p className="font-bold text-on-surface">{formatCurrency(refundModal.reg.amount_paid ?? 0)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-on-surface-variant/70 mb-0.5">{t('admin.labelCampFee')}</p>
                <p className="font-semibold text-on-surface-variant">{formatCurrency(refundModal.reg.amount_owed ?? 0)}</p>
              </div>
            </div>

            {refundError && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 mb-4">
                <span className="shrink-0 mt-0.5">⚠️</span>
                <span>{refundError}</span>
              </div>
            )}

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">
                  {t('admin.labelRefundAmount')}
                </label>
                <input
                  type="number" min="0.01" step="0.01"
                  max={refundModal.reg.amount_paid ?? 0}
                  placeholder={formatCurrency(refundModal.reg.amount_paid ?? 0).replace('$', '')}
                  value={refundForm.amount}
                  onChange={e => setRefundForm(p => ({ ...p, amount: e.target.value }))}
                  className={INPUT_CLS}
                />
                <p className="mt-1 text-xs text-on-surface-variant/70">
                  Max: {formatCurrency(refundModal.reg.amount_paid ?? 0)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">
                  {t('admin.labelReason')} <span className="font-normal text-on-surface-variant/70">{t('admin.labelReasonRequired')}</span>
                </label>
                <textarea
                  value={refundForm.reason}
                  onChange={e => setRefundForm(p => ({ ...p, reason: e.target.value }))}
                  rows={3}
                  placeholder="e.g. Camper is unable to attend due to illness"
                  className="w-full resize-none rounded-lg border border-outline-variant px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                />
              </div>
            </div>

            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 mb-5">
              ⚠️ This will <strong>cancel the registration</strong> and record a refund of the entered amount.
              The user will receive a confirmation email.
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleProcessRefund}
                disabled={refundSaving}
                className="flex-1 rounded-xl bg-tertiary py-2.5 text-sm font-semibold text-white hover:bg-[#7c3f26] disabled:opacity-60 transition"
              >
                {refundSaving ? t('admin.processingBtn') : t('admin.processRefundBtn')}
              </button>
              <button
                onClick={closeRefundModal}
                disabled={refundSaving}
                className="rounded-xl border border-outline-variant px-5 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low disabled:opacity-60 transition"
              >
                {t('admin.cancelBtn')}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}

      {adminUpload.open && adminUpload.reg && (
        <ModalWrap onClose={closeAdminUpload}>
          <div className="p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-1">
              <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">
                {t('admin.uploadOnBehalfTitle', { name: adminUpload.reg.full_name })}
              </h3>
              <button onClick={closeAdminUpload} className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none">×</button>
            </div>
            <p className="text-sm text-on-surface-variant mb-5">{t('admin.uploadOnBehalfSubtitle')}</p>

            <div className="space-y-4 mb-6">
              {/* File */}
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.uploadFileLabel')}</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={e => setAdminUploadForm(p => ({ ...p, file: e.target.files?.[0] ?? null }))}
                  className="block w-full text-sm text-on-surface-variant file:mr-3 file:rounded-lg file:border-0 file:bg-oasis-50 file:px-3 file:py-2 file:text-sm file:font-semibold file:text-oasis-700 hover:file:bg-oasis-100"
                />
                {adminUploadForm.file && (
                  <p className="mt-1 text-xs text-on-surface-variant/70">
                    {t('admin.uploadSelectedFile', { filename: adminUploadForm.file.name })}
                  </p>
                )}
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.uploadAmountLabel')}</label>
                <input
                  type="number" min="0.01" step="0.01"
                  value={adminUploadForm.amount}
                  onChange={e => setAdminUploadForm(p => ({ ...p, amount: e.target.value }))}
                  className={INPUT_CLS}
                />
              </div>

              {/* Payment method */}
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.uploadMethodLabel')}</label>
                <select
                  value={adminUploadForm.payment_method}
                  onChange={e => setAdminUploadForm(p => ({ ...p, payment_method: e.target.value }))}
                  className={INPUT_CLS}
                >
                  <option value="">{t('admin.uploadMethodPlaceholder')}</option>
                  {PAYMENT_METHOD_CODES.map(code => (
                    <option key={code} value={code}>{paymentMethodLabel(code, t)}</option>
                  ))}
                </select>
              </div>

              {adminUploadError && (
                <p className="text-sm text-red-600 font-medium">{adminUploadError}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleAdminUploadReceipt}
                disabled={adminUploadSaving}
                className="flex-1 rounded-lg bg-oasis-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {t('admin.uploadSubmitBtn')}
              </button>
              <button
                onClick={closeAdminUpload}
                disabled={adminUploadSaving}
                className="rounded-lg border border-outline-variant px-4 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition"
              >
                {t('admin.uploadCancelBtn')}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Per-Receipt Approve / Reject Modal */}
      {approveModal.open && approveModal.receipt && approveModal.reg && (
        <ModalWrap onClose={closeApproveModal} wide>
          <div className="p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('admin.reviewReceiptTitle')}</h3>
                <p className="text-sm text-on-surface-variant mt-0.5">
                  {approveModal.reg.full_name} &nbsp;·&nbsp; submitted {formatCurrency(approveModal.receipt.amount)} via {paymentMethodLabel(approveModal.receipt.payment_method, t) ?? '—'}
                </p>
              </div>
              <button onClick={closeApproveModal} className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none">×</button>
            </div>

            {/* Receipt preview */}
            <div className="mb-5">
              {(() => {
                const url = approveModal.receipt.receipt_url
                if (!url) {
                  return (
                    <div className="rounded-xl border-2 border-dashed border-outline-variant bg-surface-container-low py-10 text-center text-sm text-on-surface-variant/70">
                      {t('admin.noReceiptImage')}
                    </div>
                  )
                }
                const isPdf = /\.pdf(\?|$)/i.test(url)
                const isImage = /\.(jpg|jpeg|png|webp)(\?|$)/i.test(url) || url.includes('placehold.co')
                if (isPdf) {
                  return (
                    <a
                      href={url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 rounded-xl border border-oasis-200 bg-oasis-50 px-4 py-4 text-sm font-medium text-oasis-700 hover:bg-oasis-100 transition"
                    >
                      <span className="text-2xl">📄</span>
                      <span>{t('admin.openPdf')}</span>
                      <span className="ml-auto text-oasis-400">↗</span>
                    </a>
                  )
                }
                if (isImage) {
                  return (
                    <div>
                      <img
                        src={url} alt="Payment receipt"
                        style={{ maxHeight: '400px' }}
                        className="w-full object-contain rounded-xl border border-outline-variant/60 bg-surface-container"
                      />
                      <a
                        href={url} target="_blank" rel="noopener noreferrer"
                        className="mt-1.5 inline-block text-xs text-on-surface-variant/70 hover:text-oasis-500 transition"
                      >
                        {t('admin.openInNewTab')}
                      </a>
                    </div>
                  )
                }
                // Unknown file type — show a generic link
                return (
                  <a
                    href={url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-xl border border-oasis-200 bg-oasis-50 px-4 py-4 text-sm font-medium text-oasis-700 hover:bg-oasis-100 transition"
                  >
                    <span className="text-2xl">📎</span>
                    <span>{t('admin.viewReceiptFile')}</span>
                    <span className="ml-auto text-oasis-400">↗</span>
                  </a>
                )
              })()}
            </div>

            {/* Registration context */}
            <div className="grid grid-cols-3 gap-3 mb-5 rounded-xl bg-surface-container-low border border-outline-variant/50 p-4 text-sm">
              <div>
                <p className="text-xs font-semibold uppercase text-on-surface-variant/70 mb-0.5">{t('admin.labelAmountSubmitted')}</p>
                <p className="font-semibold text-on-surface">{formatCurrency(approveModal.receipt.amount)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-on-surface-variant/70 mb-0.5">{t('admin.labelAmountPaidSoFar')}</p>
                <p className="font-semibold text-on-surface">{formatCurrency(approveModal.reg.amount_paid ?? 0)}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-on-surface-variant/70 mb-0.5">{t('admin.labelTotalOwed')}</p>
                <p className="font-semibold text-on-surface">{formatCurrency(approveModal.reg.amount_owed)}</p>
              </div>
            </div>

            {/* Fields */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">
                  {t('admin.labelConfirmAmount')}
                </label>
                <input
                  type="number" min="0.01" step="0.01"
                  placeholder={String(approveModal.receipt.amount ?? '')}
                  value={approveForm.amount}
                  onChange={e => setApproveForm(p => ({ ...p, amount: e.target.value }))}
                  className={INPUT_CLS}
                />
                <p className="mt-1 text-xs text-on-surface-variant/70">
                  {t('admin.approveAmountHint')}
                </p>
              </div>
              {approveModal.reg && agreementByReg[approveModal.reg.id] && (
                <div className="mt-4">
                  <p className="text-sm font-semibold mb-2">{t('admin.allocateToInstallments')}</p>
                  {(installmentsByAgreement[agreementByReg[approveModal.reg.id].id] ?? [])
                    .filter(i => deriveInstallmentStatus(i, allocations) === 'Unpaid')
                    .map(inst => (
                      <div key={inst.id} className="flex justify-between items-center gap-2 mb-1 text-sm">
                        <span>#{inst.seq} · {inst.due_date} · {t('admin.remaining')} {formatCurrency(installmentRemaining(inst, allocations))}</span>
                        <input type="number" step="0.01" min="0"
                          value={approveAllocations[inst.id] ?? ''}
                          onChange={e => setApproveAllocations(a => ({ ...a, [inst.id]: e.target.value }))}
                          className="w-24 rounded-lg border border-outline px-2 py-1 text-sm" />
                      </div>
                    ))}
                  {(() => {
                    const allocated = Object.values(approveAllocations).reduce((s, v) => s + (parseFloat(v) || 0), 0)
                    const receiptAmt = parseFloat(approveForm.amount) || 0
                    return allocated > receiptAmt + 0.005 ? (
                      <p className="text-red-600 text-xs mt-1">{t('admin.allocationExceeds')}</p>
                    ) : null
                  })()}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">
                  {t('admin.labelAdminNoteApprove')}{' '}
                  <span className="font-normal text-on-surface-variant/70">{t('admin.adminNoteApproveHint')}</span>
                </label>
                <textarea
                  value={approveForm.admin_note}
                  onChange={e => {
                    setApproveNoteError(false)
                    setApproveForm(p => ({ ...p, admin_note: e.target.value }))
                  }}
                  rows={2} placeholder="e.g. Verified against bank statement"
                  className={`w-full resize-none rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition ${
                    approveNoteError
                      ? 'border-red-400 focus:border-red-400 focus:ring-red-200'
                      : 'border-outline-variant focus:border-primary focus:ring-oasis-200'
                  }`}
                />
                {approveNoteError && (
                  <p className="mt-1 text-xs text-red-600 font-medium">
                    {t('admin.rejectionNoteRequired')}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleApproveReceipt} disabled={approveSaving}
                className="flex-1 rounded-xl bg-green-600 py-2.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60 transition"
              >
                {approveSaving ? t('admin.savingChangesBtn') : t('admin.approveReceiptBtn')}
              </button>
              <button
                onClick={handleRejectFromApproveModal} disabled={approveSaving}
                className="flex-1 rounded-xl border border-red-300 bg-red-50 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-100 disabled:opacity-60 transition"
              >
                {t('admin.rejectReceiptBtn')}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Payment Agreement Modal — Create / Edit */}
      {agreementModal.open && (
        <ModalWrap onClose={closeAgreementModal} wide>
          <div className="p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-1">
              {agreementModal.editing ? t('admin.editAgreementTitle') : t('admin.createAgreementTitle')}
            </h3>
            <p className="text-sm text-on-surface-variant mb-4">{agreementModal.reg?.full_name}</p>

            {agreementRows.map((row, idx) => (
              <div key={idx} className="flex gap-2 items-center mb-2">
                <span className="text-xs w-6 text-on-surface-variant">#{idx + 1}</span>
                <input type="date" value={row.due_date} disabled={row.locked}
                  onChange={e => setAgreementRows(rows => rows.map((r, i) => i === idx ? { ...r, due_date: e.target.value } : r))}
                  className="flex-1 rounded-lg border border-outline px-2 py-1.5 text-sm disabled:opacity-50" />
                <input type="number" step="0.01" min="0" value={row.amount} disabled={row.locked}
                  placeholder="0.00"
                  onChange={e => setAgreementRows(rows => rows.map((r, i) => i === idx ? { ...r, amount: e.target.value } : r))}
                  className="w-28 rounded-lg border border-outline px-2 py-1.5 text-sm disabled:opacity-50" />
                {!row.locked && agreementRows.length > 1 && (
                  <button type="button" onClick={() => setAgreementRows(rows => rows.filter((_, i) => i !== idx))}
                    className="text-red-500 text-sm">✕</button>
                )}
              </div>
            ))}

            <button type="button" onClick={() => setAgreementRows(rows => [...rows, { due_date: '', amount: '' }])}
              className="text-sm text-primary hover:underline mt-1">+ {t('admin.addInstallment')}</button>

            {(() => {
              const total = agreementRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)
              const outstanding = Math.max(0, (agreementModal.reg?.amount_owed ?? 0) - (agreementModal.reg?.amount_paid ?? 0))
              return (
                <div className="mt-3 text-sm">
                  <div className="flex justify-between font-semibold">
                    <span>{t('admin.agreementTotal')}</span><span>{formatCurrency(total)}</span>
                  </div>
                  {Math.abs(total - outstanding) > 0.005 && (
                    <p className="text-amber-700 text-xs mt-1">
                      {t('admin.agreementTotalWarning', { outstanding: formatCurrency(outstanding) })}
                    </p>
                  )}
                </div>
              )
            })()}

            <textarea value={agreementNote} onChange={e => setAgreementNote(e.target.value)}
              placeholder={t('admin.agreementNotePlaceholder')}
              className="mt-3 w-full rounded-lg border border-outline px-2 py-1.5 text-sm" rows={2} />

            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={closeAgreementModal}
                className="px-4 py-2 rounded-lg border border-outline text-sm">{t('common.cancel')}</button>
              <button type="button" onClick={saveAgreement} disabled={agreementSaving}
                className="px-4 py-2 rounded-lg bg-primary text-on-primary text-sm disabled:opacity-50">
                {agreementSaving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Bulk Reminder Confirm */}
      {confirmBulk && (
        <ModalWrap onClose={() => setConfirmBulk(false)}>
          <div className="p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-oasis-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📧</span>
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('admin.bulkReminderTitle')}</h3>
            <p className="mt-2 text-sm text-on-surface-variant">
              This will send payment reminders to{' '}
              <strong>{pendingRegs.length}</strong> attendee{pendingRegs.length !== 1 ? 's' : ''} with outstanding balances.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={sendBulkReminders}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 transition"
              >
                Send {pendingRegs.length} Reminder{pendingRegs.length !== 1 ? 's' : ''}
              </button>
              <button onClick={() => setConfirmBulk(false)} className="rounded-xl border border-outline-variant px-5 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition">
                {t('admin.cancelBtn')}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Camp Modal — New / Edit */}
      {campModal.open && (
        <ModalWrap onClose={() => setCampModal({ open: false, mode: 'new', camp: null })}>
          <div className="p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">
                {campModal.mode === 'new' ? t('admin.newCampTitle') : t('admin.editCampTitle')}
              </h3>
              <button onClick={() => setCampModal({ open: false, mode: 'new', camp: null })} className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none">×</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelCampName')}</label>
                <input
                  type="text" value={campForm.name} placeholder="Summer Camp 2026"
                  onChange={e => setCampForm(p => ({ ...p, name: e.target.value }))}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelCampDescription')}</label>
                <textarea
                  rows={2} value={campForm.description} placeholder="A brief description…"
                  onChange={e => setCampForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-outline-variant px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelLocation')}</label>
                <input
                  type="text" value={campForm.location} placeholder="Blue Ridge Mountains, NC"
                  onChange={e => setCampForm(p => ({ ...p, location: e.target.value }))}
                  className={INPUT_CLS}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelStartDate')}</label>
                  <input type="date" value={campForm.start_date}
                    onChange={e => setCampForm(p => ({ ...p, start_date: e.target.value }))}
                    className={INPUT_CLS} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelEndDate')}</label>
                  <input type="date" value={campForm.end_date}
                    onChange={e => setCampForm(p => ({ ...p, end_date: e.target.value }))}
                    className={INPUT_CLS} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPaymentDueDate')}</label>
                <input type="date" value={campForm.payment_due_date}
                  onChange={e => setCampForm(p => ({ ...p, payment_due_date: e.target.value }))}
                  className={INPUT_CLS} />
                <p className="mt-1 text-xs text-on-surface-variant/70">
                  {t('admin.paymentDueDateHint')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelTransportCost')}</label>
                <input
                  type="number" min="0" step="0.01" placeholder="0.00"
                  value={campForm.transport_cost}
                  onChange={e => setCampForm(p => ({ ...p, transport_cost: e.target.value }))}
                  className={INPUT_CLS}
                />
                <p className="mt-1 text-xs text-on-surface-variant/70">
                  {t('admin.transportCostHint')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPackingList')}</label>
                <textarea
                  rows={4} value={campForm.packing_list} placeholder={'Biblia\nToalla\nRepelente'}
                  onChange={e => setCampForm(p => ({ ...p, packing_list: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-outline-variant px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                />
                <p className="mt-1 text-xs text-on-surface-variant/70">{t('admin.packingListHint')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelMapLink')}</label>
                <input
                  type="url" value={campForm.map_link} placeholder="https://maps.app.goo.gl/..."
                  onChange={e => setCampForm(p => ({ ...p, map_link: e.target.value }))}
                  className={INPUT_CLS}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelArrivalTime')}</label>
                  <input
                    type="text" value={campForm.arrival_time} placeholder="8:00 AM"
                    onChange={e => setCampForm(p => ({ ...p, arrival_time: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelTransportMeetingTime')}</label>
                  <input
                    type="text" value={campForm.transport_meeting_time} placeholder="6:30 AM"
                    onChange={e => setCampForm(p => ({ ...p, transport_meeting_time: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelTransportMeetingPoint')}</label>
                <input
                  type="text" value={campForm.transport_meeting_point} placeholder="Iglesia Bautista Ozama, parqueo"
                  onChange={e => setCampForm(p => ({ ...p, transport_meeting_point: e.target.value }))}
                  className={INPUT_CLS}
                />
              </div>

              {/* Payment information — feeds the reminder and confirmation emails */}
              <div className="pt-2 border-t border-outline-variant/50">
                <p className="text-sm font-semibold text-on-surface mt-3">{t('admin.paymentInfoSection')}</p>
                <p className="mt-1 text-xs text-on-surface-variant/70">{t('admin.paymentInfoHint')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPaymentInPerson')}</label>
                <input
                  type="text" value={campForm.payment_in_person_note} placeholder="Oficina pastoral: efectivo, cheque o tarjeta"
                  onChange={e => setCampForm(p => ({ ...p, payment_in_person_note: e.target.value }))}
                  className={INPUT_CLS}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPaymentBankName')}</label>
                  <input
                    type="text" value={campForm.payment_bank_name} placeholder="Banco Popular"
                    onChange={e => setCampForm(p => ({ ...p, payment_bank_name: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPaymentAccount')}</label>
                  <input
                    type="text" value={campForm.payment_account_number} placeholder="5454116-3"
                    onChange={e => setCampForm(p => ({ ...p, payment_account_number: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPaymentCardUrl')}</label>
                <input
                  type="url" value={campForm.payment_card_url} placeholder="https://oasisrd.org/donacion/"
                  onChange={e => setCampForm(p => ({ ...p, payment_card_url: e.target.value }))}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelPaymentExtra')}</label>
                <textarea
                  rows={3} value={campForm.payment_extra_info} placeholder={t('admin.paymentExtraPlaceholder')}
                  onChange={e => setCampForm(p => ({ ...p, payment_extra_info: e.target.value }))}
                  className="w-full resize-none rounded-lg border border-outline-variant px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                />
                <p className="mt-1 text-xs text-on-surface-variant/70">{t('admin.paymentExtraHint')}</p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setCampForm(p => ({ ...p, registration_open: !p.registration_open }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${campForm.registration_open ? 'bg-primary' : 'bg-surface-container-highest'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${campForm.registration_open ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm font-medium text-on-surface-variant">{t('admin.labelRegistrationOpen')}</span>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={saveCamp} disabled={campSaving || !campForm.name.trim()}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {campSaving ? t('admin.savingChangesBtn') : t('admin.saveCampBtn')}
              </button>
              <button onClick={() => setCampModal({ open: false, mode: 'new', camp: null })} className="rounded-xl border border-outline-variant px-5 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition">
                {t('admin.cancelBtn')}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Pricing Tiers Modal */}
      {tierModal.open && (
        <ModalWrap onClose={() => setTierModal(p => ({ ...p, open: false }))}>
          <div className="p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">{t('admin.pricingTiersTitle')}</h3>
              <button onClick={() => setTierModal(p => ({ ...p, open: false }))} className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none">×</button>
            </div>
            <p className="text-sm text-on-surface-variant mb-5">{tierModal.campName}</p>

            {tierModal.tiers.length === 0 ? (
              <p className="text-sm text-on-surface-variant/70 mb-5">{t('admin.noTiersYet')}</p>
            ) : (
              <div className="space-y-2 mb-5">
                {tierModal.tiers.map(tier => (
                  <div key={tier.id} className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${tier.is_default ? 'border-oasis-200 bg-oasis-50' : 'border-outline-variant/50 bg-surface-container-low'}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-on-surface">{tier.label}</p>
                        {tier.is_default && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-oasis-100 border border-oasis-200 px-2 py-0.5 text-xs font-semibold text-oasis-700">
                            ★ Default
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-on-surface-variant">
                        {tier.arrival_date ? formatDate(tier.arrival_date) : t('admin.noArrivalDate')} · {formatCurrency(tier.price)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!tier.is_default && (
                        <button onClick={() => setDefaultTier(tier.id)} className="text-xs text-oasis-500 hover:text-oasis-800 font-medium transition">
                          {t('admin.setDefaultBtn')}
                        </button>
                      )}
                      <button onClick={() => deleteTier(tier.id)} className="text-xs text-red-500 hover:text-red-700 font-medium transition">
                        {t('admin.removeBtn')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-outline-variant/50 pt-4">
              <p className="text-sm font-medium text-on-surface-variant mb-3">{t('admin.addNewTier')}</p>
              <div className="space-y-3">
                <input
                  type="text" placeholder="Label (e.g. Full Week)"
                  value={tierForm.label}
                  onChange={e => setTierForm(p => ({ ...p, label: e.target.value }))}
                  className={INPUT_CLS}
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date" value={tierForm.arrival_date}
                    onChange={e => setTierForm(p => ({ ...p, arrival_date: e.target.value }))}
                    className={INPUT_CLS}
                  />
                  <input
                    type="number" placeholder="Price" min="0" step="0.01"
                    value={tierForm.price}
                    onChange={e => setTierForm(p => ({ ...p, price: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
                <button
                  onClick={saveTier}
                  disabled={tierSaving || !tierForm.label || !tierForm.price}
                  className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
                >
                  {tierSaving ? t('admin.addingTierBtn') : t('admin.addTierBtn')}
                </button>
              </div>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Room Modal — New / Edit */}
      {roomModal.open && (
        <ModalWrap onClose={() => setRoomModal({ open: false, mode: 'new', room: null })}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">
                {roomModal.mode === 'new' ? t('admin.addRoomTitle') : t('admin.editRoomTitle')}
              </h3>
              <button onClick={() => setRoomModal({ open: false, mode: 'new', room: null })} className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none">×</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelRoomName')}</label>
                <input
                  type="text" placeholder="Cabin A" value={roomForm.name}
                  onChange={e => setRoomForm(p => ({ ...p, name: e.target.value }))}
                  className={INPUT_CLS}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelCapacity')}</label>
                  <input
                    type="number" min="1" placeholder="10" value={roomForm.capacity}
                    onChange={e => setRoomForm(p => ({ ...p, capacity: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelSex')}</label>
                  <select value={roomForm.sex} onChange={e => setRoomForm(p => ({ ...p, sex: e.target.value }))} className={INPUT_CLS}>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Mixed">Mixed</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelMinAge')}</label>
                  <input
                    type="number" min="0" placeholder="Any" value={roomForm.min_age}
                    onChange={e => setRoomForm(p => ({ ...p, min_age: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelMaxAge')}</label>
                  <input
                    type="number" min="0" placeholder="Any" value={roomForm.max_age}
                    onChange={e => setRoomForm(p => ({ ...p, max_age: e.target.value }))}
                    className={INPUT_CLS}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-on-surface-variant mb-1.5">{t('admin.labelRoomPhoto')}</label>
                {(roomPhotoFile || roomForm.photo_url) && (
                  <div className="mb-2 flex items-center gap-3">
                    <img
                      src={roomPhotoFile ? URL.createObjectURL(roomPhotoFile) : roomForm.photo_url}
                      alt={roomForm.name}
                      className="h-20 w-28 rounded-lg object-cover border border-outline-variant/60"
                    />
                    <button
                      type="button"
                      onClick={() => { setRoomPhotoFile(null); setRoomForm(p => ({ ...p, photo_url: '' })) }}
                      className="text-sm text-red-600 hover:underline"
                    >
                      {t('admin.removePhotoBtn')}
                    </button>
                  </div>
                )}
                <input
                  type="file" accept="image/*"
                  onChange={e => setRoomPhotoFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-on-surface-variant file:mr-3 file:rounded-lg file:border-0 file:bg-oasis-100 file:px-3 file:py-2 file:text-sm file:font-medium file:text-oasis-700 hover:file:bg-oasis-200"
                />
                <p className="mt-1 text-xs text-on-surface-variant/70">{t('admin.roomPhotoHint')}</p>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={saveRoom}
                disabled={roomSaving || !roomForm.name || !roomForm.capacity}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {roomSaving ? t('admin.savingChangesBtn') : t('admin.saveRoomBtn')}
              </button>
              <button onClick={() => setRoomModal({ open: false, mode: 'new', room: null })} className="rounded-xl border border-outline-variant px-5 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-low transition">
                {t('admin.cancelBtn')}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}

      {/* Manage Room Panel */}
      {manageRoom && (
        <ModalWrap onClose={() => setManageRoom(null)}>
          <div className="p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-semibold tracking-tight text-on-surface">{manageRoom.name}</h3>
              <button onClick={() => setManageRoom(null)} className="text-on-surface-variant/70 hover:text-on-surface-variant text-2xl leading-none">×</button>
            </div>
            <p className="text-sm text-on-surface-variant mb-5">
              {roomOccupancy[manageRoom.id] ?? 0} / {manageRoom.capacity} occupied
            </p>

            {(() => {
              const occupants = roomAssignments.filter(a => a.room_id === manageRoom.id)
              return occupants.length === 0 ? (
                <p className="text-sm text-on-surface-variant/70 mb-5">{t('admin.noOccupantsYet')}</p>
              ) : (
                <div className="space-y-2 mb-5">
                  {occupants.map(a => (
                    <div key={a.id} className="flex items-center gap-3 rounded-xl border border-outline-variant/50 bg-surface-container-low px-4 py-2.5">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-on-surface">{a.registrations?.full_name ?? '—'}</p>
                        <p className="text-xs text-on-surface-variant/70">
                          {a.registrations?.sex} · age {getAge(a.registrations?.date_of_birth) ?? '—'}
                        </p>
                      </div>
                      <button
                        onClick={() => removeAssignment(a.id)}
                        className="text-xs text-red-500 hover:text-red-700 font-medium transition"
                      >
                        {t('admin.removeBtn')}
                      </button>
                    </div>
                  ))}
                </div>
              )
            })()}

            {(roomOccupancy[manageRoom.id] ?? 0) < manageRoom.capacity && (
              <div className="border-t border-outline-variant/50 pt-4">
                <p className="text-sm font-medium text-on-surface-variant mb-2">{t('admin.addAttendeeTitle')}</p>
                <div className="flex gap-2">
                  <select
                    value={assignSearch}
                    onChange={e => setAssignSearch(e.target.value)}
                    className={INPUT_CLS}
                  >
                    <option value="">{t('admin.selectAttendee')}</option>
                    {roomCampRegs
                      .filter(r => !roomAssignments.some(a => a.registration_id === r.id) && isRoomEligible(r))
                      .map(r => (
                        <option key={r.id} value={r.id}>
                          {r.full_name} ({r.sex}, {getAge(r.date_of_birth) ?? '?'} yrs, {r.payment_status}{roomAgreementRegIds.has(r.id) && r.payment_status !== 'Paid' && r.payment_status !== 'Sponsored' ? ` · ${t('admin.agreementBadge')}` : ''})
                        </option>
                      ))
                    }
                  </select>
                  <button
                    onClick={() => assignSearch && addAssignment(assignSearch)}
                    disabled={!assignSearch}
                    className="flex-shrink-0 rounded-xl bg-primary px-4 text-sm font-semibold text-white hover:bg-oasis-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    {t('admin.addBtn')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalWrap>
      )}

      </div>{/* /tab content */}
      </div>{/* /sidebar rail + tab content */}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-5 right-5 z-50 rounded-xl px-4 py-3 text-sm font-medium text-white shadow-lg ${
          toast.type === 'error'   ? 'bg-tertiary' :
          toast.type === 'success' ? 'bg-green-700' :
          'bg-on-surface'
        }`}>
          {toast.message}
        </div>
      )}

    </div>
  )
}
